import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { CommitOptions } from '../../shared/useHistory';
import { Modal, RowMenu, Toggle } from '../../components/fs';
import { COARSE, NARROW, PHONE, useMediaQuery } from '../../shared/media';
import { useToast } from '../../components/Toast';
import '../../admin.css';
import '../../form.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { FieldDefinition, FieldOption, FormLayout, FormLogo, FormRule, FormWidget, GradientDir, WidgetCount, WidgetNode } from '../../shared/types';
import { accentWash, flattenWidgetTree, GRADIENT_DIRS, GRADIENT_LABEL, gradientCss, isContinuation, LOGO_ALIGNS, parseGradient, resolveTheme, themeLogos } from '../../shared/types';
import { FormThemeSettings, deleteFormLogo, getFormParents, mintTestTokenApi, promoteFormBranding, templatesOf, updateFormApi, uploadFormAsset, uploadFormLogo, workingDocumentOf } from '../../api/forms';
import { RichBlocks, RichInline } from '../../form-engine/richTextView';
import { BRAND_FONTS, fontStack, fontsHref } from '../../shared/brandFonts';
import { THEME_PRESETS } from '../../shared/themePresets';
import PlanLimitBlock from '../../components/PlanLimitBlock';
import {
  cardTarget, ruleFires, ruleKeys, ruleShowTargetKeys, rulesForField, sectionTarget, widgetTarget,
} from '../../form-engine/rules';
import { FormState, PublishedDef, createInitialState } from '../../form-engine/model';
import { evaluateForm } from '../../form-engine/evaluate';
import FormRenderer from '../../form-engine/FormRenderer';
import FormMagicBadge, { showsBadge } from '../../form-engine/FormMagicBadge';
import { ErrorSummaryTop, PagerControls, PagerProgress, useFormPager } from '../../form-engine/FormPager';
import { backgroundOf, boxStyle as boxStyleFor, hasBackground, instancePadding, sectionBodyStyle, sectionWrapStyle } from '../../form-engine/style';
import { COUNTRIES, addressFormat, compositeSubs } from '../../shared/address';
import { type FormDraft, useFormWorkspace } from './FormWorkspace';
import { draftKeyOf, recordRename } from '../../shared/documentRename';
import { usePageHeight } from './workflow/usePageHeight';
import {
  BCard, BField, BInputType, BSection, BStyle, Cont, FieldMove, Lens, MAX_CARD_DEPTH, MAX_WIDGET_DEPTH, MoveDir,
  IMAGE_OPTION_TYPES, OPTION_TYPES, PRESENTATION, SCORE_TYPES, emptyMatrix, emptyScale,
  allNodes, assignCont, attachChild, cardAndDescendants, cardDepth, cardHeight, cloneWidget, cont, contKey, contOf,
  conditionalCountKeyFor, conditionalCountKeyProblem, countKeyProblem, countKeysOf, newConditionalCount,
  defaultStyle, depthOf, deriveKeyIn, detachNode, duplicateCard, duplicateField, duplicateSection, emptyDivider,
  emptyHeading, emptyImage, emptyParagraph,
  emptyPayment, emptySignature, emptyUpload,
  emptyValidation, findNode,
  heightOf, inCont, keyProblem, mergeOptions, mkNodeMarker, moveFieldAcross, moveFieldWithin, nodeMarkerOf,
  parseOptionList, readDateBound, renameFieldKey, takesDefault,
  writeDateBound,
  isStructural, markerOf, mkCardMarker, moveScopePieces, newField, newWidgetNode, nodesOf, pack, patchNodeIn,
  pageOptions, pagesOf, placeItems, relocateCard, ruleText, ruleTriggerChip, sameCont, toFieldBody, toLayout, toStyleBody, toWidgets, uniqueScope,
  visibleUnder,
} from './builderModel';
import { DragPayload, PALETTE, PALETTE_GROUPS, PaletteEntry, paletteEntryFor, searchPalette } from './builderPalette';
import { ClipBundle, MAX_PER_ROW, canPasteInto, copyFields, moveFieldsTo, pasteFields, rowOf } from './builderClipboard';
import BuilderOutline, { type BuilderSel } from './BuilderOutline';
import AddFieldPicker from './AddFieldPicker';
import FieldPicker from './FieldPicker';
import { RunWhenEditor } from './ConnectionEditors';
import { groupsForContainer, pickerGroups, sectionsFromSpine } from './fieldGroups';
import UploadLimitsFields from './UploadLimitsFields';
import { resolveUploadLimits } from '../../form-engine/uploadLimits';

/**
 * What the inspector describes: a field, a section, a card, a repeatable
 * widget container (the root node IS its widget), or the form itself. The
 * type lives with the Outline, which selects the same things.
 */
type Sel = BuilderSel;
type Hint =
  | { t: 'f'; key: string; pos: string }
  | { t: 'c'; cont: Cont }
  | { t: 'z'; idx: number }
  /** hovering a widget container as a NESTING target */
  | { t: 'w'; scope: string }
  | null;

const FIELD_DRAGS = ['new-field', 'field', 'new-row'];
/** cards go where fields go — into a container, and between the things in it */
const CARD_DRAGS = ['new-card', 'card'];
/** what the inside of a widget container accepts as a nested container */
const NEST_DRAGS = ['new-widget', 'wnode'];
/** everything that takes a position inside a container */
const PLACE_DRAGS = [...FIELD_DRAGS, ...CARD_DRAGS, ...NEST_DRAGS];
/** what a top-level zone between sections accepts */
const SECTION_DRAGS = ['new-section', 'new-page', 'new-widget', 'section', 'wnode'];

/**
 * The builder's clipboard. Module scope, not component state: a selection
 * copied in one form can be pasted into another in the same session, which
 * is the point of a clipboard. `clipStamp` is bumped so the Paste button
 * re-renders when it changes.
 */
let CLIP: ClipBundle | null = null;

const VIS_OPTS: [string, string, string][] = [
  ['shown', 'Shown on the form', 'Everyone filling in this form sees it.'],
  ['hidden', 'Hidden', 'Hidden by default — a "show" rule on the Logic page can reveal it. Without one it stays off the form but keeps feeding rules and integrations, and a link parameter mapped to it on the Access tab still fills it (a hidden field).'],
];
/**
 * The same two choices a field's "Who sees it" offers, said for a box rather
 * than a question. A hidden container is the one-rule way to reveal a group of
 * questions: mark it hidden, then point a single `show` rule at it.
 */
const CONTAINER_VIS_OPTS: [string, string, string][] = [
  ['shown', 'Shown on the form', 'Everyone filling in this form sees it, unless a rule takes it away.'],
  ['hidden', 'Hidden', 'Hidden by default — a "show" rule on the Logic page reveals it. Without one it never renders, and nothing inside it is asked for.'],
];
const REQ_OPTS: [string, string, string][] = [
  ['never', 'Optional', 'Can be left blank.'],
  ['whenShown', 'Required whenever shown', 'Must be answered when it is on screen; a rule that hides it also waives it.'],
];
/**
 * Locked is the third question about a field, alongside who sees it and whether
 * it is required — and like those two, Build gives the answer and a rule can
 * overrule it. A locked field still SHOWS and still submits whatever it starts
 * from; it just is not the respondent's to change.
 */
const LOCK_OPTS: [string, string, string][] = [
  ['editable', 'Editable', 'The respondent fills it in.'],
  ['locked', 'Locked', 'Shown but read-only — it submits whatever it starts from, and is never demanded. An "unlock" rule can hand it back.'],
];

/**
 * The accent edge that says where a drop will land, by which edge was aimed
 * at (R4: an inset 3px line above or below, 4px beside — the design's drop
 * indicator).
 */
const DROP_EDGE: Record<string, string> = {
  left: 'inset 4px 0 0 0 var(--accent)', right: 'inset -4px 0 0 0 var(--accent)',
  above: 'inset 0 3px 0 0 var(--accent)', below: 'inset 0 -3px 0 0 var(--accent)',
};

const BADGE: Record<string, [string, string, string]> = {
  shown: ['Shown', 'var(--ok-bg)', 'var(--ok-fg)'], hidden: ['Hidden', 'var(--neutral-bg)', 'var(--neutral-fg)'],
};

/** The selected ring: the design's 2px accent outline. */
const SEL_RING = '0 0 0 2px var(--accent)';

/** Test mode's preview widths (P6): a desktop window, a tablet, a phone. */
const DEVICES = [
  { id: 'desktop', label: 'Desktop', width: 1200 },
  { id: 'tablet', label: 'Tablet', width: 768 },
  { id: 'phone', label: 'Phone', width: 390 },
] as const;
const DEVICE_WIDTH: Record<'desktop' | 'tablet' | 'phone', number> = { desktop: 1200, tablet: 768, phone: 390 };

/**
 * What the canvas reads while the workspace's draft is still in flight.
 *
 * ONE object, at module scope, rather than a literal in the `draft || {…}`
 * below — because a literal there is a FRESH pair of arrays on every render.
 * `testDef` memoises on `fields`/`sections`/`rules`/`widgets`, so fresh
 * arrays missed that memo every render, which handed `useFormPager` a new
 * `def` every render, and its `[def]` effect set state every render: React's
 * "Maximum update depth exceeded", on every single load, for as long as the
 * two requests the workspace makes are both outstanding. The workspace shows
 * the tabs as soon as the FORM arrives, so the builder renders against a null
 * draft every time, and the window is long enough to spin ~70 times.
 *
 * Nothing mutates it — every setter here replaces the part it owns — so one
 * shared instance is safe.
 */
const EMPTY_DRAFT: FormDraft = {
  sections: [], fields: [], rules: [], widgets: [], theme: {}, submitLabel: '', renames: [],
};

export default function FormBuilderAdmin() {
  /**
   * The canvas edits the workspace's ONE working copy — the same object the
   * Logic tab writes rules into. Nothing here reaches a respondent until the
   * workspace's Publish, so switching tabs mid-edit loses nothing and a
   * half-finished form never goes live.
   *
   * `limitHit` is the 402 body from a publish refused for fieldsPerForm.
   * It is rendered INLINE where the add controls are — never a modal, the
   * person is mid-edit and a dialog would lose their place.
   */
  const {
    form, refreshForm, features, brand, setBrand,
    draft, setSections, setFields, setRules, setWidgets, setTheme, setSubmitLabel, setRenames,
    history, limitHit, publicUrl,
  } = useFormWorkspace();
  const toast = useToast();
  /**
   * Test mode's live test (R9, D11): the LIVE form in a new tab, opened with a
   * signed hour-long token, so what is submitted there is saved as a test —
   * outside the quota, delivered nowhere, visible under Responses' Test chip.
   * The tab is opened before the token is asked for, so a popup blocker sees
   * the click and not a promise.
   */
  const [testLinkBusy, setTestLinkBusy] = useState(false);
  async function openLiveTest() {
    if (!publicUrl || testLinkBusy) return;
    setTestLinkBusy(true);
    const tab = window.open('', '_blank');
    try {
      const r = await mintTestTokenApi(form.id);
      const url = `${publicUrl}?test=${encodeURIComponent(r.token)}`;
      if (tab) { tab.opener = null; tab.location.href = url; } else window.location.assign(url);
    } catch (e) {
      tab?.close();
      toast((e as Error).message || 'The test link could not be made.', { kind: 'error' });
    } finally {
      setTestLinkBusy(false);
    }
  }
  const base = `/admin/forms/${form.id}`;
  const { sections, fields, rules, widgets, theme, submitLabel, renames } = draft || EMPTY_DRAFT;
  /**
   * The theme the CANVAS draws: this form's own keys over the account's brand.
   *
   * Two themes, and which is which matters. `theme` above is the form's own
   * and is what every editor writes and Publish saves — resolving into it
   * would save inherited values as this form's overrides and quietly take the
   * form out of the brand. `shown` is for drawing only, so the canvas is the
   * page a respondent gets. With no brand the two are the same object's worth
   * of keys, which is every account until B3.
   *
   * The editing panels still read `theme`, so a form inheriting a logo shows
   * an empty logo control while the canvas shows the logo. That is B4's job to
   * fix, and it is invisible until an account has a brand at all.
   */
  const shown = useMemo(() => resolveTheme(brand, theme), [brand, theme]);
  /** The logo row the canvas draws — this form's, or the house one it takes. */
  const canvasLogos = useMemo(() => themeLogos(shown), [shown]);

  /**
   * What the account's brand offers for one key, and whether this form has
   * taken it or overruled it.
   *
   * `brandGives` is the gate on every inheritance affordance below, and it is
   * the reason the Design panel is unchanged for an account with no brand: no
   * brand means no key is offered, so no chip, no banner and no palette
   * render, and the panel is exactly the markup it has always been.
   *
   * The font is asked per face — a form may take the house body and set its
   * own headings — which is the one key whose answer is not simply its own
   * presence.
   */
  type BrandKey = 'accent' | 'bg' | 'mode' | 'logos' | 'font.body' | 'font.heading';
  const brandGives = (k: BrandKey): boolean => {
    if (!brand) return false;
    if (k === 'font.body') return brand.font?.body !== undefined;
    if (k === 'font.heading') return brand.font?.heading !== undefined;
    if (k === 'logos') return !!brand.logos?.length;
    return brand[k] !== undefined;
  };
  const formAnswers = (k: BrandKey): boolean => {
    if (k === 'font.body') return theme.font?.body !== undefined;
    if (k === 'font.heading') return theme.font?.heading !== undefined;
    // The logo row is answered by EITHER key, including an empty list — see
    // resolveTheme. A form saying "no logo" has answered.
    if (k === 'logos') return 'logos' in theme || 'logo' in theme;
    return theme[k] !== undefined;
  };

  /**
   * The brand-answerable keys this form sets for itself — what "use this
   * form's branding for the account" would send up, and what "reset" would
   * take away. Kept in step with the server's RESETTABLE_KEYS by hand; the
   * server is what actually decides, this only decides whether to offer.
   */
  const ownBranded = useMemo(
    () => (['accent', 'bg', 'accent2', 'accentGradient', 'font', 'mode', 'logos', 'logo', 'presetId'] as const)
      .filter((k) => (theme as Record<string, unknown>)[k] !== undefined),
    [theme]
  );
  /**
   * The second colour each gradient had last, so unticking a Gradient box and
   * ticking it again puts back the colour that was there rather than a default
   * the person then has to find again.
   */
  const [accent2Memo, setAccent2Memo] = useState('#764ba2');
  const [bg2Memo, setBg2Memo] = useState('#ffffff');
  const [promoting, setPromoting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [adopting, setAdopting] = useState(false);
  /** Clear this form's keys as part of promoting, so it inherits what it gave. */
  const [clearOnPromote, setClearOnPromote] = useState(true);

  /** What a theme key is called in a sentence a person reads. */
  const brandKeyLabel = (k: string) => (({
    accent: 'accent colour', accent2: 'accent gradient', accentGradient: 'accent gradient',
    bg: 'background', font: 'typefaces', mode: 'colour mode',
    logos: 'logo', logo: 'logo', presetId: 'theme preset',
  } as Record<string, string>)[k] || k);

  /**
   * Send this form's look up to the account brand.
   *
   * The DRAFT theme goes with the request, because it is what the person is
   * looking at — promoting the last published colours would send up a look
   * they cannot see. Clearing afterwards happens here, on the draft, for the
   * same reason: the draft is the copy on screen and the one Publish writes.
   */
  async function doPromote() {
    setAdopting(true);
    try {
      const r = await promoteFormBranding(form.id, clearOnPromote, theme);
      setBrand(r.brand);
      if (clearOnPromote) {
        setTheme((t) => {
          const next = { ...t };
          for (const k of ['accent', 'bg', 'accent2', 'accentGradient', 'font', 'mode', 'logos', 'logo', 'presetId'] as const) delete next[k];
          return next;
        });
      }
      setPromoting(false);
      refreshForm().catch(() => undefined);
      toast(r.notes.length ? r.notes[0] : 'Account branding updated from this form.');
    } catch (e: any) {
      setError(e.message || 'The branding could not be applied to the account.');
      setPromoting(false);
    } finally { setAdopting(false); }
  }

  /**
   * Drop this form's own branded keys on the CANVAS.
   *
   * No server call: inside the builder this is an edit like any other, and it
   * reaches respondents when the form is published. The route of the same name
   * exists for the forms list and the bulk reset, which act on live forms with
   * no draft in front of them.
   */
  function doReset() {
    setTheme((t) => {
      const next = { ...t };
      for (const k of ['accent', 'bg', 'accent2', 'accentGradient', 'font', 'mode', 'logos', 'logo', 'presetId'] as const) delete next[k];
      return next;
    });
    setResetting(false);
    toast('Back to the account branding — publish to show it.');
  }

  /** Drop this form's own answer to a key, so it inherits again. */
  function revert(k: BrandKey) {
    setTheme((t) => {
      const next = { ...t };
      if (k === 'font.body' || k === 'font.heading') {
        const face = k === 'font.body' ? 'body' : 'heading';
        const font = { ...next.font };
        delete font[face];
        if (Object.keys(font).length) next.font = font; else delete next.font;
      } else if (k === 'logos') {
        delete next.logos;
        delete next.logo;
      } else if (k === 'accent') {
        // The accent PAIR is one answer (see `resolveTheme`): leaving a second
        // stop behind would gradient the house accent into a colour this form
        // was just told to stop setting.
        delete next.accent;
        delete next.accent2;
        delete next.accentGradient;
      } else {
        delete next[k];
      }
      return next;
    });
  }

  /**
   * The one mark every inheritable control carries: "Inherited" while the
   * brand answers for this form, or "Overrides branding" with a way back.
   *
   * Renders NOTHING when the brand offers nothing for that key — which is
   * what keeps the panel identical for every account that has no brand.
   */
  function InheritMark({ k }: { k: BrandKey }) {
    if (!brandGives(k)) return null;
    if (!formAnswers(k)) return <span className="fb-inherit" title="Set on Settings → Branding">Inherited</span>;
    return (
      <span className="fb-inherit over">
        Overrides branding
        <button type="button" onClick={(e) => { e.stopPropagation(); revert(k); }}
          title="Use the account's branding for this again">Revert</button>
      </span>
    );
  }

  /**
   * A control's label, with its inheritance mark beside it.
   *
   * The mark is a SIBLING of the <label>, never a child: Revert is a button,
   * and a button inside a label takes the label's text as its accessible name
   * — "Accent Overrides branding" instead of "Revert" — besides being a
   * control nested in something that forwards clicks to another control.
   *
   * With no mark to draw this returns the bare <label> the panel has always
   * rendered, so an account without a brand gets exactly today's markup and
   * not a wrapper around it.
   */
  const LABEL_STYLE = { display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 } as const;
  function panelLabel(text: React.ReactNode, k?: BrandKey) {
    const label = <label style={LABEL_STYLE}>{text}</label>;
    if (!k || !brandGives(k)) return label;
    return (
      <div className="fb-labelrow">
        <label style={{ ...LABEL_STYLE, marginBottom: 0 }}>{text}</label>
        <InheritMark k={k} />
      </div>
    );
  }

  const [sel, setSel] = useState<Sel>(null);
  /**
   * The multi-selection (R4): field keys, in the order they were picked. The
   * selection bar counts these; `sel` stays the one the inspector describes.
   * A plain click sets both; Shift/⌘/Ctrl-click (or a tap in Select mode)
   * toggles a key here.
   */
  const [multi, setMulti] = useState<string[]>([]);
  /** On a coarse pointer, the toolbar's Select toggle turns taps into toggles. */
  const [selectMode, setSelectMode] = useState(false);
  const coarse = useMediaQuery(COARSE);
  /** The sidebar: the palette's tiles, or the Outline. */
  const [sideTab, setSideTab] = useState<'fields' | 'outline'>('fields');
  /** The inspector's tab. It follows the selection; a click can override it. */
  const [inspTab, setInspTab] = useState<'field' | 'page' | 'form'>('field');
  /** Between 900 and 1100 the inspector overlays the canvas and can be put away. */
  const midOnly = useMediaQuery('(max-width: 1100px)');
  const [inspHidden, setInspHidden] = useState(false);
  /** The page on show (an index into `pagesOf(sections)`). */
  const [pageIx, setPageIx] = useState(0);
  /**
   * The "/" picker. `pickerAt` is where the pick lands when it was opened
   * from a specific place ("+ Field here" on a section); null means "after
   * the selection, or the end of the form", which is what "/" means.
   */
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAt, setPickerAt] = useState<{ cont: Cont; afterKey: string | null; label: string } | null>(null);
  /** "Put beside…": the field looking for a row-mate. */
  const [besideFor, setBesideFor] = useState<string | null>(null);
  /** The selection bar's Delete, armed for its second press. */
  const [barConfirm, setBarConfirm] = useState(false);
  /** Bumped when the clipboard changes, so the Paste button re-renders. */
  const [clipStamp, setClipStamp] = useState(0);
  /** The form name being typed on the Form tab, landed on blur or Enter. */
  const [nameDraft, setNameDraft] = useState<string | null>(null);

  // The builder's own cross-references (a payment's amount, a state list's
  // country, a widget's title) pick from the same arranged list every other
  // picker draws — the canvas's sections and cards, off this very draft.
  const pickerSections = useMemo(() => sectionsFromSpine(sections, fields), [sections, fields]);
  const pickerDefs = useMemo<FieldDefinition[]>(() => fields
    .filter((f) => !isStructural(f))
    .map((f) => ({
      id: f.id || 0, key: f.key, label: f.label, section: f.section, input_type: f.type, options: f.options,
      flow_applicability: f.flow, scope: f.scope, sort_order: 0, required_default: false,
      ui_meta: { ...(f.group ? { group: f.group } : {}) },
    } as FieldDefinition)), [fields]);
  const builderPicker = (keep: (d: FieldDefinition) => boolean, scope?: string) => (scope
    ? groupsForContainer(scope, pickerDefs.filter(keep), pickerSections, undefined, { strayLabel: 'Questions' })
    : pickerGroups(pickerDefs.filter(keep), pickerSections, widgets));
  const [hint, setHint] = useState<Hint>(null);
  const [lensId, setLensId] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [dragKind, setDragKind] = useState('');
  const [testMode, setTestMode] = useState(false);
  /**
   * `?test=1` opens the builder straight into Test mode (R3: the Publish
   * panel's "Test it first"). The flag is consumed — dropped from the URL —
   * so a reload or a Back does not re-arm it. `toggleTest` is the same
   * function the toolbar button runs; it is declared below and hoisted.
   */
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!draft) return;
    const params = new URLSearchParams(location.search);
    if (params.get('test') !== '1') return;
    if (!testMode) toggleTest();
    params.delete('test');
    navigate({ search: params.toString() ? `?${params}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, !!draft]);
  /**
   * A stand-in ORDER for Test mode, on a form that continues another one.
   *
   * Test mode is the real renderer over the real rule engine, so a rule
   * written against `parent.*` has to weigh something there or the preview
   * lies — it would show the form a respondent with no order would get, which
   * on a follow-up form is nobody. Fetched once Test mode is first switched
   * on rather than with the page: a form that continues nothing never asks,
   * and the lookup costs a query.
   */
  const [testParent, setTestParent] = useState<{ data: Record<string, unknown>; note: string } | null>(null);
  useEffect(() => {
    if (!testMode || testParent || !isContinuation(form.settings.continuation)) return;
    getFormParents(form.id, undefined, true)
      .then((r) => setTestParent(r.sample || null))
      .catch(() => setTestParent(null));
  }, [testMode, testParent, form.id, form.settings.continuation]);
  const [testState, setTestState] = useState<FormState | null>(null);
  /** Test mode's preview width (P6): the paper's width, not an iframe, so the form's own breakpoints fire. */
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'phone'>('desktop');
  const [hlRule, setHlRule] = useState<string | null>(null);
  const [error, setError] = useState('');
  /** the image block whose upload is in flight, if any */
  const [uploadingAsset, setUploadingAsset] = useState<string | null>(null);
  /** the option (`key:index`) whose picture is uploading, if any (P3 image choice) */
  const [uploadingOpt, setUploadingOpt] = useState<string | null>(null);
  async function pickOptionImage(key: string, i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingOpt(`${key}:${i}`);
    try {
      const r = await uploadFormAsset(form.id, file);
      setFields((fs) => fs.map((x) => (x.key === key
        ? { ...x, options: x.options.map((o, j) => (j === i ? { ...o, image: r.asset.url } : o)) } : x)));
      mark();
      setError('');
      toast('Image uploaded.');
    } catch (err: any) {
      toast(err?.message || 'Image upload failed.', { kind: 'error' });
    } finally {
      setUploadingOpt(null);
    }
  }
  async function pickAsset(key: string, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingAsset(key);
    try {
      const r = await uploadFormAsset(form.id, file);
      setFields((fs) => fs.map((x) => (x.key === key ? { ...x, image: { ...(x.image || emptyImage()), url: r.asset.url } } : x)));
      mark();
      setError('');
      toast('Image uploaded.');
    } catch (err: any) {
      toast(err?.message || 'Image upload failed.', { kind: 'error' });
    } finally {
      setUploadingAsset(null);
    }
  }
  /** Which row's ⋯ menu is open: `field:<key>`, `card:<sec>:<id>`, `sec:<id>`. */
  const [menu, setMenu] = useState<string | null>(null);
  /**
   * The builder under 900 (§4E E1). `narrow` covers the tablet and the phone:
   * the palette becomes a sheet reached from one "+" and the inspector an
   * overlay rather than a third column. `phone` is where the inspector is a
   * full-screen sheet split into two tabs. Every drag path stays exactly where
   * it was; these are additional branches, so the desktop DOM is untouched.
   */
  const narrow = useMediaQuery(NARROW);
  const phone = useMediaQuery(PHONE);
  /** Under 1100 the inspector is an overlay; a narrow screen is under 1100 whatever the stub says. */
  const mid = midOnly || narrow;
  /**
   * The builder fills the viewport under the form bar (self-framed, like
   * Workflow and Runs), so the sidebar, the canvas and the inspector each
   * scroll on their own: on a long form the inspector stays beside the field
   * picked at the bottom of the page. Off on a phone, where the paper is the
   * page and the inspector a full-screen sheet.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  usePageHeight(pageRef, !phone, !!draft);
  const [palSearch, setPalSearch] = useState('');
  const [panelTab, setPanelTab] = useState<'question' | 'rules'>('question');
  /** the "Move to…" picker: which field is being moved */
  const [moveTo, setMoveTo] = useState<string | null>(null);
  /** What the screen reader is told after a keyboard move or a duplicate. */
  const [announce, setAnnounce] = useState('');
  const announceSeq = useRef(0);
  /**
   * The question whose POINTS column is open. Scoring is otherwise derived
   * from the options themselves — a question with points on it shows them
   * whenever it is selected — so this only has to carry the moment between
   * ticking the box and typing the first number.
   */
  const [scoreOpen, setScoreOpen] = useState<string | null>(null);

  /** The "Paste a list" box, open for one field's options at a time. */
  const [pasteFor, setPasteFor] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [pasteReplace, setPasteReplace] = useState(false);
  /**
   * The key being typed, before it is committed.
   *
   * A key is the field's identity — the panel finds the field BY it — so
   * renaming on every keystroke tore the panel out from under the cursor after
   * every character. The typing happens here instead, and the rename lands on
   * blur or Enter, once, when there is a whole key to land.
   */
  const [keyDraft, setKeyDraft] = useState<{ of: string; text: string } | null>(null);
  /** The same, for the count key being typed on a widget container. */
  const [countDraft, setCountDraft] = useState<{ of: string; text: string } | null>(null);
  /** A conditional count's key being typed; `of` is `${scope}:${index}`. */
  const [condKeyDraft, setCondKeyDraft] = useState<{ of: string; text: string } | null>(null);

  const drag = useRef<DragPayload | null>(null);
  const counters = useRef({ row: 20, slot: 0, card: 10, section: 0 });
  /**
   * Ids the form is ALREADY using for a given kind of thing.
   *
   * The counters below start from a fixed number on every mount, so on their
   * own they hand out `card11` again to a form that has had a card11 since the
   * session before. A card is addressed by its id — its fields point at it as
   * their group — so two cards answering to one id rendered the same fields
   * twice and a drop appeared to duplicate a card instead of adding one.
   */
  function idsInUse(p: keyof typeof counters.current): Set<string> {
    if (p === 'card') return new Set(sections.flatMap((s) => s.cards.map((c) => c.id)));
    if (p === 'section') return new Set(sections.map((s) => s.id));
    if (p === 'row') return new Set(fields.map((f) => f.row).filter(Boolean) as string[]);
    // A slot's id lives inside its field key, as `__slot7`.
    return new Set(fields.filter((f) => f.slot).map((f) => f.key.replace(/^__/, '')));
  }
  const uid = (p: keyof typeof counters.current) => {
    const taken = idsInUse(p);
    let id = p + (++counters.current[p]);
    while (taken.has(id)) id = p + (++counters.current[p]);
    return id;
  };
  /** Where scope naming got to, so 'item7' is never minted twice in a session. */
  const scopeSeq = useRef(0);

  // Preview lenses come from the form's saved presets (named flag bundles).
  const lenses = useMemo<Lens[]>(() => [
    { id: '', name: 'Everything (builder view)', flow: '', cfg: {} },
    ...(form.settings?.previewPresets || []).map((p) => ({ id: p.id, name: p.name, flow: '' as const, cfg: p.flags })),
  ], [form.settings?.previewPresets]);

  const lens = lenses.find((p) => p.id === lensId) || lenses[0];
  /**
   * The lens's flags, as ONE object per lens.
   *
   * `lens.cfg || {}` minted a new empty object on every render, and this is
   * the config Test mode's `evaluateForm`, `useFormPager` and the renderer
   * all key their memos on — so every one of them missed on every render of
   * the heaviest screen in the product. `lens` itself is stable (`lenses` is
   * memoised), so memoising on it is enough.
   */
  const lensCfg = useMemo(() => lens.cfg || {}, [lens]);
  const widgetSecIds = useMemo(() => new Set(sections.filter((s) => s.type === 'locations' || s.type === 'widget').map((s) => s.id)), [sections]);
  const widgetFor = (sec: BSection): FormWidget | undefined =>
    sec.type === 'locations' ? (widgets.find((w) => w.id === 'locations') || widgets.find((w) => w.builtin)) :
    sec.type === 'widget' ? widgets.find((w) => w.id === sec.widgetId) : undefined;

  // ---- pages (R4): one page on show at a time -----------------------------------
  const pages = useMemo(() => pagesOf(sections), [sections]);
  const curPage = pages[Math.min(pageIx, Math.max(0, pages.length - 1))] || null;
  useEffect(() => {
    if (pageIx > pages.length - 1) setPageIx(Math.max(0, pages.length - 1));
  }, [pages.length, pageIx]);
  const pageIndexOfSection = (secId: string) =>
    pages.findIndex((p) => sections.slice(p.start, p.end).some((s) => s.id === secId));
  const sectionOfSel = (s: Sel): string | null => (!s ? null
    : s.t === 'field' ? (fields.find((f) => f.key === s.key)?.section || null)
      : s.t === 'form' ? null : s.t === 'sec' ? s.id : s.sec);
  /** The canvas element for a selection, for scrolling it into view. */
  const elOf = (s: Sel): Element | null => {
    if (!s) return null;
    const esc = (v: string) => (window.CSS?.escape ? window.CSS.escape(v) : v.replace(/["\\]/g, '\\$&'));
    if (s.t === 'field') return document.querySelector(`.fb-el[data-key="${esc(s.key)}"]`);
    if (s.t === 'sec') return document.querySelector(`.of-section[data-sec="${esc(s.id)}"]`);
    if (s.t === 'card') return document.querySelector(`.fb-card-box[data-card="${esc(s.id)}"]`);
    if (s.t === 'wnode') return document.querySelector(`.fb-wnode[data-scope="${esc(s.scope)}"]`);
    return null;
  };
  /**
   * Select something, bring its page on screen, and point the inspector's
   * tab at it. Every path that can select something off the current page
   * (the Outline, a paste, an add) comes through here.
   */
  function select(s: Sel, opts: { scroll?: boolean } = {}) {
    setSel(s); setConfirm(false); setHlRule(null); setBarConfirm(false); setKeyDraft(null);
    setMulti(s && s.t === 'field' ? [s.key] : []);
    if (s) setInspTab(s.t === 'field' ? 'field' : s.t === 'form' ? 'form' : 'page');
    const secId = sectionOfSel(s);
    if (secId) { const ix = pageIndexOfSection(secId); if (ix >= 0 && ix !== pageIx) setPageIx(ix); }
    if (opts.scroll && s) {
      window.requestAnimationFrame(() => {
        const el = elOf(s);
        if (el && typeof (el as HTMLElement).scrollIntoView === 'function') (el as HTMLElement).scrollIntoView({ block: 'center' });
      });
    }
  }
  /** A click on a field: select it, or toggle it in the multi-selection. */
  function clickField(e: React.MouseEvent, key: string) {
    e.stopPropagation();
    const toggling = selectMode || e.shiftKey || e.metaKey || e.ctrlKey;
    if (!toggling) { select({ t: 'field', key }); return; }
    setConfirm(false); setHlRule(null); setBarConfirm(false);
    if (multi.includes(key)) {
      const rest = multi.filter((k) => k !== key);
      setMulti(rest);
      if (sel?.t === 'field' && sel.key === key) setSel(rest.length ? { t: 'field', key: rest[rest.length - 1] } : null);
    } else {
      setMulti([...multi, key]);
      setSel({ t: 'field', key }); setInspTab('field');
    }
  }
  /** The keys in the multi-selection that are still fields on the form, in canvas order. */
  const selKeys = useMemo(() => {
    const want = new Set(multi);
    return fields.filter((f) => want.has(f.key) && !isStructural(f)).map((f) => f.key);
  }, [multi, fields]);
  const clearMulti = () => { setMulti(sel?.t === 'field' ? [sel.key] : []); setBarConfirm(false); };

  // ---- the clipboard (R4) --------------------------------------------------------
  const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;
  function copySel() {
    if (!selKeys.length) return;
    CLIP = copyFields(fields, selKeys);
    setClipStamp((n) => n + 1);
    say(`Copied ${plural(selKeys.length, 'field')}`);
    toast(`Copied ${plural(selKeys.length, 'field')}.`, { kind: 'info' });
  }
  /** Paste after the selected field's row, or at the end of what is selected. */
  function pasteClip() {
    if (!CLIP || !CLIP.fields.length) return;
    const anchor = sel?.t === 'field' ? fields.find((f) => f.key === sel.key) : undefined;
    const target = anchor ? contOf(anchor, widgetSecIds) : tapTarget();
    if (!target) { toast('Add a section first — a field needs somewhere to live.', { kind: 'error' }); return; }
    const r = pasteFields({ fields, clip: CLIP, widgets, target, afterKey: anchor?.key || null, widgetSecIds, nextRowId: () => uid('row') });
    if (r.refused) {
      toast(`${plural(r.refused, 'field')} could not be pasted here — a payment is charged once per submission, never per item.`, { kind: 'error' });
    }
    if (!r.keys.length) return;
    setFields(r.fields);
    setMulti(r.keys); setSel({ t: 'field', key: r.keys[r.keys.length - 1] }); setInspTab('field'); setBarConfirm(false); mark();
    say(`Pasted ${plural(r.keys.length, 'field')}`);
  }
  /** Copy and paste in one go: the selection again, right under itself. */
  function duplicateSel() {
    if (!selKeys.length) return;
    if (selKeys.length === 1) { dupField(selKeys[0]); return; }
    const last = fields.filter((f) => selKeys.includes(f.key)).pop()!;
    const r = pasteFields({
      fields, clip: copyFields(fields, selKeys), widgets, target: contOf(last, widgetSecIds), afterKey: last.key, widgetSecIds,
      nextRowId: () => uid('row'),
    });
    if (!r.keys.length) return;
    setFields(r.fields);
    setMulti(r.keys); setSel({ t: 'field', key: r.keys[r.keys.length - 1] }); setInspTab('field'); setBarConfirm(false); mark();
    noteRulesLeft(selKeys);
    say(`Duplicated ${plural(selKeys.length, 'field')}`);
  }
  /** The bar's Delete: armed on the first press, done on the second, undone from the toast. */
  function deleteSel() {
    if (!selKeys.length) return;
    if (!barConfirm) { setBarConfirm(true); return; }
    const gone = new Set(selKeys);
    const n = selKeys.length;
    setFields((fs) => fs.filter((f) => !gone.has(f.key)));
    setSel(null); setMulti([]); setBarConfirm(false); setConfirm(false);
    say(`Deleted ${plural(n, 'field')}`);
    toast(`Deleted ${plural(n, 'field')}. Undo (Ctrl+Z) puts ${n === 1 ? 'it' : 'them'} back.`, { kind: 'info' });
  }

  // ---- "Put beside…" (R4): the tap path to a row -----------------------------------
  /** The fields `key` could share a row with: its container's, rows with room, not its own row. */
  function besideCandidates(key: string): BField[] {
    const f = fields.find((x) => x.key === key);
    if (!f || isStructural(f)) return [];
    const items = inCont(fields, contOf(f, widgetSecIds), widgetSecIds);
    return items.filter((x) => {
      if (x.key === key || isStructural(x)) return false;
      if (f.row && x.row === f.row) return false;
      const size = x.row ? items.filter((y) => y.row === x.row).length : 1;
      return size < MAX_PER_ROW;
    });
  }
  function putBeside(key: string, targetKey: string) {
    setBesideFor(null);
    const flds = fields.map((f) => ({ ...f }));
    const moving = flds.find((f) => f.key === key);
    const target = flds.find((f) => f.key === targetKey);
    if (!moving || !target) return;
    flds.splice(flds.indexOf(moving), 1);
    place(flds, moving, contOf(target, widgetSecIds), targetKey, 'right');
    setFields(flds); select({ t: 'field', key }); mark();
    say(`Put ${moving.label || key} beside ${target.label || targetKey}`);
  }

  // ---- pages: add, title, delete ---------------------------------------------------
  /** A new section at `idx` in the section list, starting a page if asked. */
  function insertSection(idx: number, breaks: boolean) {
    const id = uid('section');
    const next = [...sections];
    const really = breaks && idx > 0;
    next.splice(idx, 0, {
      id, title: really ? 'New Page' : 'New Section', type: 'fields', showFlag: '', hideWith: '', cards: [], style: defaultStyle(),
      ...(really ? { pageBreak: true } : {}),
    });
    setSections(next); select({ t: 'sec', id }); mark();
    say(really ? 'Added a page at the end of the form' : 'Added a section');
  }
  const addPage = () => insertSection(sections.length, true);
  /** Delete every section on the page on show. */
  const [pageDelConfirm, setPageDelConfirm] = useState(false);
  function deletePage() {
    if (!curPage) return;
    if (!pageDelConfirm) { setPageDelConfirm(true); return; }
    const secs = sections.slice(curPage.start, curPage.end);
    for (const s of secs) deleteTarget({ t: 'sec', id: s.id });
    setPageDelConfirm(false);
    say(`Deleted page ${curPage.n}`);
    toast(`Deleted page ${curPage.n} and its ${plural(secs.length, 'section')}. Undo (Ctrl+Z) puts it back.`, { kind: 'info' });
  }
  useEffect(() => { setPageDelConfirm(false); }, [pageIx]);

  /** The picker's line on where a pick will land. */
  function pickerWhere(): React.ReactNode {
    if (pickerAt) return <>Lands in <b>{pickerAt.label}</b>.</>;
    if (sel?.t === 'field') {
      const f = fields.find((x) => x.key === sel.key);
      if (f) return <>Adds after <b>“{f.label || f.key}”</b>, in {tapTargetLabel()}.</>;
    }
    return <>Lands in <b>{tapTargetLabel()}</b>. Tap something on the form first to put it somewhere else.</>;
  }
  const openPicker = (at: { cont: Cont; afterKey: string | null; label: string } | null = null) => {
    setPickerAt(at); setPickerOpen(true);
  };
  /** The container a section's own body is: for "+ Field here". */
  const sectionBody = (s: BSection): Cont =>
    cont(s.id, widgetSecIds.has(s.id) ? (widgetFor(s)?.tree.scope || 'order') : 'order', '');

  /** Land the form name typed on the Form tab; the row is saved live, like Settings. */
  async function commitName() {
    const next = (nameDraft ?? '').trim();
    setNameDraft(null);
    if (!next || next === form.name) return;
    try {
      await updateFormApi(form.id, { name: next });
      await refreshForm();
      toast('Renamed.', { kind: 'info' });
    } catch (e: any) {
      toast(e?.message || 'The name could not be saved.', { kind: 'error' });
    }
  }

  /** "Page 2 — Your details", for a rule sentence that skips to a page. */
  const pageLbl = (id: string) => pageOptions(sections).find((p) => p.id === id)?.label || `a page no longer on the form (${id})`;
  const lbl = (k: string) => {
    if (k.startsWith('sec:')) {
      const s = sections.find((x) => x.id === k.slice(4));
      return s ? `Section: ${s.title || s.id}` : k;
    }
    if (k.startsWith('card:')) {
      const [sid, cid] = k.slice(5).split(':');
      const c = sections.find((x) => x.id === sid)?.cards.find((x) => x.id === cid);
      return c ? `Card: ${c.title || '(untitled)'}` : k;
    }
    const dot = k.indexOf('.');
    if (dot > 0) {
      const f = fields.find((x) => x.key === k.slice(0, dot));
      if (f && (f.type === 'name' || f.type === 'address')) {
        const subs = f.type === 'name' ? compositeSubs('name') : compositeSubs('address', f.addressKind === 'shipping');
        const s = subs.find((x) => x.sub === k.slice(dot + 1));
        return `${f.label} › ${s?.label || k.slice(dot + 1)}`;
      }
    }
    return fields.find((f) => f.key === k)?.label || k;
  };
  /** Every setter marks the draft dirty already; this just drops a pending confirm. */
  const mark = () => setConfirm(false);

  /** Every target a show rule names — what makes a hidden container reachable. */
  const showTargets = useMemo(() => ruleShowTargetKeys(rules), [rules]);
  /**
   * The canvas's word on a container marked hidden. One no show rule names can
   * never appear, so it is called out rather than left looking like an
   * ordinary setting someone will wonder about later.
   */
  const hiddenChip = (target: string) => {
    const reachable = showTargets.has(target);
    return (
      <span className="fb-sec-chip"
        title={reachable
          ? 'Hidden until a show rule on the Logic page reveals it'
          : 'Hidden, and no show rule names it — as it stands this never renders'}
        style={reachable ? { background: '#e2e8f0', color: '#4a5568' } : { background: '#fed7d7', color: '#742a2a' }}>
        {reachable ? 'Hidden until shown' : 'Hidden — no show rule'}
      </span>
    );
  };

  // ---- test mode: the REAL renderer over a def assembled from the canvas ----
  // What test mode shows is what publishing this draft would produce — the
  // renderer, value model and rule engine are the public form's own.
  const testDef = useMemo<PublishedDef>(() => {
    const { fieldOrder } = placeItems(fields);
    return {
      fields: fields.filter((f) => !isStructural(f))
        .map((f, i) => ({ id: f.id ?? 0, ...toFieldBody(f, fieldOrder.get(f.key) ?? (i + 1) * 10) }) as unknown as FieldDefinition),
      layout: toLayout(sections, fields),
      rules,
      widgets: toWidgets(widgets, fields),
      // The order a follow-up form would be continuing. Absent on every other
      // form, and on this one until the sample has arrived — a rule reading
      // `parent.*` then weighs a blank, exactly as it would for a respondent
      // who opened the form without an invite.
      ...(testParent ? { parent: testParent.data } : {}),
    };
  }, [sections, fields, rules, widgets, testParent]);
  const testEv = useMemo(
    () => (testMode && testState ? evaluateForm(testDef, testState, lensCfg) : null),
    [testMode, testState, testDef, lensCfg]
  );
  // Test mode pages exactly like the public form (same hook, same skipping).
  const emptyTestState = useMemo(() => createInitialState({ fields: [], layout: { version: 1, sections: [] }, rules: [], widgets: [] }, {}), []);
  // D15: the same switch the public form reads, so the canvas shows the summary where respondents get it.
  const testPager = useFormPager(testDef, testState ?? emptyTestState, lensCfg, { errorSummary: form.settings?.errorSummary === true });

  // The canvas is a preview of the real page, so it wants the real typefaces.
  useEffect(() => {
    const href = fontsHref(shown.font);
    if (!href) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
    return () => { link.remove(); };
  }, [shown.font?.body, shown.font?.heading]); // eslint-disable-line react-hooks/exhaustive-deps

  const hlKeys = useMemo(() => {
    if (!hlRule) return null;
    const r = rules.find((x) => x.id === hlRule);
    return r ? new Set(ruleKeys(r)) : null;
  }, [hlRule, rules]);

  // ---- drag and drop ---------------------------------------------------------
  const startDrag = (payload: DragPayload) => (e: React.DragEvent) => {
    if (testMode) return;
    drag.current = payload;
    try { e.dataTransfer.setData('text/plain', payload.kind); e.dataTransfer.effectAllowed = 'move'; } catch { /* older browsers */ }
    setDragKind(payload.kind);
  };
  const endDrag = () => { drag.current = null; setHint(null); setDragKind(''); };

  /**
   * Where a drop on `target` lands: above, below, or sharing its row.
   *
   * Rows are for fields. A card is a block — it never sits beside a field, and
   * neither does a Row placeholder — so those drops resolve to above/below
   * whichever edge they were aimed at.
   */
  function calcPos(e: React.DragEvent, kind: string, target?: BField): string {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / Math.max(r.width, 1);
    const y = (e.clientY - r.top) / Math.max(r.height, 1);
    const block = kind === 'new-row' || CARD_DRAGS.includes(kind) || NEST_DRAGS.includes(kind)
      || !!target?.cardId || !!target?.nodeScope;
    if (block) return y < 0.5 ? 'above' : 'below';
    if (x < 0.22) return 'left';
    if (x > 0.78) return 'right';
    return y < 0.5 ? 'above' : 'below';
  }

  function place(flds: BField[], moving: BField, c: Cont, targetKey: string | null, pos: string) {
    assignCont(moving, c);
    const items = inCont(flds, c, widgetSecIds);
    const t = targetKey ? flds.find((f) => f.key === targetKey) : null;
    if (!t || !items.includes(t)) {
      moving.row = null;
      const last = items[items.length - 1];
      const gi = last ? flds.indexOf(last) + 1 : flds.length;
      flds.splice(gi, 0, moving);
      return;
    }
    const ti = flds.indexOf(t);
    // The 4-per-row cap (R4): a fifth lands under the row instead of in it.
    const roomInRow = !t.row || items.filter((x) => x.row === t.row).length < MAX_PER_ROW;
    if ((pos === 'left' || pos === 'right') && roomInRow) {
      if (!t.row) t.row = uid('row');
      moving.row = t.row;
      flds.splice(pos === 'left' ? ti : ti + 1, 0, moving);
    } else {
      if (pos === 'left') pos = 'above';
      if (pos === 'right') pos = 'below';
      moving.row = null;
      const ci = items.indexOf(t);
      let s = ci, e2 = ci;
      if (t.row) { while (s > 0 && items[s - 1].row === t.row) s--; while (e2 < items.length - 1 && items[e2 + 1].row === t.row) e2++; }
      const anchor = pos === 'above' ? items[s] : items[e2];
      const ai = flds.indexOf(anchor);
      flds.splice(pos === 'above' ? ai : ai + 1, 0, moving);
    }
  }

  const mkSlot = (c: Cont, row: string): BField => ({
    key: '__' + uid('slot'), slot: true, label: '', type: 'slot',
    section: c.sec, group: c.grp, scope: c.scope,
    flow: 'both', row, vis: 'shown', req: 'never', lock: 'editable', options: [],
    optionsPreset: '', countryField: '', showWhen: null, placeholder: '', infoText: '',
  });

  function insertSlots(flds: BField[], t: BField, pos: string) {
    const row = uid('row');
    const c = contOf(t, widgetSecIds);
    const a = mkSlot(c, row), b = mkSlot(c, row);
    const items = inCont(flds, c, widgetSecIds);
    const ci = items.indexOf(t);
    let s = ci, e = ci;
    if (t.row) { while (s > 0 && items[s - 1].row === t.row) s--; while (e < items.length - 1 && items[e + 1].row === t.row) e++; }
    const anchor = pos === 'above' ? items[s] : items[e];
    const ai = flds.indexOf(anchor);
    flds.splice(pos === 'above' ? ai : ai + 1, 0, a, b);
  }

  const overField = (key: string) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d || !PLACE_DRAGS.includes(d.kind)) return;
    if (d.kind === 'field' && d.key === key) return;
    const target = fields.find((f) => f.key === key);
    if (!target) return;
    if (d.kind === 'card' && target.cardId === d.id && target.section === d.sec) return;
    if (d.kind === 'wnode' && target.nodeScope === d.scope) return;
    const into = contOf(target, widgetSecIds);
    if (CARD_DRAGS.includes(d.kind) && !cardFits(d, into)) return;
    // A container only nests in a widget's own body, never inside a card.
    if (NEST_DRAGS.includes(d.kind) && !(into.scope !== 'order' && !into.grp && canNestInto(d, into.scope))) return;
    e.preventDefault(); e.stopPropagation();
    const pos = calcPos(e, d.kind, target);
    setHint((h) => (h && h.t === 'f' && h.key === key && h.pos === pos ? h : { t: 'f', key, pos }));
  };

  const dropField = (key: string) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d || !PLACE_DRAGS.includes(d.kind)) return;
    e.preventDefault(); e.stopPropagation();
    const target = fields.find((f) => f.key === key);
    if (!target) return endDrag();
    // A card dropped on its own heading has not moved.
    if (d.kind === 'card' && target.cardId === d.id && target.section === d.sec) return endDrag();
    if (d.kind === 'wnode' && target.nodeScope === d.scope) return endDrag();
    const pos = calcPos(e, d.kind, target);
    const into = contOf(target, widgetSecIds);
    if (CARD_DRAGS.includes(d.kind)) return placeCard(d, into, key, pos);
    if (NEST_DRAGS.includes(d.kind)) {
      if (into.scope === 'order' || into.grp) return endDrag();
      return nestWidget(d, into.sec, into.scope, { targetKey: key, pos });
    }
    const flds = fields.map((f) => ({ ...f }));
    const t = flds.find((f) => f.key === key);
    if (!t) return endDrag();
    const c = contOf(t, widgetSecIds);
    if (t.slot && (d.kind === 'field' || d.kind === 'new-field')) {
      // dropping onto an empty row slot replaces it and inherits its row
      let moving: BField;
      if (d.kind === 'field') {
        const m = flds.find((f) => f.key === d.key);
        if (!m) return endDrag();
        moving = m;
        flds.splice(flds.indexOf(moving), 1);
      } else moving = newField(d.type, flds, countKeysOf(widgets));
      const idx = flds.findIndex((f) => f.key === key);
      const row = t.row;
      assignCont(moving, c);
      moving.row = row;
      flds.splice(idx, 1, moving);
      drag.current = null;
      setFields(flds); setHint(null); setDragKind(''); setSel({ t: 'field', key: moving.key }); mark();
      return;
    }
    if (d.kind === 'new-row') insertSlots(flds, t, pos);
    else if (d.kind === 'new-field') {
      const moving = newField(d.type, flds, countKeysOf(widgets), d.preset);
      place(flds, moving, c, key, pos);
      drag.current = null;
      setFields(flds); setHint(null); setDragKind(''); setSel({ t: 'field', key: moving.key }); mark();
      return;
    } else if (d.kind === 'field') {
      // Dragging one field of a multi-selection moves the whole selection,
      // in document order (R4).
      if (multi.length > 1 && multi.includes(d.key)) {
        const moved = moveFieldsTo({
          fields, keys: multi, target: c, anchorKey: key,
          pos: pos === 'above' || pos === 'left' ? 'above' : 'below', widgetSecIds,
        });
        if (!moved) return endDrag();
        drag.current = null;
        setFields(moved); setHint(null); setDragKind(''); mark();
        return;
      }
      const moving = flds.find((f) => f.key === d.key);
      if (!moving || moving.key === key) return endDrag();
      flds.splice(flds.indexOf(moving), 1);
      place(flds, moving, c, key, pos);
    }
    drag.current = null;
    setFields(flds); setHint(null); setDragKind(''); mark();
  };

  /**
   * How many more levels of card a container can take.
   *
   * A card groups questions wherever it lands: a section body, a widget node's
   * body (where it then repeats with every instance), or another card. What
   * stops it is depth — the renderer draws MAX_CARD_DEPTH levels, and
   * offering a drop the published form would flatten would be a lie.
   */
  function cardRoom(c: Cont): number {
    if (c.scope === 'order' && sections.find((s) => s.id === c.sec)?.type !== 'fields') return 0;
    return MAX_CARD_DEPTH - (c.grp ? cardDepth(fields, c.sec, c.grp) + 1 : 0);
  }

  /** Will this card — with whatever is stacked inside it — fit in that container? */
  function cardFits(d: DragPayload, c: Cont): boolean {
    const room = cardRoom(c);
    if (d.kind === 'new-card') return room >= 1;
    if (d.kind !== 'card') return false;
    // A card cannot go inside itself, or inside a card it contains: the group
    // would be left with no home outside itself.
    if (c.sec === d.sec && c.grp && cardAndDescendants(fields, d.sec, d.id).has(c.grp)) return false;
    return room >= cardHeight(fields, d.sec, d.id);
  }

  /**
   * Land a card in `c`, before or after `targetKey` (null = at the end). A new
   * card is minted; an existing one is carried over with its fields and
   * everything nested in it.
   */
  function placeCard(d: DragPayload, c: Cont, targetKey: string | null, pos: string) {
    if (!cardFits(d, c)) return endDrag();
    let nextSections = sections;
    let flds: BField[];
    let id: string;

    if (d.kind === 'new-card') {
      id = uid('card');
      nextSections = sections.map((s) => (s.id === c.sec
        ? { ...s, cards: [...s.cards, { id, title: 'New Card', scope: c.scope, style: defaultStyle() }] }
        : s));
      flds = fields.map((f) => ({ ...f }));
      flds.push(mkCardMarker(id, c));
    } else if (d.kind === 'card') {
      const moved = relocateCard(sections, fields, { sec: d.sec, id: d.id }, c);
      nextSections = moved.sections;
      flds = moved.fields.map((f) => ({ ...f }));
      id = moved.id;
    } else return endDrag();

    const marker = flds.find((f) => f.cardId === id && f.section === c.sec);
    if (!marker) return endDrag();
    flds.splice(flds.indexOf(marker), 1);
    place(flds, marker, c, targetKey, pos);
    drag.current = null;
    setSections(nextSections); setFields(flds);
    setHint(null); setDragKind(''); setSel({ t: 'card', sec: c.sec, id }); mark();
  }

  const overCont = (c: Cont) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d) return;
    const nestable = NEST_DRAGS.includes(d.kind) && c.scope !== 'order' && !c.grp && canNestInto(d, c.scope);
    if (!FIELD_DRAGS.includes(d.kind) && !(CARD_DRAGS.includes(d.kind) && cardFits(d, c)) && !nestable) return;
    e.preventDefault(); e.stopPropagation();
    setHint((h) => (h && h.t === 'c' && sameCont(h.cont, c) ? h : { t: 'c', cont: c }));
  };

  const dropCont = (c: Cont) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d) return;
    e.preventDefault(); e.stopPropagation();
    if (CARD_DRAGS.includes(d.kind)) return placeCard(d, c, null, 'end');
    if (NEST_DRAGS.includes(d.kind)) {
      if (c.scope === 'order' || c.grp || !canNestInto(d, c.scope)) return;
      return nestWidget(d, c.sec, c.scope, { targetKey: null, pos: 'end' });
    }
    if (!FIELD_DRAGS.includes(d.kind)) return;
    const flds = fields.map((f) => ({ ...f }));
    if (d.kind === 'new-field') {
      const moving = newField(d.type, flds, countKeysOf(widgets), d.preset);
      place(flds, moving, c, null, 'end');
      drag.current = null;
      setFields(flds); setHint(null); setDragKind(''); setSel({ t: 'field', key: moving.key }); mark();
      return;
    }
    if (d.kind === 'new-row') {
      const row = uid('row');
      const a = mkSlot(c, row), b = mkSlot(c, row);
      const items = inCont(flds, c, widgetSecIds);
      const last = items[items.length - 1];
      const gi = last ? flds.indexOf(last) + 1 : flds.length;
      flds.splice(gi, 0, a, b);
      drag.current = null;
      setFields(flds); setHint(null); setDragKind(''); mark();
      return;
    }
    const dKey = (d as { kind: 'field'; key: string }).key;
    if (multi.length > 1 && multi.includes(dKey)) {
      const moved = moveFieldsTo({ fields, keys: multi, target: c, anchorKey: null, pos: 'end', widgetSecIds });
      if (!moved) return endDrag();
      drag.current = null;
      setFields(moved); setHint(null); setDragKind(''); mark();
      return;
    }
    const moving = flds.find((f) => f.key === dKey);
    if (!moving) return endDrag();
    flds.splice(flds.indexOf(moving), 1);
    place(flds, moving, c, null, 'end');
    drag.current = null;
    setFields(flds); setHint(null); setDragKind(''); mark();
  };

  const overZone = (idx: number) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d || !SECTION_DRAGS.includes(d.kind)) return;
    // A container that is already a root has nowhere to be promoted TO.
    if (d.kind === 'wnode' && findNode(widgets, d.scope)?.widget.tree.scope === d.scope) return;
    e.preventDefault(); e.stopPropagation();
    setHint((h) => (h && h.t === 'z' && h.idx === idx ? h : { t: 'z', idx }));
  };

  const dropZone = (idx: number) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d || !SECTION_DRAGS.includes(d.kind)) return;
    e.preventDefault(); e.stopPropagation();
    let next = [...sections];
    let nextFields = fields;
    let nextWidgets = widgets;
    let nextSel: Sel = sel;
    if (d.kind === 'new-section' || d.kind === 'new-page') {
      const id = uid('section');
      // A Page break is a section that starts a page (R4). Dropped first it
      // cannot break anything, so it is a plain section there.
      const breaks = d.kind === 'new-page' && idx > 0;
      next.splice(idx, 0, {
        id, title: breaks ? 'New Page' : 'New Section', type: 'fields', showFlag: '', hideWith: '', cards: [], style: defaultStyle(),
        ...(breaks ? { pageBreak: true } : {}),
      });
      nextSel = { t: 'sec', id };
    } else if (d.kind === 'new-widget') {
      const secId = freeSectionId('widget', next);
      const { widget } = mintWidget(widgets);
      nextWidgets = [...widgets, widget];
      next.splice(idx, 0, {
        id: secId, title: 'Repeatable Widget', type: 'widget', showFlag: '', hideWith: '',
        cards: [], style: defaultStyle(), widgetId: widget.id,
      });
      nextSel = { t: 'wnode', sec: secId, scope: widget.tree.scope };
    } else if (d.kind === 'wnode') {
      // Promoting a container out of its parent: it becomes a widget of its
      // own, in a section of its own, and takes its fields and cards along.
      const src = findNode(widgets, d.scope);
      if (!src || src.widget.tree.scope === d.scope) return endDrag();
      const clones = widgets.map(cloneWidget);
      const holder = clones.find((w) => w.id === src.widget.id)!;
      const { tree, taken } = detachNode(holder.tree, d.scope);
      if (!taken) return endDrag();
      holder.tree = tree;
      const secId = freeSectionId('widget', next);
      const promoted: FormWidget = { id: freeWidgetId(clones), name: taken.itemLabel || 'Widget', tree: taken };
      nextWidgets = [...clones, promoted];
      next.splice(idx, 0, {
        id: secId, title: taken.itemLabel || 'Repeatable Widget', type: 'widget', showFlag: '', hideWith: '',
        cards: [], style: defaultStyle(), widgetId: promoted.id,
      });
      const moved = moveScopePieces(next, fields, new Set(nodesOf(taken).map((n) => n.scope)), secId);
      next = moved.sections;
      nextFields = moved.fields;
      nextSel = { t: 'wnode', sec: secId, scope: d.scope };
    } else if (d.kind === 'section') {
      const si = next.findIndex((s) => s.id === d.id);
      if (si < 0) return endDrag();
      const [mv] = next.splice(si, 1);
      next.splice(idx > si ? idx - 1 : idx, 0, mv);
    }
    drag.current = null;
    setSections(next); setFields(nextFields); setWidgets(nextWidgets);
    setHint(null); setDragKind(''); setSel(nextSel); mark();
  };

  /**
   * Where a tap-added element lands (§4E E1).
   *
   * The palette's drag path names its own target by where it was dropped; a
   * tap has to be told. It goes into the container of whatever is selected —
   * a field's own container, a card's body, a widget's body, a section's own
   * group — and at the end of the last section when nothing is selected.
   */
  function tapTarget(): Cont | null {
    if (sel?.t === 'field') {
      const f = fields.find((x) => x.key === sel.key);
      if (f) return contOf(f, widgetSecIds);
    }
    if (sel?.t === 'card') {
      const card = sections.find((x) => x.id === sel.sec)?.cards.find((c) => c.id === sel.id);
      return cont(sel.sec, card?.scope || 'order', sel.id);
    }
    if (sel?.t === 'wnode') return cont(sel.sec, sel.scope, '');
    const secOf = (s: BSection) => cont(s.id, widgetSecIds.has(s.id) ? (widgetFor(s)?.tree.scope || 'order') : 'order', '');
    if (sel?.t === 'sec') {
      const s = sections.find((x) => x.id === sel.id);
      return s ? secOf(s) : null;
    }
    const last = sections[sections.length - 1];
    return last ? secOf(last) : null;
  }

  /** That container, said the way the palette sheet says it. */
  function tapTargetLabel(): string {
    const c = tapTarget();
    if (!c) return 'nowhere yet — add a section first';
    const sec = sections.find((x) => x.id === c.sec);
    const card = c.grp ? sec?.cards.find((x) => x.id === c.grp) : null;
    if (card) return `the card ${card.title || 'Card'}`;
    if (sec && widgetSecIds.has(sec.id)) return `the widget ${sec.title || sec.id}`;
    return `the section ${sec?.title || sec?.id || ''}`.trim();
  }

  /**
   * Add one palette entry by tapping it rather than dragging it.
   *
   * Every branch calls the SAME primitive the drop handler calls — `place`,
   * `placeCard`, or the section mint `dropZone` uses — so a tapped element
   * lands exactly where a dropped one would and one code path decides what a
   * new field is. Nothing about the drag path changes.
   */
  function addFromPalette(d: DragPayload) {
    setPickerOpen(false);
    const at = pickerAt;
    setPickerAt(null);
    if (d.kind === 'new-section' || d.kind === 'new-widget' || d.kind === 'new-page') {
      const next = [...sections];
      let nextWidgets = widgets;
      let nextSel: Sel = sel;
      if (d.kind === 'new-section' || d.kind === 'new-page') {
        const id = uid('section');
        const breaks = d.kind === 'new-page' && next.length > 0;
        next.push({
          id, title: breaks ? 'New Page' : 'New Section', type: 'fields', showFlag: '', hideWith: '', cards: [], style: defaultStyle(),
          ...(breaks ? { pageBreak: true } : {}),
        });
        nextSel = { t: 'sec', id };
      } else {
        const secId = freeSectionId('widget', next);
        const { widget } = mintWidget(widgets);
        nextWidgets = [...widgets, widget];
        next.push({
          id: secId, title: 'Repeatable Widget', type: 'widget', showFlag: '', hideWith: '',
          cards: [], style: defaultStyle(), widgetId: widget.id,
        });
        nextSel = { t: 'wnode', sec: secId, scope: widget.tree.scope };
      }
      setSections(next); setWidgets(nextWidgets); select(nextSel); mark();
      say(`Added a ${d.kind === 'new-section' ? 'section' : d.kind === 'new-page' ? 'page' : 'widget'} at the end of the form`);
      return;
    }

    // Where it lands: the spot the picker was opened for, else after the
    // selected field's row, else the end of what is selected (or the form).
    const c = at ? at.cont : tapTarget();
    if (!c) { toast('Add a section first — a field needs somewhere to live.', { kind: 'error' }); return; }
    const where = at ? at.label : tapTargetLabel();
    const afterKey = at ? at.afterKey : (sel?.t === 'field' ? sel.key : null);

    if (d.kind === 'new-card') {
      drag.current = d;
      placeCard(d, c, afterKey, 'below');
      say(`Added a card to ${where}`);
      return;
    }

    const flds = fields.map((f) => ({ ...f }));
    if (d.kind === 'new-field') {
      const moving = newField(d.type, flds, countKeysOf(widgets), d.preset);
      place(flds, moving, c, afterKey, 'below');
      setFields(flds); select({ t: 'field', key: moving.key }); mark();
      say(`Added ${moving.label || moving.key} to ${where}`);
      return;
    }
    if (d.kind === 'new-row') {
      const row = uid('row');
      const a = mkSlot(c, row), b = mkSlot(c, row);
      const items = inCont(flds, c, widgetSecIds);
      const last = items[items.length - 1];
      const gi = last ? flds.indexOf(last) + 1 : flds.length;
      flds.splice(gi, 0, a, b);
      setFields(flds); mark();
      say(`Added a side-by-side row to ${where}`);
    }
  }

  /**
   * Every container a field could be moved into, for the phone's "Move to…"
   * (§4E E1). The drag path reaches these by aiming at them; a tap needs them
   * listed. Each entry carries the same `Cont` the drop handler would build.
   */
  function moveTargets(): { cont: Cont; label: string }[] {
    const out: { cont: Cont; label: string }[] = [];
    for (const s of sections) {
      const isWidget = widgetSecIds.has(s.id);
      const base = isWidget ? (widgetFor(s)?.tree.scope || 'order') : 'order';
      const secName = s.title || s.id;
      out.push({ cont: cont(s.id, base, ''), label: isWidget ? `Widget: ${secName}` : `Section: ${secName}` });
      for (const card of s.cards) {
        out.push({ cont: cont(s.id, card.scope || base, card.id), label: `${secName} › ${card.title || 'Card'}` });
      }
    }
    return out;
  }

  /** Move one field into a container the picker named. */
  function moveFieldTo(key: string, c: Cont) {
    setMoveTo(null);
    const flds = fields.map((f) => ({ ...f }));
    const moving = flds.find((f) => f.key === key);
    if (!moving) return;
    flds.splice(flds.indexOf(moving), 1);
    moving.row = null;
    place(flds, moving, c, null, 'end');
    setFields(flds); setSel({ t: 'field', key }); mark();
    const sec = sections.find((x) => x.id === c.sec);
    const card = c.grp ? sec?.cards.find((x) => x.id === c.grp) : null;
    say(`Moved ${moving.label || key} to ${card ? (card.title || 'the card') : (sec?.title || c.sec)}`);
  }

  // ---- widget containers: mint, nest, promote, delete -------------------------
  /** An id no section is using — sections and widgets are addressed by id. */
  function freeSectionId(base: string, secs: BSection[]): string {
    const taken = new Set(secs.map((x) => x.id));
    let id = base, i = 2;
    while (taken.has(id)) id = base + i++;
    return id;
  }
  function freeWidgetId(ws: FormWidget[]): string {
    const taken = new Set(ws.map((w) => w.id));
    let id = 'widget', i = 2;
    while (taken.has(id)) id = 'widget' + i++;
    return id;
  }
  /** A brand-new container with a scope and count key nothing else claims. */
  function mintWidget(ws: FormWidget[]): { widget: FormWidget } {
    const { scope, num } = uniqueScope(ws, scopeSeq.current);
    scopeSeq.current = num;
    // The count key shares the submission's flat namespace with every answer,
    // so it has to clear the questions as well as the other containers.
    const taken = [...countKeysOf(ws), ...fields.map((f) => f.key)];
    return { widget: { id: freeWidgetId(ws), name: 'Widget', tree: newWidgetNode(scope, num, taken) } };
  }

  /**
   * May this drag land inside the container `scope`?
   *
   * Three ways it must not: a container into itself, a container into one of
   * its own descendants (which would orphan the tree), and any drop that would
   * push the stack past the depth the published definition allows — the server
   * silently drops nodes below it, so the canvas must not offer them.
   */
  function canNestInto(d: DragPayload, scope: string): boolean {
    if (!NEST_DRAGS.includes(d.kind)) return false;
    const target = findNode(widgets, scope);
    if (!target) return false;
    const childDepth = depthOf(target.widget.tree, scope) + 1;
    if (d.kind === 'new-widget') return childDepth + 1 <= MAX_WIDGET_DEPTH;
    if (d.kind !== 'wnode' || d.scope === scope) return false;
    const src = findNode(widgets, d.scope);
    if (!src) return false;
    if (nodesOf(src.node).some((n) => n.scope === scope)) return false;
    return childDepth + heightOf(src.node) <= MAX_WIDGET_DEPTH;
  }

  /**
   * What a drop on a container's BOX means: inside it, or beside it.
   *
   * The box covers everything it holds, so without this the only thing it
   * could offer was "nest inside me" — a container already stacked in a widget
   * could be dropped after the whole body but never above a sibling. The edges
   * therefore mean BESIDE: aim near the top or bottom of a nested container
   * and the drop lands before or after it, in the container that holds it.
   * The middle still means inside.
   *
   * The band is a quarter of the box, capped so a tall container (a Location
   * with everything in it) does not turn half its height into an edge.
   */
  type NestSpot = { mode: 'in' } | { mode: 'edge'; marker: BField; pos: string };
  function nestSpot(e: React.DragEvent, d: DragPayload, scope: string): NestSpot | null {
    if (!NEST_DRAGS.includes(d.kind)) return null;
    const marker = nodeMarkerOf(fields, scope);
    // A root container IS its section: there is nothing to sit beside it in,
    // and dropping a container relative to ITSELF means nothing.
    if (marker && !(d.kind === 'wnode' && d.scope === scope)) {
      const r = e.currentTarget.getBoundingClientRect();
      const band = Math.min(r.height * 0.25, 44);
      const y = e.clientY - r.top;
      const pos = y < band ? 'above' : y > r.height - band ? 'below' : '';
      if (pos && canNestInto(d, marker.scope)) return { mode: 'edge', marker, pos };
    }
    return canNestInto(d, scope) ? { mode: 'in' } : null;
  }

  const overNest = (scope: string) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d) return;
    const spot = nestSpot(e, d, scope);
    if (!spot) return;
    e.preventDefault(); e.stopPropagation();
    if (spot.mode === 'edge') {
      const { marker, pos } = spot;
      setHint((h) => (h && h.t === 'f' && h.key === marker.key && h.pos === pos ? h : { t: 'f', key: marker.key, pos }));
    } else {
      setHint((h) => (h && h.t === 'w' && h.scope === scope ? h : { t: 'w', scope }));
    }
  };

  /**
   * Nest a container inside the container `scope`, at a place of the author's
   * choosing.
   *
   * The tree says which container repeats inside which; the canvas spine says
   * WHERE it sits among its new parent's own fields and cards — the same
   * marker a card uses. `at` is that position: a field or marker to land
   * before or after, or the end of the body.
   */
  function nestWidget(
    d: DragPayload, secId: string, scope: string,
    at: { targetKey: string | null; pos: string } = { targetKey: null, pos: 'end' }
  ) {
    if (!canNestInto(d, scope)) return endDrag();
    let nextWidgets = widgets.map(cloneWidget);
    let nextSections = sections;
    let nextFields = fields;
    let child: WidgetNode;
    /** scopes whose markers travel with the container being moved */
    let moving = new Set<string>();

    if (d.kind === 'new-widget') {
      const { widget } = mintWidget(nextWidgets);
      child = widget.tree;
    } else if (d.kind === 'wnode') {
      const src = findNode(nextWidgets, d.scope);
      if (!src) return endDrag();
      const scopes = new Set(nodesOf(src.node).map((n) => n.scope));
      moving = scopes;
      const absorbed = src.widget.tree.scope === d.scope;
      if (absorbed) {
        child = src.widget.tree;
        nextWidgets = nextWidgets.filter((w) => w.id !== src.widget.id);
      } else {
        const holder = nextWidgets.find((w) => w.id === src.widget.id)!;
        const { tree, taken } = detachNode(holder.tree, d.scope);
        if (!taken) return endDrag();
        holder.tree = tree;
        child = taken;
      }
      // Move the contents out FIRST, then drop the emptied section: a whole
      // widget absorbed into another leaves a section behind with nothing to
      // render, and removing it before its cards were rescued would take them
      // with it.
      const moved = moveScopePieces(sections, fields, scopes, secId);
      nextSections = absorbed
        ? moved.sections.filter((x) => !(x.type === 'widget' && x.widgetId === src.widget.id))
        : moved.sections;
      nextFields = moved.fields;
    } else return endDrag();

    const holder = nextWidgets.find((w) => w.tree && nodesOf(w.tree).some((n) => n.scope === scope));
    if (!holder) return endDrag();
    holder.tree = attachChild(holder.tree, scope, child);

    // The marker: the moved container's own goes to the new spot; the ones
    // belonging to containers stacked inside it follow their parent's body.
    const body = cont(secId, scope, '');
    const flds = nextFields.map((f) => ({ ...f }));
    for (const f of flds) {
      if (f.nodeScope && moving.has(f.nodeScope) && f.nodeScope !== child.scope) {
        f.section = secId;
      }
    }
    const existing = flds.findIndex((f) => f.nodeScope === child.scope);
    const marker = existing >= 0 ? flds.splice(existing, 1)[0] : mkNodeMarker(child.scope, body);
    // A target that the move itself carried away leaves the drop at the end.
    const target = at.targetKey && flds.some((f) => f.key === at.targetKey) ? at.targetKey : null;
    place(flds, marker, body, target, target ? at.pos : 'end');

    drag.current = null;
    setWidgets(nextWidgets); setSections(nextSections); setFields(flds);
    setHint(null); setDragKind(''); setSel({ t: 'wnode', sec: secId, scope: child.scope }); mark();
  }

  const dropNest = (secId: string, scope: string) => (e: React.DragEvent) => {
    const d = drag.current;
    if (!d) return;
    const spot = nestSpot(e, d, scope);
    if (!spot) return;
    e.preventDefault(); e.stopPropagation();
    if (spot.mode === 'edge') {
      nestWidget(d, spot.marker.section, spot.marker.scope, { targetKey: spot.marker.key, pos: spot.pos });
    } else {
      nestWidget(d, secId, scope);
    }
  };

  // ---- mutations ---------------------------------------------------------------
  /**
   * Typing into one box is one undo step. A patch that sets a single string
   * (or a single nested object — the validation block, a style) is typing;
   * anything else (a toggle, a row added to the options) is a step of its own.
   */
  const typingKey = (scope: string, patch: object): CommitOptions | undefined => {
    const ks = Object.keys(patch);
    if (ks.length !== 1) return undefined;
    const v = (patch as Record<string, unknown>)[ks[0]];
    const typed = typeof v === 'string' || (!!v && typeof v === 'object' && !Array.isArray(v));
    return typed ? { coalesce: `${scope}:${ks[0]}` } : undefined;
  };
  const setField = (key: string, patch: Partial<BField>, opts?: CommitOptions) => {
    setFields((fs) => fs.map((f) => (f.key === key ? { ...f, ...patch } : f)), opts ?? typingKey(`field:${key}`, patch));
    mark();
  };
  function setFieldLabel(key: string, label: string) {
    const flds = fields.map((f) => ({ ...f }));
    const f = flds.find((x) => x.key === key);
    if (!f) return;
    f.label = label;
    let nextSel: Sel = sel;
    if (f.isNew && !f.keyTouched) {
      const others = flds.filter((x) => x !== f);
      const nk = deriveKeyIn(label, others, countKeysOf(widgets));
      f.key = nk;
      nextSel = { t: 'field', key: nk };
    }
    setFields(flds, { coalesce: `label:${key}` }); setSel(nextSel);
  }
  /**
   * Land the key being typed. A key that is empty, malformed or already taken
   * is left in the box for the person to fix rather than half-applied — and a
   * committed one moves everything that pointed at the old key with it.
   */
  function commitKey(f: BField) {
    const draft = keyDraft;
    if (!draft || draft.of !== f.key) return;
    const next = draft.text.trim();
    if (!next || next === f.key || keyProblem(next, fields, f.key, widgets)) { setKeyDraft(null); return; }
    const moved = renameFieldKey(f.key, next, { fields, rules, widgets });
    setFields(moved.fields.map((x) => (x.key === next ? { ...x, keyTouched: true } : x)));
    setRules(moved.rules);
    setWidgets(moved.widgets);
    // The document follows too (R5) — at publish, when the new key goes
    // live, from this record. A rename straight back cancels the record.
    setRenames((rs) => recordRename(rs, f.key, next));
    setKeyDraft(null);
    setSel({ t: 'field', key: next });
    mark();
  }

  const setSection = (id: string, patch: Partial<BSection>) => {
    setSections((ss) => ss.map((s) => (s.id === id ? { ...s, ...patch } : s)), typingKey(`sec:${id}`, patch));
    mark();
  };
  const setSecStyle = (id: string, patch: Partial<BSection['style']>) => {
    setSections((ss) => ss.map((s) => (s.id === id ? { ...s, style: { ...s.style, ...patch } } : s)), typingKey(`secstyle:${id}`, patch));
  };
  const setCard = (secId: string, cid: string, patch: Partial<BCard>) => {
    setSections((ss) => ss.map((s) => (s.id === secId ? { ...s, cards: s.cards.map((c) => (c.id === cid ? { ...c, ...patch } : c)) } : s)), typingKey(`card:${secId}:${cid}`, patch));
  };
  const setCardStyle = (secId: string, cid: string, patch: Partial<BStyle>) => {
    setSections((ss) => ss.map((s) => (s.id === secId
      ? { ...s, cards: s.cards.map((c) => (c.id === cid ? { ...c, style: { ...c.style, ...patch } } : c)) }
      : s)), typingKey(`cardstyle:${secId}:${cid}`, patch));
  };

  // ---- ergonomics (U4): duplicate, move by keyboard, announce ------------------
  /** Tell assistive tech what just happened; a repeat still reads because it differs by a space. */
  const say = (text: string) => {
    announceSeq.current += 1;
    setAnnounce(text + (announceSeq.current % 2 ? '' : '\u00a0'));
  };
  const cardTitle = (secId: string, cardId: string) =>
    sections.find((s) => s.id === secId)?.cards.find((c) => c.id === cardId)?.title || 'an untitled card';
  /** A field, a card or a slot, as the announcement names it. */
  const nameOf = (f: BField): string => f.cardId
    ? `the card ${cardTitle(f.section, f.cardId)}`
    : f.nodeScope ? 'a repeating group' : f.slot ? 'an empty slot' : (f.label || f.key);
  const describe = (m: FieldMove['landed']): string => m.kind === 'beside'
    ? `Moved ${m.pos} ${nameOf(m.anchor)}`
    : `Moved to ${m.card ? `the card ${m.card.title || 'Card'}` : 'the section body'}`;

  /** Rules are keyed by field key, so a copy has none — and the person is told. */
  const noteRulesLeft = (keys: string[], skipped = 0) => {
    // Distinct rules, not fields: one rule that names two copied fields is one rule.
    const touched = new Set(keys.flatMap((k) => rulesForField(rules, k).map((r) => r.id))).size;
    const parts: string[] = [];
    if (touched) parts.push(touched === 1 ? 'The rule about the original was not copied.' : `The ${touched} rules about the originals were not copied.`);
    if (skipped) parts.push(skipped === 1 ? 'A repeating group inside was left out.' : `${skipped} repeating groups inside were left out.`);
    if (parts.length) toast(`Duplicated. ${parts.join(' ')}`, { kind: 'info' });
  };
  function dupField(key: string) {
    const src = fields.find((f) => f.key === key);
    const r = duplicateField(fields, key, widgets, widgetSecIds);
    if (!r || !src) return;
    setFields(r.fields);
    setSel({ t: 'field', key: r.key }); setConfirm(false);
    noteRulesLeft([key]);
    say(`Duplicated ${src.label || src.key}`);
  }
  function dupCard(secId: string, cardId: string) {
    const r = duplicateCard(sections, fields, widgets, secId, cardId);
    if (!r) return;
    setSections(r.sections); setFields(r.fields);
    setSel({ t: 'card', sec: secId, id: r.id }); setConfirm(false);
    noteRulesLeft([...r.keys.keys()], r.skipped);
    say(`Duplicated the card ${cardTitle(secId, cardId)}`);
  }
  function dupSection(secId: string) {
    const r = duplicateSection(sections, fields, widgets, secId);
    if (!r) { toast('A repeating widget is its own container and is not duplicated here.', { kind: 'error' }); return; }
    setSections(r.sections); setFields(r.fields);
    setSel({ t: 'sec', id: r.id }); setConfirm(false);
    noteRulesLeft([...r.keys.keys()], r.skipped);
    say(`Duplicated the section ${sections.find((s) => s.id === secId)?.title || ''}`.trim());
  }
  /** Alt+↑/↓ within the container; with Shift, to the neighbouring container. */
  function moveSelected(dir: MoveDir, across: boolean) {
    if (sel?.t !== 'field') return;
    const r = across
      ? moveFieldAcross(fields, sections, sel.key, dir, widgetSecIds)
      : moveFieldWithin(fields, sel.key, dir, widgetSecIds);
    if (!r) {
      say(across
        ? (dir === 'up' ? 'Already in the first container of this section' : 'Already in the last container of this section')
        : (dir === 'up' ? 'Already at the top' : 'Already at the bottom'));
      return;
    }
    setFields(r.fields);
    say(describe(r.landed));
  }
  /** The menu's Move up / Move down: the same step, on a named field. */
  function moveSelectedKey(key: string, dir: MoveDir) {
    const r = moveFieldWithin(fields, key, dir, widgetSecIds);
    if (!r) return;
    setFields(r.fields);
    say(describe(r.landed));
  }
  /** Whether the selected field can step that way — for the menu items. */
  const canMove = (key: string, dir: MoveDir) => !!moveFieldWithin(fields, key, dir, widgetSecIds);

  /**
   * Step a section or a card one place (§4E E1).
   *
   * Both were drag-only: the section grip reorders sections, and a card is
   * carried by dropping it. A finger fires neither, so the row menu offers the
   * step as well. A section moves in `sections`; a card moves by reordering
   * its marker among the items of the container it is already in, which is
   * exactly what the drop path does when it lands a card next to a sibling.
   */
  function moveSectionBy(secId: string, dir: MoveDir) {
    const i = sections.findIndex((s) => s.id === secId);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const next = [...sections];
    const [mv] = next.splice(i, 1);
    next.splice(j, 0, mv);
    setSections(next); mark();
    say(`Moved the section ${mv.title || mv.id} ${dir}`);
  }
  const canMoveSection = (secId: string, dir: MoveDir) => {
    const i = sections.findIndex((s) => s.id === secId);
    return dir === 'up' ? i > 0 : i >= 0 && i < sections.length - 1;
  };

  /** The card's marker among its container's items, and its neighbours. */
  function cardNeighbours(secId: string, cardId: string) {
    const marker = fields.find((f) => f.cardId === cardId && f.section === secId);
    if (!marker) return null;
    const c = contOf(marker, widgetSecIds);
    const items = inCont(fields, c, widgetSecIds);
    const at = items.indexOf(marker);
    return { marker, items, at };
  }
  const canMoveCard = (secId: string, cardId: string, dir: MoveDir) => {
    const n = cardNeighbours(secId, cardId);
    if (!n || n.at < 0) return false;
    return dir === 'up' ? n.at > 0 : n.at < n.items.length - 1;
  };
  function moveCardBy(secId: string, cardId: string, dir: MoveDir) {
    const n = cardNeighbours(secId, cardId);
    if (!n || n.at < 0) return;
    const target = n.items[dir === 'up' ? n.at - 1 : n.at + 1];
    if (!target) return;
    const flds = fields.map((f) => ({ ...f }));
    const marker = flds.find((f) => f.key === n.marker.key)!;
    const other = flds.find((f) => f.key === target.key)!;
    flds.splice(flds.indexOf(marker), 1);
    const oi = flds.indexOf(other);
    flds.splice(dir === 'up' ? oi : oi + 1, 0, marker);
    setFields(flds); mark();
    const title = sections.find((s) => s.id === secId)?.cards.find((c) => c.id === cardId)?.title;
    say(`Moved the card ${title || 'Card'} ${dir}`);
  }

  /**
   * Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y step the history; Alt+arrows move the
   * selected field. Undo stays out of text boxes, where it is the box's own.
   * The handler lives in a ref so one listener sees the latest closure.
   */
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => undefined);
  keyHandler.current = (e: KeyboardEvent) => {
    if (testMode) return;
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    const editing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!t?.isContentEditable;
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    // While a sheet or dialog is up, the keys are its own.
    const overlay = pickerOpen || !!besideFor || !!moveTo || promoting || resetting;
    if (mod && !e.altKey && (k === 'z' || k === 'y')) {
      if (editing) return;
      e.preventDefault();
      if (k === 'y' || e.shiftKey) history.redo(); else history.undo();
      return;
    }
    if (e.altKey && !mod && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      // Not from inside a text box either (R0 found it fired while typing).
      if (editing || sel?.t !== 'field') return;
      e.preventDefault();
      moveSelected(e.key === 'ArrowUp' ? 'up' : 'down', e.shiftKey);
      return;
    }
    if (editing || overlay) return;
    // The clipboard (R4): fields only, from the canvas, never over a native
    // text selection — copying words the person highlighted must stay theirs.
    const nativeSel = !!(window.getSelection && window.getSelection()?.toString());
    if (mod && !e.altKey && !e.shiftKey && k === 'c') {
      if (nativeSel || !selKeys.length) return;
      e.preventDefault(); copySel(); return;
    }
    if (mod && !e.altKey && !e.shiftKey && k === 'v') {
      if (!CLIP?.fields.length) return;
      e.preventDefault(); pasteClip(); return;
    }
    // ⌘/Ctrl+Shift+D, not ⌘D: that one is the browser's bookmark.
    if (mod && e.shiftKey && !e.altKey && k === 'd') {
      if (!selKeys.length) return;
      e.preventDefault(); duplicateSel(); return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && !mod && !e.altKey) {
      if (nativeSel || !selKeys.length) return;
      e.preventDefault(); deleteSel(); return;
    }
    if (e.key === '/' && !mod && !e.altKey) {
      e.preventDefault(); openPicker(null); return;
    }
    if (e.key === 'Escape') {
      // No overlay is up (checked above), so Esc lets go of the multi-selection
      // and disarms a pending delete.
      if (multi.length > 1 || barConfirm) { clearMulti(); }
    }
  };
  useEffect(() => {
    const on = (e: KeyboardEvent) => keyHandler.current(e);
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, []);

  // Undo can remove the thing that was selected; the panel must not describe a ghost.
  useEffect(() => {
    if (!sel) return;
    const ok = sel.t === 'field' ? fields.some((f) => f.key === sel.key)
      : sel.t === 'sec' ? sections.some((s) => s.id === sel.id)
        : sel.t === 'card' ? sections.some((s) => s.id === sel.sec && s.cards.some((c) => c.id === sel.id))
          : sel.t === 'wnode' ? !!findNode(widgets, sel.scope)
            : true;
    if (!ok) { setSel(null); setConfirm(false); }
  }, [sel, fields, sections, widgets]);
  /**
   * The inspector follows the selection, whichever path set it — a drop, the
   * Outline, an undo: its tab points at the kind of thing selected, its page
   * comes on screen, and the multi-selection is the one field unless the
   * click that set it was a toggle (which adds the key itself first).
   */
  useEffect(() => {
    if (!sel) { if (multi.length) setMulti([]); return; }
    setInspTab(sel.t === 'field' ? 'field' : sel.t === 'form' ? 'form' : 'page');
    setInspHidden(false);
    const secId = sectionOfSel(sel);
    if (secId) { const ix = pageIndexOfSection(secId); if (ix >= 0 && ix !== pageIx) setPageIx(ix); }
    if (sel.t === 'field') { if (!multi.includes(sel.key)) setMulti([sel.key]); }
    else if (multi.length) setMulti([]);
    setBarConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel]);
  // The same for the multi-selection: keys that left the form leave it too.
  useEffect(() => {
    if (multi.length && multi.some((k) => !fields.some((f) => f.key === k))) {
      setMulti((m) => m.filter((k) => fields.some((f) => f.key === k)));
      setBarConfirm(false);
    }
  }, [multi, fields]);

  /**
   * A list pasted into an EMPTY option row becomes that many rows. A row with
   * something typed in it keeps the paste as text, the way a text box does.
   */
  function pasteIntoRow(e: React.ClipboardEvent<HTMLInputElement>, f: BField, i: number) {
    const text = e.clipboardData.getData('text');
    const o = f.options[i];
    if (!/\r?\n/.test(text.trim()) || (o.value || '').trim() || (o.text || '').trim()) return;
    const parsed = parseOptionList(text);
    if (!parsed.length) return;
    e.preventDefault();
    const rest = f.options.filter((_, j) => j !== i);
    const have = new Set(rest.map((x) => x.value));
    const fresh = parsed.filter((p) => !have.has(p.value));
    setField(f.key, { options: [...rest.slice(0, i), ...fresh, ...rest.slice(i)] });
  }
  const rowMenu = (id: string, items: React.ReactNode, inline = false) => (
    <span className={`fb-row-menu${inline ? ' inline' : ''}${menu === id ? ' open' : ''}`}
      // A click in the menu is not a click on the row: "Duplicate" selects
      // the copy, and the row's own onClick must not re-select the original.
      onClick={(e) => e.stopPropagation()}
      draggable={false} onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <RowMenu open={menu === id} onToggle={(o) => setMenu(o ? id : null)}>{items}</RowMenu>
    </span>
  );
  const removeSlot = (key: string) => { setFields((fs) => fs.filter((f) => f.key !== key)); };

  /** Every widget scope a section renders — a widget section's whole tree. */
  function scopesOfSection(secId: string): Set<string> {
    const sec = sections.find((x) => x.id === secId);
    const w = sec ? widgetFor(sec) : undefined;
    return new Set(w?.tree ? nodesOf(w.tree).map((n) => n.scope) : []);
  }

  /** Drop a container and everything under it: child containers, fields, cards. */
  function deleteWidgetNode(secId: string, scope: string) {
    const found = findNode(widgets, scope);
    if (!found) return;
    const scopes = new Set(nodesOf(found.node).map((n) => n.scope));
    const isRoot = found.widget.tree.scope === scope;
    setWidgets((ws) => (isRoot
      ? ws.filter((w) => w.id !== found.widget.id)
      : ws.map((w) => (w.id === found.widget.id ? { ...w, tree: detachNode(w.tree, scope).tree } : w))));
    // A root container IS its section — nothing would be left to render.
    setSections((ss) => (isRoot
      ? ss.filter((x) => x.id !== secId)
      : ss.map((x) => (x.id === secId ? { ...x, cards: x.cards.filter((c) => !scopes.has(c.scope)) } : x))));
    // A card that repeated inside the container goes with it — marker included.
    setFields((fs) => fs.filter((f) => !scopes.has(f.scope)));
    setSel(null); setConfirm(false);
  }

  /**
   * Remove what `target` names. No confirm here: the panel's Delete asks
   * twice, the row menu's relies on undo being one keystroke away.
   */
  function deleteTarget(target: Sel) {
    if (!target) return;
    if (target.t === 'field') setFields((fs) => fs.filter((f) => f.key !== target.key));
    else if (target.t === 'card') {
      // Deleting a card unwraps it: its fields, and any card nested in it,
      // stay on the form and move up into whatever held the card.
      const marker = markerOf(fields, target.sec, target.id);
      const up = marker?.group || '';
      setSections((ss) => ss.map((s) => (s.id === target.sec ? { ...s, cards: s.cards.filter((c) => c.id !== target.id) } : s)));
      setFields((fs) => fs
        .filter((f) => !(f.cardId === target.id && f.section === target.sec))
        .map((f) => (f.section === target.sec && f.group === target.id ? { ...f, group: up } : f)));
    } else if (target.t === 'wnode') {
      deleteWidgetNode(target.sec, target.scope);
      return;
    } else if (target.t === 'sec') {
      // Deleting a widget section takes its containers with it: nothing else
      // renders them, and a widget nothing renders is a scope with no home.
      const scopes = scopesOfSection(target.id);
      const dead = sections.find((x) => x.id === target.id);
      if (dead?.widgetId) setWidgets((ws) => ws.filter((w) => w.id !== dead.widgetId));
      setSections((ss) => ss.filter((s) => s.id !== target.id));
      // Its card markers name this section too, so they go with it.
      setFields((fs) => fs.filter((f) => f.section !== target.id && !scopes.has(f.scope)));
    }
    setSel(null); setConfirm(false);
  }
  function doDelete() {
    if (!sel) return;
    if (!confirm) { setConfirm(true); return; }
    deleteTarget(sel);
  }
  /** The row menu's Delete: one click, then a toast that names the way back. */
  function deleteFromMenu(target: Sel, what: string) {
    deleteTarget(target);
    say(`Deleted ${what}`);
    toast(`Deleted ${what}. Undo (Ctrl+Z) puts it back.`, { kind: 'info' });
  }

  /** Edit one container's settings; the widget's name follows its item label. */
  const setWNode = (scope: string, patch: Partial<WidgetNode>) => {
    setWidgets((ws) => ws.map((w) => {
      if (!w.tree || !nodesOf(w.tree).some((n) => n.scope === scope)) return w;
      const tree = patchNodeIn(w.tree, scope, patch);
      return { ...w, tree, ...(tree.scope === scope && patch.itemLabel !== undefined ? { name: patch.itemLabel || 'Widget' } : {}) };
    }));
    mark();
  };

  // Saving and publishing belong to the workspace bar above the tabs: what
  // gets published is the whole form — canvas, logic and presentation
  // together — so no single tab owns the button.

  // ---- test mode ------------------------------------------------------------------
  function toggleTest() {
    setTestMode((t) => {
      const next = !t;
      setTestState(next ? createInitialState(testDef, lensCfg) : null);
      return next;
    });
    setSel(null); setHint(null); setHlRule(null); setConfirm(false);
  }

  // =================================================================================
  // Canvas renderers (plain functions, not components — keeps input focus stable)
  // =================================================================================
  function renderFieldEl(f: BField, oneCol: boolean) {
    if (f.slot) {
      return (
        <div key={f.key} onDragOver={overField(f.key)} onDrop={dropField(f.key)}
          style={{ border: '2px dashed #cbd5e0', borderRadius: 8, height: 41, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#a0aec0', fontSize: 12.5, fontWeight: 600, background: '#fafbfc', minWidth: 0 }}>
          Drop a field here
          <span onClick={(e) => { e.stopPropagation(); removeSlot(f.key); }}
            style={{ cursor: 'pointer', color: '#c53030', border: '1px solid #fed7d7', borderRadius: 6, padding: '0 6px', background: '#fff', fontSize: 12 }}>×</span>
        </div>
      );
    }

    const isSel = sel?.t === 'field' && sel.key === f.key;
    const inMulti = multi.includes(f.key);
    const hintPos = hint && hint.t === 'f' && hint.key === f.key ? hint.pos : null;
    const hidden = lens.id ? !visibleUnder(f, lens) : false;
    const hl = hlKeys?.has(f.key);
    // While something is in the air over this field, the accent edge is the
    // only thing worth seeing: it is the answer to "where will this land". A
    // selection ring drawn around the same box hides it, so the edge takes
    // over for as long as the drag is over the field.
    const shadows = hintPos ? [DROP_EDGE[hintPos]] : [
      ...(isSel || inMulti ? [SEL_RING] : []),
      ...(hl ? ['0 0 0 2px var(--logic-dash)'] : []),
    ];
    const joining = hintPos === 'left' || hintPos === 'right';
    const [bTxt, bBg, bFg] = BADGE[f.vis] || BADGE.shown;
    let ghost = f.placeholder || '';
    if (f.type === 'select') ghost = f.optionsPreset === 'us_states' ? 'State list' : f.optionsPreset === 'countries' ? 'Country list' : 'Select…';
    if (f.type === 'date' && !ghost) ghost = 'mm/dd/yyyy';
    if (f.type === 'datetime' && !ghost) ghost = 'mm/dd/yyyy, --:-- --';
    if (f.type === 'time' && !ghost) ghost = '--:-- --';
    if (f.type === 'url' && !ghost) ghost = 'https://';
    if (f.type === 'file') ghost = 'Choose files…';
    const frules = rulesForField(rules, f.key);
    const badge = <span className="fb-badge" style={{ background: bBg, color: bFg }}>{bTxt}</span>;
    const ruleBadge = frules.length > 0 && (
      <span className="fb-rule-badge" title={frules.map((r) => r.name).join(' · ')}>⚡ {frules.length}</span>
    );
    void oneCol;
    // The single selected field's inline toolbar (R4): ↑ ↓ ⧉ ⇔ ⌫. Each is a
    // path the ⋯ menu also offers, named so neither steals the other's name.
    const toolbar = isSel && !testMode && multi.length <= 1 && (
      <span className="fb-el-tools" onClick={(e) => e.stopPropagation()}
        draggable={false} onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        <button type="button" aria-label="Move up a row" title="Move up (Alt+↑)" disabled={!canMove(f.key, 'up')}
          onClick={() => moveSelectedKey(f.key, 'up')}>↑</button>
        <button type="button" aria-label="Move down a row" title="Move down (Alt+↓)" disabled={!canMove(f.key, 'down')}
          onClick={() => moveSelectedKey(f.key, 'down')}>↓</button>
        <button type="button" aria-label="Duplicate this field" title="Duplicate (Ctrl+Shift+D)" onClick={() => dupField(f.key)}>⧉</button>
        <button type="button" aria-label="Put beside another field…" title="Put beside… — share a row without dragging"
          disabled={!besideCandidates(f.key).length} onClick={() => setBesideFor(f.key)}>⇔</button>
        <button type="button" aria-label="Remove this field" title="Delete (Undo puts it back)" className="danger"
          onClick={() => deleteFromMenu({ t: 'field', key: f.key }, f.label || f.key)}>⌫</button>
      </span>
    );
    return (
      <div key={f.key} draggable data-key={f.key} className={`fb-el${isSel ? ' sel' : ''}${inMulti ? ' msel' : ''}`}
        onDragStart={startDrag({ kind: 'field', key: f.key })} onDragEnd={endDrag}
        onDragOver={overField(f.key)} onDrop={dropField(f.key)}
        onClick={(e) => clickField(e, f.key)}
        style={{
          position: 'relative', borderRadius: 6, cursor: 'grab', padding: 4, margin: -4,
          opacity: hidden ? 0.35 : 1, boxShadow: shadows.join(', ') || 'none',
          background: joining ? 'var(--accent-wash)' : isSel || inMulti ? 'var(--surface)' : hl ? 'color-mix(in srgb, var(--logic-dash) 8%, transparent)' : 'transparent', minWidth: 0,
        }}>
        {f.type === 'heading' || f.type === 'paragraph' || f.type === 'image' ? (
          <>
            {/* A content block (U5) wears the form's own classes, so the
                canvas shows what the page will. Badges as for the line:
                only when hidden or a rule reaches it. */}
            {(f.vis !== 'shown' || frules.length > 0) && (
              <div style={{ marginBottom: 2 }}>{f.vis !== 'shown' && badge}{ruleBadge}</div>
            )}
            {f.type === 'heading' && (() => {
              const n = Math.max(1, Math.min(3, Number(f.heading?.level) || 1));
              const Tag = (`h${n + 1}`) as 'h2' | 'h3' | 'h4';
              return <Tag className={`of-heading of-heading-${n}`} style={{ margin: '4px 0' }}>{f.label || 'Heading'}</Tag>;
            })()}
            {f.type === 'paragraph' && ((f.paragraph?.text || '').trim()
              ? <RichBlocks className="of-paragraph" text={f.paragraph!.text} />
              : <div className="of-paragraph" style={{ color: 'var(--of-ink-soft, #a0aec0)', fontStyle: 'italic' }}>Paragraph — click to write it.</div>)}
            {f.type === 'image' && ((f.image?.url || '').trim()
              ? (
                <figure className={`of-image of-image-${f.image?.align || 'center'}`} style={{ margin: '4px 0' }}>
                  <img src={f.image!.url} alt={f.image?.alt || ''} style={Number(f.image?.width) > 0 ? { width: `${Number(f.image!.width)}px` } : undefined} />
                </figure>
              )
              : (
                <div style={{ border: '2px dashed var(--of-border, #e2e8f0)', borderRadius: 8, padding: '18px 14px', textAlign: 'center', color: 'var(--of-ink-soft, #a0aec0)', fontSize: 13 }}>
                  Image — upload a picture or paste a URL.
                </div>
              ))}
          </>
        ) : f.type === 'divider' ? (
          <>
            {/* A line has no label to hang badges off, so they only appear
                when there is something to say: it is hidden, or a rule
                reaches it. Otherwise the canvas shows just the line. */}
            {(f.vis !== 'shown' || frules.length > 0) && (
              <div style={{ marginBottom: 2 }}>{f.vis !== 'shown' && badge}{ruleBadge}</div>
            )}
            <hr className="of-divider" style={{
              borderTopWidth: Math.max(1, Math.min(8, Number(f.divider?.thickness) || 1)),
              ...(f.divider?.color ? { borderTopColor: f.divider.color } : {}),
              marginTop: Math.max(0, Math.min(80, Number(f.divider?.space ?? 20))),
              marginBottom: Math.max(0, Math.min(80, Number(f.divider?.space ?? 20))),
            }} />
          </>
        ) : f.type === 'checkbox' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 41 }}>
            <span style={{ width: 20, height: 20, border: '2px solid var(--of-border, #cbd5e0)', borderRadius: 4, background: 'var(--of-input-bg, #fff)', flex: 'none' }} />
            <span style={{ color: 'var(--of-ink, #2d3748)', fontWeight: 600, fontSize: 14 }}>{f.label}</span>
            {badge}{ruleBadge}
          </div>
        ) : (
          <>
            <div style={{ color: 'var(--of-ink, #2d3748)', fontWeight: 600, marginBottom: 6, fontSize: 14, lineHeight: 1.35 }}>
              {f.label}
              {f.req === 'whenShown' && (form.settings?.requiredStyle?.marker === 'label'
                ? <span style={{ color: '#fc8181', marginLeft: 6, fontSize: 11, fontWeight: 700, fontVariant: 'small-caps', letterSpacing: '.04em' }}>Required</span>
                : <span style={{ color: '#fc8181', marginLeft: 2 }}>*</span>)}
              {f.req === 'never' && form.settings?.requiredStyle?.optionalSuffix !== false && <span style={{ color: '#a0aec0', fontWeight: 400, fontSize: 12 }}> (optional)</span>}
              {badge}{ruleBadge}
            </div>
            {f.type === 'select' ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '2px solid var(--of-border, #e2e8f0)', borderRadius: 8, background: 'var(--of-input-bg, #fff)', height: 41, padding: '0 14px' }}>
                <span style={{ color: 'var(--of-ink-soft, #a0aec0)', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ghost}</span>
                <span style={{ color: '#718096', fontSize: 10, flex: 'none' }}>▼</span>
              </div>
            ) : f.type === 'radio' || f.type === 'multiselect' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0' }}>
                {(f.options.length ? f.options.slice(0, 4) : [{ value: '', text: 'Option 1' }, { value: '', text: 'Option 2' }]).map((o, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 16, height: 16, border: '2px solid var(--of-border, #cbd5e0)', borderRadius: f.type === 'radio' ? '50%' : 4, background: 'var(--of-input-bg, #fff)', flex: 'none' }} />
                    {o.image && <img src={o.image} alt="" style={{ width: 36, height: 27, objectFit: 'cover', borderRadius: 4 }} />}
                    <span style={{ color: 'var(--of-ink, #2d3748)', fontSize: 13.5 }}>{o.text || o.value || `Option ${i + 1}`}</span>
                  </div>
                ))}
                {f.options.length > 4 && <span style={{ color: '#a0aec0', fontSize: 12 }}>… {f.options.length - 4} more</span>}
                {f.allowOther && !f.optionsPreset && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 16, height: 16, border: '2px solid var(--of-border, #cbd5e0)', borderRadius: f.type === 'radio' ? '50%' : 4, background: 'var(--of-input-bg, #fff)', flex: 'none' }} />
                    <span style={{ color: 'var(--of-ink, #2d3748)', fontSize: 13.5 }}>{(f.otherLabel || '').trim() || 'Other'}</span>
                    <span style={{ color: '#a0aec0', fontSize: 12 }}>+ write-in</span>
                  </div>
                )}
              </div>
            ) : f.type === 'rating' ? (
              <div style={{ color: '#f6ad55', fontSize: 22, letterSpacing: 2 }}>
                {'★'.repeat(Math.max(1, Math.min(10, parseInt(f.ratingMax || '', 10) || 5)))}
              </div>
            ) : f.type === 'scale' ? (() => {
              const sc = f.scale || emptyScale();
              const min = sc.min === '0' ? 0 : 1;
              const max = Math.max(min + 1, Math.min(10, parseInt(sc.max || '', 10) || 5));
              return (
                <div className="of-scale" style={{ pointerEvents: 'none' }}>
                  <div className="of-scale-steps">
                    {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
                      <span key={n} className="of-scale-step" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</span>
                    ))}
                  </div>
                  {(sc.minLabel || sc.maxLabel) && (
                    <div className="of-scale-ends"><span>{sc.minLabel}</span><span>{sc.maxLabel}</span></div>
                  )}
                </div>
              );
            })() : f.type === 'matrix' ? (() => {
              const mx = f.matrix || emptyMatrix();
              const rows = mx.rows.filter((o) => o.value.trim() || o.text.trim()).slice(0, 4);
              const cols = mx.columns.filter((o) => o.value.trim() || o.text.trim()).slice(0, 5);
              return (
                <table className="of-matrix" style={{ pointerEvents: 'none' }}>
                  <thead><tr><th /> {cols.map((c, i) => <th key={i}>{c.text || c.value}</th>)}</tr></thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <th>{r.text || r.value}</th>
                        {cols.map((_, j) => (
                          <td key={j}><span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid var(--of-border, #cbd5e0)', borderRadius: mx.multi ? 4 : '50%', background: 'var(--of-input-bg, #fff)' }} /></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })() : f.type === 'ranking' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0' }}>
                {(f.options.length ? f.options.slice(0, 4) : [{ value: '', text: 'Option 1' }, { value: '', text: 'Option 2' }]).map((o, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'var(--of-well, #edf2f7)', color: '#718096', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{i + 1}</span>
                    <span style={{ color: 'var(--of-ink, #2d3748)', fontSize: 13.5 }}>{o.text || o.value || `Option ${i + 1}`}</span>
                    <span style={{ color: '#a0aec0', fontSize: 11 }}>▲ ▼</span>
                  </div>
                ))}
                {f.options.length > 4 && <span style={{ color: '#a0aec0', fontSize: 12 }}>… {f.options.length - 4} more</span>}
              </div>
            ) : f.type === 'signature' ? (
              <div style={{
                position: 'relative', border: '1px solid var(--of-border, #cbd5e0)', borderRadius: 10,
                background: 'var(--of-input-bg, #fdfdfe)',
                height: Math.max(80, Math.min(400, parseInt(f.signature?.height || '', 10) || 160)) / 2,
              }}>
                <span style={{ position: 'absolute', left: 14, right: 14, bottom: '26%', borderBottom: '1px solid #d3dbe5' }} />
                <span style={{ position: 'absolute', left: 18, bottom: 'calc(26% + 5px)', color: 'var(--of-ink-soft, #a0aec0)', fontSize: 12 }}>
                  {f.signature?.type && f.signature?.draw === false ? 'Type to sign' : 'Sign here'}
                </span>
              </div>
            ) : f.type === 'payment' ? (
              <div style={{ border: '2px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ color: '#4a5568', fontSize: 13 }}>💳 Paid via Stripe at submit</span>
                <span style={{ color: '#2d3748', fontWeight: 700, fontSize: 15 }}>
                  {f.payment?.amountMode === 'field'
                    ? (f.payment.amountField ? `= ${lbl(f.payment.amountField)}` : 'amount from a field')
                    : `${Number(f.payment?.amountFixed) > 0 ? Number(f.payment?.amountFixed).toFixed(2) : '0.00'} ${(f.payment?.currency || 'usd').toUpperCase()}`}
                </span>
              </div>
            ) : f.type === 'name' ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {['First Name', 'Last Name'].map((s) => (
                  <div key={s} style={{ minWidth: 0 }}>
                    <div style={{ border: '2px solid var(--of-border, #e2e8f0)', borderRadius: 8, background: 'var(--of-input-bg, #fff)', height: 41 }} />
                    <div style={{ color: 'var(--of-ink-soft, #a0aec0)', fontSize: 11.5, marginTop: 3 }}>{s}</div>
                  </div>
                ))}
              </div>
            ) : f.type === 'address' ? (() => {
              const fm = addressFormat(f.addressDefaultCountry || undefined);
              const box = (s: string) => (
                <div key={s} style={{ minWidth: 0 }}>
                  <div style={{ border: '2px solid var(--of-border, #e2e8f0)', borderRadius: 8, background: 'var(--of-input-bg, #fff)', height: 36 }} />
                  <div style={{ color: 'var(--of-ink-soft, #a0aec0)', fontSize: 11.5, marginTop: 3 }}>{s}</div>
                </div>
              );
              // Mirror the renderer: Address 1 / Address 2 / City–State–Zip on
              // one line (city widest) / Country on its own line, with only the
              // country's real sub-fields — never an empty placeholder cell.
              const middle = [
                fm.cityLabel,
                ...(fm.stateLabel ? [fm.stateLabel] : []),
                ...(fm.postalLabel ? [fm.postalLabel] : []),
              ];
              const cols = middle.length === 3 ? '2fr 1fr 1fr' : middle.length === 2 ? '2fr 1fr' : '1fr';
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {f.addressKind === 'shipping' && box('Company / Name')}
                  {f.addressKind === 'shipping' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {box('Attention')}
                      {box('Phone Number')}
                    </div>
                  )}
                  {box('Address Line 1')}
                  {box('Address Line 2')}
                  <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12 }}>{middle.map(box)}</div>
                  {box(f.addressDefaultCountry ? `Country (${f.addressDefaultCountry})` : 'Country')}
                </div>
              );
            })() : f.type === 'textarea' ? (
              <div style={{ border: '2px solid var(--of-border, #e2e8f0)', borderRadius: 8, background: 'var(--of-input-bg, #fff)', height: 90, padding: '10px 14px', color: 'var(--of-ink-soft, #a0aec0)', fontSize: 15 }}>{ghost}</div>
            ) : (
              <div style={{ border: '2px solid var(--of-border, #e2e8f0)', borderRadius: 8, background: 'var(--of-input-bg, #fff)', height: 41, lineHeight: '37px', padding: '0 14px', color: 'var(--of-ink-soft, #a0aec0)', fontSize: 15, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{ghost}</div>
            )}
          </>
        )}
        {f.infoText && <div style={{ color: '#718096', fontSize: 13, marginTop: 4, fontStyle: 'italic' }}><RichInline text={f.infoText} /></div>}
        {!testMode && rowMenu(`field:${f.key}`, (
          <>
            <button onClick={() => dupField(f.key)}>Duplicate</button>
            <button disabled={!canMove(f.key, 'up')} onClick={() => { select({ t: 'field', key: f.key }); moveSelectedKey(f.key, 'up'); }}>Move up</button>
            <button disabled={!canMove(f.key, 'down')} onClick={() => { select({ t: 'field', key: f.key }); moveSelectedKey(f.key, 'down'); }}>Move down</button>
            {/* The tap paths (R4): a row without dragging, and another container
                without dragging — the latter was under 900 only, and with one
                page on show at a time a mouse needs it to reach another page. */}
            <button disabled={!besideCandidates(f.key).length} onClick={() => { select({ t: 'field', key: f.key }); setBesideFor(f.key); }}>Put beside…</button>
            <button onClick={() => { select({ t: 'field', key: f.key }); setMoveTo(f.key); }}>Move to…</button>
            <div className="rule" />
            <button className="danger" onClick={() => deleteFromMenu({ t: 'field', key: f.key }, f.label || f.key)}>Delete</button>
          </>
        ))}
        {toolbar}
      </div>
    );
  }

  /**
   * One container's contents: the fields in it and the cards dropped between
   * them, in canvas order. `c` says which container — a section body, a
   * repeatable widget's body, or a card — so the same renderer serves all
   * three and a card dragged into a widget looks and behaves exactly like one
   * dropped in a section.
   *
   * `depth` is how deep in cards this container sits, which is what the
   * renderer uses to alternate a nested card's surface.
   */
  function renderGroup(sec: BSection, c: Cont, depth: number, oneCol: boolean, opts: {
    emptyNote?: string; pad?: number;
    /** how to draw a nested container — only a widget's own body passes one */
    nested?: (marker: BField) => React.ReactNode;
  } = {}) {
    const items = inCont(fields, c, widgetSecIds);
    const contHint = hint && hint.t === 'c' && sameCont(hint.cont, c);
    const boxStyle: React.CSSProperties = { padding: '2px 0', minHeight: 10 };
    if (contHint) { boxStyle.outline = '2px dashed var(--accent)'; boxStyle.outlineOffset = 3; boxStyle.background = 'var(--accent-wash)'; }
    return (
      <div className="fb-cont-body" data-cont={contKey(c)}
        style={boxStyle} onDragOver={overCont(c)} onDrop={dropCont(c)}>
        {pack(items).map((run) => {
          if (run[0].cardId) return renderCardBox(sec, run[0], depth, oneCol);
          if (run[0].nodeScope) return opts.nested ? opts.nested(run[0]) : null;
          return (
            <div key={run[0].key + '-r'} className={`fb-row of-row-${oneCol ? 1 : Math.min(run.length, 4)}`}>
              {run.map((f) => renderFieldEl(f, oneCol))}
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="fb-empty-drop" style={opts.pad ? { padding: opts.pad, fontSize: opts.pad < 14 ? 12 : 13 } : undefined}>
            {opts.emptyNote || 'Drag fields here'}
          </div>
        )}
      </div>
    );
  }

  /**
   * A card, drawn the way the published form draws it: the same heading, the
   * same surface, the same alternation as it nests. The only thing the canvas
   * adds is a grip to move it by — an untitled card gets a slim tab to hold,
   * because on the form it has no heading to grab.
   */
  function renderCardBox(sec: BSection, marker: BField, depth: number, oneCol: boolean): React.ReactNode {
    const cardId = marker.cardId!;
    const card = sec.cards.find((x) => x.id === cardId);
    if (!card) return null;
    const inner = cont(sec.id, marker.scope, cardId);
    const isSel = sel?.t === 'card' && sel.sec === sec.id && sel.id === cardId;
    const hintPos = hint && hint.t === 'f' && hint.key === marker.key ? hint.pos : null;
    const contHint = hint && hint.t === 'c' && sameCont(hint.cont, inner);
    const d = Math.min(depth, 2);
    const pick = (e: React.MouseEvent) => {
      e.stopPropagation();
      select({ t: 'card', sec: sec.id, id: cardId });
    };
    const box: React.CSSProperties = { ...cardStyleOf(card), cursor: 'pointer' };
    if (isSel && !hintPos) box.boxShadow = SEL_RING;
    if (contHint) { box.outline = '2px dashed var(--accent)'; box.outlineOffset = 3; }
    const grip = !testMode && (
      <span className="fb-sec-grip fb-card-grip" draggable
        onDragStart={startDrag({ kind: 'card', sec: sec.id, id: cardId })} onDragEnd={endDrag}
        onClick={pick}
        title="Drag to move this card — into another card, another widget, or another section">⠿</span>
    );
    // As for a field: the drop edge replaces the selection ring rather than
    // being drawn under it.
    const shadows = hintPos ? [DROP_EDGE[hintPos]] : [];
    return (
      <div key={marker.key} onDragOver={overField(marker.key)} onDrop={dropField(marker.key)}
        style={{ boxShadow: shadows.join(', ') || 'none', borderRadius: 8 }}>
        {card.title
          ? (
            <div className={`of-subsection-title d${d}`} onClick={pick}>
              {grip}{card.title}
              {card.hidden && !testMode && <> {hiddenChip(cardTarget(sec.id, cardId))}</>}
              {!testMode && rowMenu(`card:${sec.id}:${cardId}`, (
                <>
                  <button onClick={() => dupCard(sec.id, cardId)}>Duplicate card</button>
                  {/* Under 900 only: a card is carried by dragging it, and a
                      finger cannot. A desktop menu that grew two items would be
                      a desktop change this phase is not allowed to make. */}
                  {narrow && <button disabled={!canMoveCard(sec.id, cardId, 'up')}
                    onClick={() => moveCardBy(sec.id, cardId, 'up')}>Move up</button>}
                  {narrow && <button disabled={!canMoveCard(sec.id, cardId, 'down')}
                    onClick={() => moveCardBy(sec.id, cardId, 'down')}>Move down</button>}
                  <div className="rule" />
                  <button className="danger" title="Its fields stay on the form and move up a level"
                    onClick={() => deleteFromMenu({ t: 'card', sec: sec.id, id: cardId }, `the card ${card.title || 'Card'}`)}>Delete card</button>
                </>
              ), true)}
            </div>
          )
          : !testMode && (
            <div className="fb-card-tab" onClick={pick}>
              {grip}Card{card.hidden && <> {hiddenChip(cardTarget(sec.id, cardId))}</>}
              {rowMenu(`card:${sec.id}:${cardId}`, (
                <>
                  <button onClick={() => dupCard(sec.id, cardId)}>Duplicate card</button>
                  {/* Under 900 only: a card is carried by dragging it, and a
                      finger cannot. A desktop menu that grew two items would be
                      a desktop change this phase is not allowed to make. */}
                  {narrow && <button disabled={!canMoveCard(sec.id, cardId, 'up')}
                    onClick={() => moveCardBy(sec.id, cardId, 'up')}>Move up</button>}
                  {narrow && <button disabled={!canMoveCard(sec.id, cardId, 'down')}
                    onClick={() => moveCardBy(sec.id, cardId, 'down')}>Move down</button>}
                  <div className="rule" />
                  <button className="danger" title="Its fields stay on the form and move up a level"
                    onClick={() => deleteFromMenu({ t: 'card', sec: sec.id, id: cardId }, `the card ${card.title || 'Card'}`)}>Delete card</button>
                </>
              ), true)}
            </div>
          )}
        <div className={`of-subcard d${d} fb-card-box`} data-card={cardId} style={box} onClick={pick}
          onDragOver={overCont(inner)} onDrop={dropCont(inner)}>
          {/* An empty card is the one thing the canvas shows that the form
              will not: the renderer drops a card with nothing in it rather
              than drawing a heading over a blank box. Say so, rather than
              letting the two quietly disagree. */}
          {renderGroup(sec, inner, depth + 1, oneCol, {
            emptyNote: 'Drag fields here — an empty card is left off the form',
            pad: 14,
          })}
        </div>
      </div>
    );
  }

  /** A card's box exactly as the published form styles it. */
  function cardStyleOf(card: BSection['cards'][number]): React.CSSProperties {
    return boxStyleFor(toStyleBody(card.style), { padding: 20 });
  }

  function renderZone(idx: number) {
    const d = drag.current;
    // A container that is already a root has nowhere to be promoted to, so the
    // zones stay shut rather than opening on a drop they would refuse.
    const secDrag = SECTION_DRAGS.includes(dragKind)
      && !(d?.kind === 'wnode' && findNode(widgets, d.scope)?.widget.tree.scope === d.scope);
    const active = hint && hint.t === 'z' && hint.idx === idx;
    return (
      <div className="fb-zone" onDragOver={overZone(idx)} onDrop={dropZone(idx)}
        style={{
          height: secDrag ? 30 : 10,
          border: secDrag ? (active ? '2px solid var(--accent)' : '2px dashed var(--accent-ring)') : '2px dashed transparent',
          background: active ? 'var(--accent-soft)' : secDrag ? 'var(--accent-wash)' : 'transparent',
          borderRadius: 8, transition: 'height .12s',
        }} />
    );
  }

  /**
   * One repeatable widget container, drawn where it lives — on the canvas,
   * with its fields and cards inside it and its stack of nested containers
   * below them, in the same boxes the published form draws. Design mode draws
   * ONE structural instance per container; the add/remove pills are inert here
   * (they work for real in Test mode and on the published form).
   *
   * The whole box is a drop target for another container, which is how a stack
   * is built: drag a Widget from the palette (or an existing one from the
   * canvas) onto the box you want it to repeat inside.
   */
  function renderWNode(sec: BSection, node: WidgetNode, depth: number): React.ReactNode {
    const isSel = sel?.t === 'wnode' && sel.scope === node.scope;
    const nestHint = hint && hint.t === 'w' && hint.scope === node.scope;
    const nesting = NEST_DRAGS.includes(dragKind);
    const box: React.CSSProperties = {
      ...boxStyleFor(node.style, { padding: instancePadding(depth) }),
      position: 'relative', cursor: testMode ? 'default' : 'pointer',
    };
    if (isSel) { box.boxShadow = SEL_RING; }
    if (nestHint) { box.outline = '2px dashed var(--accent)'; box.outlineOffset = 3; box.background = 'var(--accent-wash)'; }

    const oneCol = depth >= 3;
    const limits = `${node.min || 0}–${node.max || '∞'}`;
    const inertPill = (e: React.MouseEvent) => { e.stopPropagation(); };
    const pillTitle = `Live in Test mode and on the published form (${limits} per ${depth === 0 ? 'form' : 'parent'})`;
    const body = cont(sec.id, node.scope, '');
    const canHoldMore = depth + 2 <= MAX_WIDGET_DEPTH;
    // The same instance-card classes the renderer uses, so a widget on the
    // canvas is the widget respondents get.
    const cardClass = depth === 0 ? 'of-location-card' : depth === 1 ? 'of-device-card' : 'of-line-card';

    return (
      <div
        key={node.scope}
        className={`${cardClass} fb-wnode`}
        data-scope={node.scope}
        style={box}
        onClick={testMode ? undefined : (e) => { e.stopPropagation(); select({ t: 'wnode', sec: sec.id, scope: node.scope }); }}
        onDragOver={overNest(node.scope)}
        onDrop={dropNest(sec.id, node.scope)}
      >
        <div className="of-card-head">
          {!testMode && (
            <span className="fb-sec-grip fb-wnode-grip" draggable
              onDragStart={startDrag({ kind: 'wnode', sec: sec.id, scope: node.scope })} onDragEnd={endDrag}
              title={depth === 0 ? 'Drag into another widget to stack them' : 'Drag to re-nest, or out to the page to give it its own section'}>⠿</span>
          )}
          <span className={depth === 0 ? 'of-card-title' : 'of-device-title'}
            style={depth === 0 ? { fontSize: 18, color: '#2d3748' } : { marginBottom: 0 }}>
            {node.itemLabel} 1
          </span>
          <span className="fb-more-chip" title={pillTitle}>repeats {limits}</span>
          {node.hidden && !testMode && hiddenChip(widgetTarget(node.scope))}
          <span style={{ flex: 1 }} />
          <span className="of-remove fb-inert" onClick={inertPill} title={pillTitle}>Remove {node.itemLabel}</span>
        </div>

        {renderGroup(sec, body, 0, oneCol, {
          emptyNote: `Drag per-${node.itemLabel.toLowerCase()} fields here`,
          pad: depth >= 2 ? 12 : 14,
          // A container stacked in this one renders where its marker sits, so
          // it can be dropped between fields rather than only after them.
          nested: (marker) => {
            const child = (node.children || []).find((c) => c.scope === marker.nodeScope);
            if (!child) return null;
            const hintPos = hint && hint.t === 'f' && hint.key === marker.key ? hint.pos : null;
            const edge = hintPos ? DROP_EDGE[hintPos] : null;
            return (
              <div key={child.scope}
                style={{ marginTop: depth === 0 ? 16 : 12, borderRadius: 8, boxShadow: edge || 'none' }}
                onDragOver={overField(marker.key)} onDrop={dropField(marker.key)}>
                {renderWNode(sec, child, depth + 1)}
                <span className="of-add fb-inert" onClick={inertPill} title={pillTitle}>{child.addLabel}</span>
              </div>
            );
          },
        })}

        {/* The invitation stands while something nestable is in the air, and
            otherwise only on a container with nothing in it yet — once a stack
            exists, the boxes speak for themselves. */}
        {!testMode && canHoldMore && (nesting || !node.children?.length) && (
          <div className="fb-nest-drop" style={{ opacity: nesting ? 1 : 0.65 }}>
            Drop a <b>Widget</b> here to repeat it inside every {node.itemLabel.toLowerCase()}
            {depth > 0 && <> — or near this box's top or bottom edge to sit beside it</>}
          </div>
        )}
      </div>
    );
  }

  /** A widget section: its container tree, plus the add button under the root. */
  function renderWidgetSection(sec: BSection) {
    const w = widgetFor(sec);
    if (!w?.tree) {
      return <div className="fb-empty-drop">This section points at a widget that no longer exists — delete it and drop a fresh Widget on the canvas.</div>;
    }
    const body = sec.style ? toStyleBody(sec.style) : undefined;
    const run = (
      <>
        {renderWNode(sec, w.tree, 0)}
        <span className="of-add fb-inert" onClick={(e) => e.stopPropagation()}
          title="Live in Test mode and on the published form">{w.tree.addLabel}</span>
      </>
    );
    return (
      <>
        {/* The body card a coloured widget section gets on the published form
            — the builder draws the same box, and nothing extra without one. */}
        {hasBackground(body)
          ? <div className="of-section-card" style={sectionBodyStyle(body)}>{run}</div>
          : run}
        <div className="fb-note">
          Widgets stack inside widgets — each add button repeats just its own container and everything in it.
          Add/remove buttons come alive in <b>Test mode</b> and on the published form.
        </div>
      </>
    );
  }

  function renderSection(sec: BSection, i: number) {
    const isSel = sel?.t === 'sec' && sel.id === sec.id;
    const cfg = lensCfg;
    let hidden = false;
    if (lens.id) {
      if (sec.showFlag && !cfg[sec.showFlag]) hidden = true;
      if (sec.hideWith && cfg[sec.hideWith]) hidden = true;
      if ((sec.type === 'locations' || sec.type === 'widget') && lens.flow === 'simple') hidden = true;
      if (sec.type === 'totals' && lens.flow === 'complex') hidden = true;
    }
    // The section box the published form draws, plus the canvas's own chrome.
    const wrapStyle: React.CSSProperties = {
      ...sectionWrapStyle(toStyleBody(sec.style)),
      cursor: testMode ? 'default' : 'pointer',
      position: 'relative',
    };
    if (i === sections.length - 1 || (curPage && i === curPage.end - 1)) wrapStyle.borderBottom = 'none';
    if (hidden) { if (testMode) wrapStyle.display = 'none'; else wrapStyle.opacity = 0.35; }
    if (isSel) { wrapStyle.outline = '2px solid var(--accent)'; wrapStyle.outlineOffset = 6; wrapStyle.borderRadius = wrapStyle.borderRadius || 4; }
    const notes: string[] = [];
    if (sec.showFlag) notes.push('shows with ' + sec.showFlag);
    if (sec.hideWith) notes.push('hidden by ' + sec.hideWith);
    const showGroups = sec.type === 'fields' || sec.type === 'totals' || sec.type === 'files';

    return (
      <React.Fragment key={sec.id}>
        <div className="of-section" data-sec={sec.id} style={wrapStyle} onClick={testMode ? undefined : (e) => { e.stopPropagation(); select({ t: 'sec', id: sec.id }); }}>
          <div className="fb-sec-head">
            {!testMode && <span className="fb-sec-grip" draggable onDragStart={startDrag({ kind: 'section', id: sec.id })} onDragEnd={endDrag} title="Drag to reorder sections">⠿</span>}
            <h2 className="of-section-title">{sec.title}</h2>
            {sec.type !== 'fields' && <span className="fb-sec-chip" style={{ background: 'var(--neutral-bg)', color: 'var(--neutral-fg)' }}>Repeatable widget</span>}
            {notes.length > 0 && <span className="fb-gate-chip">{notes.join(' · ')}</span>}
            {sec.hidden && !testMode && hiddenChip(sectionTarget(sec.id))}
            {/* "+ Field here" (R4): the picker, aimed at this section's own body. */}
            {!testMode && showGroups && (
              <button type="button" className="fb-field-here"
                onClick={(e) => { e.stopPropagation(); select({ t: 'sec', id: sec.id }); openPicker({ cont: sectionBody(sec), afterKey: null, label: `the section ${sec.title || sec.id}` }); }}>
                + Field here
              </button>
            )}
            {!testMode && rowMenu(`sec:${sec.id}`, (
              <>
                <button disabled={!!sec.widgetId} title={sec.widgetId ? 'A repeating widget is not duplicated here' : undefined}
                  onClick={() => dupSection(sec.id)}>Duplicate section</button>
                {/* Under 900 only, for the same reason as the card's pair:
                    the ⠿ grip is the desktop's way and it still is. */}
                {narrow && <button disabled={!canMoveSection(sec.id, 'up')} onClick={() => moveSectionBy(sec.id, 'up')}>Move up</button>}
                {narrow && <button disabled={!canMoveSection(sec.id, 'down')} onClick={() => moveSectionBy(sec.id, 'down')}>Move down</button>}
                <div className="rule" />
                <button className="danger" title={sec.widgetId ? 'Takes the repeating widget and everything in it' : 'Takes every field and card in it'}
                  onClick={() => deleteFromMenu({ t: 'sec', id: sec.id }, `the section ${sec.title || ''}`.trim())}>Delete section</button>
              </>
            ), true)}
          </div>
          {sec.desc ? <RichBlocks className="of-section-desc" text={sec.desc} /> : null}
          {showGroups && (
            <div className="of-section-card" style={sectionBodyStyle(toStyleBody(sec.style))}>
              {renderGroup(sec, cont(sec.id, 'order', ''), 0, false)}
            </div>
          )}
          {sec.type === 'files' && (
            <div style={{ border: '2px dashed #cbd5e0', borderRadius: 8, padding: 22, textAlign: 'center', color: '#718096', fontSize: 14, background: '#fafbfc', marginTop: 4 }}>
              Choose files… <span style={{ color: '#a0aec0', fontSize: 12 }}>(built-in uploader, up to 10 files)</span>
            </div>
          )}
          {sec.type === 'totals' && (
            <div style={{ color: '#718096', fontSize: 12, fontStyle: 'italic', marginTop: 2 }}>Simple flow only — complex orders compute totals from the location tree.</div>
          )}
          {(sec.type === 'locations' || sec.type === 'widget') && renderWidgetSection(sec)}
        </div>
        {renderZone(i + 1)}
      </React.Fragment>
    );
  }

  // =================================================================================
  // Properties panel
  // =================================================================================
  function panelInput(label: string, value: string, onChange: (v: string) => void, o: {
    mono?: boolean; ro?: boolean; hint?: string; placeholder?: string;
    /** run when the field loses focus or Enter is pressed */
    commit?: () => void;
    /** shown in red instead of the hint */
    problem?: string | null;
  } = {}) {
    return (
      <div className="fb-field" key={label}>
        <label>{label}</label>
        <input
          className={`fb-input ${o.mono ? 'mono' : ''} ${o.ro ? 'ro' : ''}`}
          value={value} readOnly={o.ro} placeholder={o.placeholder || ''}
          style={o.problem ? { borderColor: '#fc8181' } : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={o.commit}
          onKeyDown={o.commit ? (e) => { if (e.key === 'Enter') { e.preventDefault(); o.commit!(); } } : undefined}
        />
        {o.problem
          ? <div className="fb-hint" style={{ color: '#c53030', fontWeight: 600 }}>{o.problem}</div>
          : o.hint && <div className="fb-hint">{o.hint}</div>}
      </div>
    );
  }

  /** What a 'payment' field charges. The server resolves the real amount at submit. */
  function renderPaymentBlock(f: BField) {
    const p = f.payment || emptyPayment();
    const patch = (x: Partial<typeof p>) => setField(f.key, { payment: { ...p, ...x } });
    const others = fields.filter((x) => !x.slot && x.key !== f.key && x.type === 'payment');
    return (
      <>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Payment</div>
        {others.length > 0 && (
          <div className="fb-hint" style={{ color: '#c53030', marginBottom: 8 }}>
            Only one payment field is honored per form — remove "{others[0].label}" or this one.
          </div>
        )}
        <div className="fb-field">
          <label>Amount</label>
          <select className="fb-input" value={p.amountMode} onChange={(e) => patch({ amountMode: e.target.value as 'fixed' | 'field' })}>
            <option value="fixed">A fixed price</option>
            <option value="field">Taken from another field (e.g. a calculated total)</option>
          </select>
        </div>
        {p.amountMode === 'fixed' ? (
          <div className="fb-field">
            <label>Price</label>
            <input className="fb-input" inputMode="decimal" placeholder="e.g. 25.00" value={p.amountFixed}
              onChange={(e) => patch({ amountFixed: e.target.value })} />
          </div>
        ) : (
          <div className="fb-field">
            <label>Amount comes from</label>
            <FieldPicker
              className="fb-input" value={p.amountField} placeholder="Choose a number field…"
              groups={builderPicker((d) => d.scope === 'order' && d.input_type === 'number', 'order')}
              onChange={(amountField) => patch({ amountField })}
            />
            <div className="fb-hint">Point it at a number field — often a calculation target from the Logic page (quantity × price). An empty or zero amount skips the payment.</div>
          </div>
        )}
        <div className="fb-field">
          <label>Currency</label>
          <select className="fb-input" value={p.currency} onChange={(e) => patch({ currency: e.target.value })}>
            {['usd', 'eur', 'gbp', 'cad', 'aud'].map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
        {panelInput('What the charge is for', p.description, (v) => patch({ description: v }), { placeholder: 'Shown on the Stripe payment page.' })}
        <div className="fb-hint">
          Respondents pay on a secure Stripe page right after submitting. Money goes to the Stripe account connected under{' '}
          <Link to="/admin/settings/payments">Settings → Payments</Link>.
        </div>
      </>
    );
  }

  /**
   * One end of a date field's window. A bound is either a fixed day or one
   * measured from the day the form is filled in — "no earlier than today" has
   * to keep meaning today, not the day the form was built.
   */
  function renderDateBound(f: BField, which: 'dateMin' | 'dateMax', label: string) {
    const v = f.validation || emptyValidation();
    const cur = readDateBound(v[which]);
    const set = (kind: typeof cur.kind, days: string, date: string) =>
      setField(f.key, { validation: { ...v, [which]: writeDateBound(kind, days, date) } });
    return (
      <div className="fb-field">
        <label>{label}</label>
        <select className="fb-input" value={cur.kind}
          onChange={(e) => {
            const kind = e.target.value as typeof cur.kind;
            // Switching to a day count starts at 0 rather than at nothing, so
            // the rule is real the moment it is chosen.
            set(kind, kind === 'offset' ? (cur.days || '0') : '', cur.date);
          }}>
          <option value="">No limit</option>
          <option value="today">Today</option>
          <option value="offset">A number of days from today</option>
          <option value="fixed">A fixed date</option>
        </select>
        {cur.kind === 'offset' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <input className="fb-input" style={{ width: 90 }} inputMode="numeric" value={cur.days}
              onChange={(e) => set('offset', e.target.value.replace(/[^0-9-]/g, ''), '')} />
            <span style={{ fontSize: 11.5, color: '#718096' }}>
              days from today — a minus sign counts backwards
            </span>
          </div>
        )}
        {cur.kind === 'fixed' && (
          <input className="fb-input" type="date" style={{ marginTop: 6 }} value={cur.date}
            onChange={(e) => set('fixed', '', e.target.value)} />
        )}
      </div>
    );
  }

  /** Constraint editors for the types that have any (text-like, number, date). */
  function renderValidationBlock(f: BField) {
    const textLike = ['text', 'textarea', 'email', 'tel', 'url'].includes(f.type);
    if (f.type === 'date' || f.type === 'datetime') {
      const v = f.validation || emptyValidation();
      const both = readDateBound(v.dateMin).kind && readDateBound(v.dateMax).kind;
      return (
        <>
          <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Dates allowed</div>
          {renderDateBound(f, 'dateMin', 'Earliest')}
          {renderDateBound(f, 'dateMax', 'Latest')}
          <div className="fb-hint" style={{ marginBottom: 8 }}>
            The date picker only offers days inside the window, and a typed date outside it is
            refused on submit. {both ? 'Both ends apply together.' : 'Leave an end at "No limit" to leave it open.'}
          </div>
          <div className="fb-field">
            <label>Message when the date is outside it</label>
            <input className="fb-input" value={v.message} placeholder="e.g. Pick an install date at least a week out"
              onChange={(e) => setField(f.key, { validation: { ...v, message: e.target.value } })} />
          </div>
        </>
      );
    }
    if (!textLike && f.type !== 'number') return null;
    const v = f.validation || emptyValidation();
    const patch = (p: Partial<typeof v>) => setField(f.key, { validation: { ...v, ...p } });
    const pair = (label1: string, k1: keyof typeof v, label2: string, k2: keyof typeof v) => (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="fb-field">
          <label>{label1}</label>
          <input className="fb-input" inputMode="numeric" value={v[k1]} placeholder="—" onChange={(e) => patch({ [k1]: e.target.value } as Partial<typeof v>)} />
        </div>
        <div className="fb-field">
          <label>{label2}</label>
          <input className="fb-input" inputMode="numeric" value={v[k2]} placeholder="—" onChange={(e) => patch({ [k2]: e.target.value } as Partial<typeof v>)} />
        </div>
      </div>
    );
    return (
      <>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Answer rules</div>
        {textLike
          ? pair('Min characters', 'minLen', 'Max characters', 'maxLen')
          : pair('Lowest allowed', 'min', 'Highest allowed', 'max')}
        {textLike && (
          <div className="fb-field">
            <label>Must match pattern</label>
            <input className="fb-input mono" value={v.pattern} placeholder="e.g. [A-Z]{2}-\d{4}" onChange={(e) => patch({ pattern: e.target.value })} />
            <div className="fb-hint">A regular expression the whole answer has to match. Leave blank for none.</div>
          </div>
        )}
        <div className="fb-field">
          <label>Message when the answer breaks a rule</label>
          <input className="fb-input" value={v.message} placeholder="e.g. Use your work order number, like AB-1234" onChange={(e) => patch({ message: e.target.value })} />
        </div>
      </>
    );
  }

  /** The visibility, rules and delete tail every presentation panel ends with. */
  function renderBlockTail(f: BField, noun: string) {
    const frules = rulesForField(rules, f.key);
    return (
      <>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Who sees it</div>
        {VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${f.vis === v ? 'on' : ''}`}
            onClick={() => setField(f.key, { vis: v as BField['vis'] })}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          A show / hide rule on the <Link to={`${base}/logic`}>Logic</Link> page can take the {noun} away
          with the questions around it.
        </div>
        {frules.length > 0 && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Rules touching it</div>
            {frules.map((r) => {
              const chip = ruleTriggerChip(r);
              return (
                <div key={r.id} className="fb-panel-rule" onClick={(e) => { e.stopPropagation(); setHlRule(hlRule === r.id ? null : r.id); }}>
                  <span className="fb-kind-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: '#4a5568', lineHeight: 1.45, marginTop: 3 }}>{ruleText(r, lbl, fields, pageLbl)}</span>
                  <Link to={`${base}/logic?rule=${encodeURIComponent(r.id)}`} onClick={(e) => e.stopPropagation()}
                    style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, marginTop: 4 }}>Open this rule →</Link>
                </div>
              );
            })}
          </>
        )}
        <div style={{ borderTop: '1px solid #edf2f7', marginTop: 14, paddingTop: 12 }}>
          <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
            {confirm ? `Confirm — removes the ${noun}` : `Delete ${noun}`}
          </button>
        </div>
      </>
    );
  }

  /**
   * A content block's settings (U5): a heading, a paragraph or a picture.
   * Like the line, it publishes as a field and asks nothing, so it gets its
   * own short panel: the words or the picture, then who sees it.
   */
  function renderBlockPanel(f: BField) {
    const richHint = 'Use **bold**, *italic*, a [link](https://…) and blank lines for paragraphs.';
    if (f.type === 'heading') {
      const h = f.heading || emptyHeading();
      return (
        <>
          <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            A title between questions. It asks nothing and never appears in submissions or the table.
          </div>
          {panelInput('Heading text', f.label, (v) => setField(f.key, { label: v }))}
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Size</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {([['1', 'Large'], ['2', 'Medium'], ['3', 'Small']] as const).map(([v, name]) => (
              <button key={v} className={`fb-seg ${h.level === v ? 'on' : ''}`}
                onClick={(e) => { e.stopPropagation(); setField(f.key, { heading: { level: v } }); }}>{name}</button>
            ))}
          </div>
          {renderBlockTail(f, 'heading')}
        </>
      );
    }
    if (f.type === 'paragraph') {
      const p = f.paragraph || emptyParagraph();
      return (
        <>
          <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            Words between questions — an explanation, a warning, a link to the terms. It asks nothing.
          </div>
          <div className="fb-field">
            <label htmlFor={`fb-para-${f.key}`}>Text</label>
            <textarea id={`fb-para-${f.key}`} className="fb-input" rows={7} value={p.text}
              placeholder="What the respondent should read here."
              onChange={(e) => setField(f.key, { paragraph: { text: e.target.value } })} />
            <div className="fb-hint">{richHint}</div>
          </div>
          {renderBlockTail(f, 'paragraph')}
        </>
      );
    }
    const im = f.image || emptyImage();
    const patch = (x: Partial<typeof im>) => setField(f.key, { image: { ...im, ...x } });
    return (
      <>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          A picture on the form — a map, a diagram, a product. It asks nothing. Uploads count toward the
          account's file storage.
        </div>
        <label className="fb-seg" style={{ display: 'inline-block', cursor: 'pointer', marginBottom: 10 }}>
          {uploadingAsset === f.key ? 'Uploading…' : 'Upload image…'}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }}
            disabled={uploadingAsset === f.key}
            aria-label="Upload an image"
            onChange={(e) => void pickAsset(f.key, e)} />
        </label>
        {panelInput('Or paste an image URL', im.url, (v) => patch({ url: v }), { placeholder: 'https://…', hint: 'PNG, JPEG, GIF or WebP, up to 5 MB when uploaded.' })}
        {panelInput('Description (alt text)', im.alt, (v) => patch({ alt: v }), { hint: 'Read out by screen readers; shown if the picture cannot load.' })}
        {panelInput('Width in pixels', im.width, (v) => patch({ width: v.replace(/[^0-9]/g, '') }), { placeholder: 'Blank = natural size, never wider than the form' })}
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Align</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {(['left', 'center', 'right'] as const).map((v) => (
            <button key={v} className={`fb-seg ${im.align === v ? 'on' : ''}`}
              onClick={(e) => { e.stopPropagation(); patch({ align: v }); }}>{v[0].toUpperCase() + v.slice(1)}</button>
          ))}
        </div>
        {renderBlockTail(f, 'image')}
      </>
    );
  }

  /**
   * A horizontal line's settings. It publishes as a field because that is
   * where position lives, but it asks nothing — so it gets its own panel
   * rather than a question panel with most of it greyed out.
   */
  function renderDividerPanel(f: BField) {
    const d = f.divider || emptyDivider();
    const patch = (x: Partial<typeof d>) => setField(f.key, { divider: { ...d, ...x } });
    const frules = rulesForField(rules, f.key);
    return (
      <>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          A line across the form. It asks nothing, so it never appears in submissions, rules or the
          submissions table — it is there to break a long stretch of questions into parts.
        </div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Look</div>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Thickness</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {['1', '2', '4'].map((t) => (
            <button key={t} className={`fb-seg ${(d.thickness || '1') === t ? 'on' : ''}`}
              onClick={(e) => { e.stopPropagation(); patch({ thickness: t }); }}>{t}px</button>
          ))}
        </div>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Colour</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input type="color" value={d.color || '#e2e8f0'} onChange={(e) => patch({ color: e.target.value })}
            style={{ width: 44, height: 30, padding: 2, border: '2px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
          <button className={`fb-seg ${d.color ? '' : 'on'}`} onClick={(e) => { e.stopPropagation(); patch({ color: '' }); }}>
            Match the form
          </button>
        </div>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Space around it</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {[['8', 'Tight'], ['20', 'Normal'], ['40', 'Roomy']].map(([v, name]) => (
            <button key={v} className={`fb-seg ${(d.space || '20') === v ? 'on' : ''}`}
              onClick={(e) => { e.stopPropagation(); patch({ space: v }); }}>{name}</button>
          ))}
        </div>
        <div className="fb-hint" style={{ marginBottom: 10 }}>Applied above and below the line.</div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Who sees it</div>
        {VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${f.vis === v ? 'on' : ''}`}
            onClick={() => setField(f.key, { vis: v as BField['vis'] })}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          A show / hide rule on the <Link to={`${base}/logic`}>Logic</Link> page can take the line away
          with the questions around it.
        </div>
        {frules.length > 0 && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Rules touching it</div>
            {frules.map((r) => {
              const chip = ruleTriggerChip(r);
              return (
                <div key={r.id} className="fb-panel-rule" onClick={(e) => { e.stopPropagation(); setHlRule(hlRule === r.id ? null : r.id); }}>
                  <span className="fb-kind-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: '#4a5568', lineHeight: 1.45, marginTop: 3 }}>{ruleText(r, lbl, fields, pageLbl)}</span>
                  <Link to={`${base}/logic?rule=${encodeURIComponent(r.id)}`} onClick={(e) => e.stopPropagation()}
                    style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, marginTop: 4 }}>Open this rule →</Link>
                </div>
              );
            })}
          </>
        )}
        <div style={{ borderTop: '1px solid #edf2f7', marginTop: 14, paddingTop: 12 }}>
          <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
            {confirm ? 'Confirm — removes the line' : 'Delete line'}
          </button>
        </div>
      </>
    );
  }

  /**
   * What the field starts from. A starting point, not a lock: the respondent
   * can change it, and a prefill rule on the Logic page still wins over it.
   * Every new widget instance starts from it too.
   */
  function renderDefaultBlock(f: BField) {
    if (!takesDefault(f.type)) return null;
    const set = (v: BField['defaultValue']) => setField(f.key, { defaultValue: v });
    const str = typeof f.defaultValue === 'string' ? f.defaultValue : '';
    const picked = Array.isArray(f.defaultValue) ? f.defaultValue : [];
    const opts = f.options.filter((o) => o.value.trim() || o.text.trim());
    const head = <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Starts with</div>;

    if (f.type === 'checkbox') {
      return (
        <>
          {head}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            {[['', 'Unticked'], ['on', 'Ticked']].map(([v, name]) => (
              <button key={name} className={`fb-seg ${(f.defaultValue === true) === (v === 'on') ? 'on' : ''}`}
                onClick={(e) => { e.stopPropagation(); set(v === 'on'); }}>{name}</button>
            ))}
          </div>
        </>
      );
    }

    if (f.type === 'multiselect') {
      return (
        <>
          {head}
          {opts.length === 0
            ? <div className="fb-hint">Add some choices above first.</div>
            : opts.map((o, i) => {
              const val = o.value || o.text;
              const on = picked.includes(val);
              return (
                <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: '#2d3748', marginBottom: 5 }}>
                  <input type="checkbox" checked={on}
                    onChange={() => set(on ? picked.filter((x) => x !== val) : [...picked, val])} />
                  {o.text || o.value}
                </label>
              );
            })}
          {picked.length > 0 && (
            <button className="fb-seg" style={{ marginTop: 4 }} onClick={(e) => { e.stopPropagation(); set([]); }}>
              Start with none
            </button>
          )}
        </>
      );
    }

    if (f.type === 'select' || f.type === 'radio') {
      // A built-in list (states, countries) has no options here to choose
      // from, so the default is typed — it has to match a value on the list.
      if (f.type === 'select' && f.optionsPreset) {
        return (
          <>
            {head}
            {panelInput('Starts with', str, (v) => set(v), {
              placeholder: 'e.g. United States',
              hint: 'Type it exactly as it appears on the built-in list, or leave blank to start unselected.',
            })}
          </>
        );
      }
      return (
        <>
          {head}
          <div className="fb-field">
            <select className="fb-input" value={str} onChange={(e) => set(e.target.value)}>
              <option value="">Nothing selected</option>
              {opts.map((o, i) => <option key={i} value={o.value || o.text}>{o.text || o.value}</option>)}
            </select>
          </div>
        </>
      );
    }

    if (f.type === 'scale') {
      const sc = f.scale || emptyScale();
      const min = sc.min === '0' ? 0 : 1;
      const max = Math.max(min + 1, Math.min(10, parseInt(sc.max || '', 10) || 5));
      return (
        <>
          {head}
          <div className="fb-field">
            <select className="fb-input" value={str} onChange={(e) => set(e.target.value)}>
              <option value="">Nothing picked</option>
              {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
                <option key={n} value={String(n)}>{n}</option>
              ))}
            </select>
          </div>
        </>
      );
    }

    if (f.type === 'rating') {
      const top = Math.max(1, Math.min(10, parseInt(f.ratingMax || '', 10) || 5));
      return (
        <>
          {head}
          <div className="fb-field">
            <select className="fb-input" value={str} onChange={(e) => set(e.target.value)}>
              <option value="">No stars</option>
              {Array.from({ length: top }, (_, i) => i + 1).map((n) => (
                <option key={n} value={String(n)}>{n} star{n === 1 ? '' : 's'}</option>
              ))}
            </select>
          </div>
        </>
      );
    }

    if (f.type === 'date') {
      const today = str === 'today';
      return (
        <>
          {head}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            {[['', 'Blank'], ['today', "Today's date"], ['fixed', 'A fixed date']].map(([v, name]) => {
              const on = v === '' ? !str : v === 'today' ? today : !!str && !today;
              return (
                <button key={name} className={`fb-seg ${on ? 'on' : ''}`}
                  onClick={(e) => { e.stopPropagation(); set(v === 'fixed' ? (today ? '' : str) : v); }}>{name}</button>
              );
            })}
          </div>
          {!!str && !today && (
            <input className="fb-input" type="date" value={str} onChange={(e) => set(e.target.value)} />
          )}
          {!str && (
            <div className="fb-hint">Pick "A fixed date" to choose one, or "Today's date" to fill in whatever day the form is opened.</div>
          )}
        </>
      );
    }

    const inputType = f.type === 'time' ? 'time' : f.type === 'number' ? 'number' : 'text';
    return (
      <>
        {head}
        <div className="fb-field">
          <input className="fb-input" type={inputType} value={str} placeholder="Leave blank to start empty"
            onChange={(e) => set(e.target.value)} />
          <div className="fb-hint">The respondent can change it — it is a starting point, not a locked answer.</div>
        </div>
      </>
    );
  }

  /** Which of the Field tab's groups are open (R4); the first three start open. */
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(['basics', 'options', 'type']));
  const groupOpen = (id: string) => openGroups.has(id);
  const toggleGroup = (id: string, open: boolean) => setOpenGroups((s) => {
    const n = new Set(s);
    if (open) n.add(id); else n.delete(id);
    return n;
  });

  /**
   * The Field tab (R4): every setting today's panel has, grouped into
   * collapsible sections so the common ones sit at the top — Basics, Options,
   * Type settings, Visibility and rules, Default value, Validation, Upload
   * limits, Repeats inside. A desktop renders the groups as <details>; a phone
   * keeps the M5 sheet's two tabs, Question (what the field IS) and Rules
   * (what the form does with it), with the same pieces dealt between them.
   */
  function renderFieldPanel(f: BField) {
    if (f.type === 'divider') return renderDividerPanel(f);
    if (PRESENTATION.has(f.type)) return renderBlockPanel(f);
    /**
     * Is this question scored? A question that already carries points says so
     * itself, whichever visit to the panel this is; the checkbox only has to
     * remember the one it was just ticked on.
     */
    const scoringOn = SCORE_TYPES.includes(f.type as BInputType) && !f.optionsPreset
      && (scoreOpen === f.key || f.options.some((o) => typeof o.score === 'number' || o.exempt));
    const facts: string[] = [];
    facts.push(f.scope === 'order' ? 'Asked once per submission' : `Repeats for every ${f.scope}`);
    if (f.optionsPreset === 'us_states') facts.push('State list is built in' + (f.countryField ? ` (follows "${lbl(f.countryField)}")` : ''));
    if (f.optionsPreset === 'countries') facts.push('Country list is built in');
    const frules = rulesForField(rules, f.key);
    const widgetScopes = widgets.flatMap((w) => flattenWidgetTree(w.tree)).map((n) => ({ scope: n.scope, itemLabel: n.itemLabel }));
    const hasPlaceholder = !['checkbox', 'file', 'radio', 'multiselect', 'rating', 'payment', 'name', 'address', 'signature', 'scale', 'matrix', 'ranking', 'datetime'].includes(f.type);

    // ---- 1. Basics: label, key, placeholder, help, required ----
    const basics = (
      <>
        {panelInput('Question shown on the form', f.label, (v) => setFieldLabel(f.key, v))}
        {(() => {
          const typed = keyDraft?.of === f.key ? keyDraft.text : f.key;
          const problem = typed === f.key ? null : keyProblem(typed, fields, f.key, widgets);
          return panelInput('Key', typed, (v) => setKeyDraft({ of: f.key, text: v }), {
            mono: true,
            commit: () => commitKey(f),
            problem,
            hint: 'Used in submitted data and integration mappings. Rules, calculations and the document’s placements follow a rename.',
          });
        })()}
        {hasPlaceholder && panelInput('Placeholder', f.placeholder, (v) => setField(f.key, { placeholder: v }))}
        {panelInput('Helper text', f.infoText, (v) => setField(f.key, { infoText: v }), { placeholder: 'One short sentence.' })}
        {f.type !== 'payment' && (
          <div className="fb-required-row">
            <Toggle
              checked={f.req === 'whenShown'} label="Required"
              onChange={(on) => setField(f.key, { req: on ? 'whenShown' : 'never' })}
            />
            <div className="fb-hint">
              {f.req === 'whenShown'
                ? 'Must be answered when it is on screen; a rule that hides it also waives it.'
                : 'Can be left blank.'}
              {' '}Where the ⚡ badge appears, a rule on the <Link to={`${base}/logic`}>Logic</Link> page can make it required — or waive it — for as long as its condition holds.
            </div>
          </div>
        )}
      </>
    );

    // ---- 2. Options: the editor, paste-a-list, preset and country source, images, points, n/a, Other ----
    const options = OPTION_TYPES.includes(f.type as BInputType) ? (
      <>
        {f.type === 'select' && (
        <div className="fb-field">
          <label>Use a built-in list</label>
          <select className="fb-input" value={f.optionsPreset} onChange={(e) => setField(f.key, { optionsPreset: e.target.value as BField['optionsPreset'] })}>
            <option value="">No — type the values below</option>
            <option value="us_states">States / provinces</option>
            <option value="countries">Countries</option>
          </select>
        </div>
        )}
        {f.optionsPreset === 'us_states' && (
          <div className="fb-field">
            <label>Country comes from</label>
            <FieldPicker
              className="fb-input" value={f.countryField} placeholder="US states only"
              groups={builderPicker((d) => d.input_type === 'select' && d.key !== f.key)}
              onChange={(countryField) => setField(f.key, { countryField })}
            />
          </div>
        )}
        {!f.optionsPreset && f.options.map((o, i) => (
          <div key={i}>
          {/* The value/label row keeps its own template at every width.
              The panel is about 300px wide, so points squeezed in beside
              them left "Option 1" reading "Opti" — they get their own
              strip underneath instead, and this row is untouched. */}
          <div style={{ display: 'grid', gridTemplateColumns: IMAGE_OPTION_TYPES.includes(f.type as BInputType) ? '1fr 1.2fr 34px 26px' : '1fr 1.2fr 26px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
            <input className="fb-input mono" style={{ padding: '7px 9px' }} value={o.value} placeholder="value"
              onPaste={(e) => pasteIntoRow(e, f, i)}
              onChange={(e) => setField(f.key, { options: f.options.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }, { coalesce: `opt:${f.key}:${i}:value` })} />
            <input className="fb-input" style={{ padding: '7px 9px', fontSize: 12.5 }} value={o.text} placeholder="label"
              onPaste={(e) => pasteIntoRow(e, f, i)}
              onChange={(e) => setField(f.key, { options: f.options.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }, { coalesce: `opt:${f.key}:${i}:text` })} />
            {IMAGE_OPTION_TYPES.includes(f.type as BInputType) && (o.image
              ? (
                <button type="button" className="fc-x" style={{ height: 26, padding: 0, overflow: 'hidden' }}
                  title="Remove the picture" aria-label={`Remove the picture on ${o.text || o.value || `option ${i + 1}`}`}
                  onClick={(e) => { e.stopPropagation(); setField(f.key, { options: f.options.map((x, j) => (j === i ? { value: x.value, text: x.text } : x)) }); }}>
                  <img src={o.image} alt="" style={{ width: 34, height: 26, objectFit: 'cover', display: 'block' }} />
                </button>
              ) : (
                <label className="fc-act" style={{ height: 26, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                  title="Add a picture to this choice">
                  {uploadingOpt === `${f.key}:${i}` ? '…' : '🖼'}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }}
                    disabled={uploadingOpt === `${f.key}:${i}`}
                    aria-label={`Add a picture to ${o.text || o.value || `option ${i + 1}`}`}
                    onChange={(e) => void pickOptionImage(f.key, i, e)} />
                </label>
              ))}
            <button className="fc-x" style={{ height: 26 }} onClick={(e) => { e.stopPropagation(); setField(f.key, { options: f.options.filter((_, j) => j !== i) }); }}>×</button>
          </div>
          {scoringOn && (
            <div className="fb-score-row">
              <span className="lbl">Points</span>
              <input className="fb-input" type="number" inputMode="numeric"
                value={typeof o.score === 'number' ? String(o.score) : ''} placeholder="0" disabled={!!o.exempt}
                aria-label={`Points for ${o.text || o.value || `option ${i + 1}`}`}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  const score = e.target.value.trim() === '' || !Number.isFinite(n) ? undefined : n;
                  setField(f.key, { options: f.options.map((x, j) => (j === i ? { ...x, score } : x)) }, { coalesce: `opt:${f.key}:${i}:score` });
                }} />
              <label className="fb-score-na" title="This answer takes the whole question out of the scorecard">
                <input type="checkbox" checked={!!o.exempt}
                  aria-label={`${o.text || o.value || `option ${i + 1}`} takes the question out of the scorecard`}
                  onChange={(e) => setField(f.key, {
                    options: f.options.map((x, j) => (j === i
                      ? (e.target.checked ? { ...x, exempt: true, score: undefined } : { ...x, exempt: undefined })
                      : x)),
                  })} />
                <span>not scored (n/a)</span>
              </label>
            </div>
          )}
          </div>
        ))}
        {IMAGE_OPTION_TYPES.includes(f.type as BInputType) && !f.optionsPreset && (
          <div className="fb-hint" style={{ marginBottom: 6 }}>
            Give a choice a picture and the whole question shows as tiles. PNG, JPEG, GIF or WebP, up to 5 MB.
          </div>
        )}
        {!f.optionsPreset && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button className="fc-act" onClick={() => setField(f.key, { options: [...f.options, { value: '', text: '' } as FieldOption] })}>+ Add value</button>
            <button className="fc-act" aria-expanded={pasteFor === f.key}
              onClick={() => { setPasteFor(pasteFor === f.key ? null : f.key); setPasteText(''); setPasteReplace(false); }}>
              Paste a list
            </button>
          </div>
        )}
        {!f.optionsPreset && pasteFor === f.key && (() => {
          const parsed = parseOptionList(pasteText);
          return (
            <div className="fb-field fb-paste" style={{ marginBottom: 8 }}>
              <textarea className="fb-input" rows={5} value={pasteText} autoFocus
                placeholder={'One option per line\nor  value | label'} aria-label="Options to add, one per line"
                onChange={(e) => setPasteText(e.target.value)} />
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', margin: '6px 0' }}>
                <input type="checkbox" checked={pasteReplace} onChange={(e) => setPasteReplace(e.target.checked)} />
                Replace the existing options
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="fc-act" disabled={!parsed.length}
                  onClick={() => { setField(f.key, { options: mergeOptions(f.options, parsed, pasteReplace) }); setPasteFor(null); setPasteText(''); }}>
                  Add {parsed.length} {parsed.length === 1 ? 'option' : 'options'}
                </button>
                <button className="fc-act" onClick={() => { setPasteFor(null); setPasteText(''); }}>Cancel</button>
              </div>
              <div className="fb-hint">Blank lines are skipped and a value already in the list is kept once. Pasting a list straight into an empty row does the same.</div>
            </div>
          );
        })()}
        {!f.optionsPreset && SCORE_TYPES.includes(f.type as BInputType) && (
          <div className="fb-field" style={{ marginTop: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
              <input type="checkbox" checked={scoringOn}
                onChange={(e) => {
                  if (e.target.checked) { setScoreOpen(f.key); return; }
                  // Turning scoring off has to TAKE the points: a number
                  // left behind in a box nobody can see would still count.
                  setScoreOpen(null);
                  setField(f.key, {
                    options: f.options.map((x) => ({ value: x.value, text: x.text, ...(x.image ? { image: x.image } : {}) })),
                  });
                }}
              /> Score this question
            </label>
            {scoringOn && (
              <div className="fb-hint">
                Points per answer — blank earns nothing, which is what “No” is. Tick “n/a” on the answer that
                takes the question out of the scorecard, so it offers no points either. Add them up with a
                “scorecard total” on the Logic page.
              </div>
            )}
          </div>
        )}
        {!f.optionsPreset && f.type !== 'ranking' && (
          <div className="fb-field" style={{ marginTop: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
              <input type="checkbox" checked={!!f.allowOther}
                onChange={(e) => setField(f.key, { allowOther: e.target.checked })}
              /> Offer “Other” with a write-in
            </label>
            {f.allowOther && (
              <input className="fb-input" value={f.otherLabel || ''} placeholder="Other"
                aria-label="Label for the Other choice"
                onChange={(e) => setField(f.key, { otherLabel: e.target.value })}
              />
            )}
            <div className="fb-hint">
              A last choice that opens a text box. What they type is the answer — it reaches exports and integrations as text.
            </div>
          </div>
        )}
      </>
    ) : null;
    const optionsTitle = f.type === 'select' ? 'Dropdown values' : f.type === 'ranking' ? 'Items to put in order' : 'Choices';

    // ---- 3. Type settings ----
    const typePieces: React.ReactNode[] = [];
    if (f.type === 'tel') typePieces.push(
      <div className="fb-field" key="tel">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
          <input type="checkbox" checked={!!f.phoneUs}
            onChange={(e) => setField(f.key, { phoneUs: e.target.checked })}
          /> Format as a US phone number
        </label>
        <div className="fb-hint">
          Shows (555) 555-5555 as they type, stores the ten digits, and refuses anything shorter.
        </div>
      </div>
    );
    if (f.type === 'rating') typePieces.push(
      <div className="fb-field" key="rating">
        <label>Scale goes up to</label>
        <select className="fb-input" value={String(Math.max(1, Math.min(10, parseInt(f.ratingMax || '', 10) || 5)))}
          onChange={(e) => setField(f.key, { ratingMax: e.target.value })}>
          {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n} stars</option>)}
        </select>
      </div>
    );
    if (f.type === 'scale') typePieces.push((() => {
      const sc = f.scale || emptyScale();
      const patch = (p: Partial<typeof sc>) => setField(f.key, { scale: { ...sc, ...p } });
      const min = sc.min === '0' ? 0 : 1;
      return (
        <React.Fragment key="scale">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div className="fb-field">
              <label>Starts at</label>
              <select className="fb-input" value={sc.min} onChange={(e) => patch({ min: e.target.value === '0' ? '0' : '1' })}>
                <option value="1">1</option>
                <option value="0">0 (an NPS)</option>
              </select>
            </div>
            <div className="fb-field">
              <label>Ends at</label>
              <select className="fb-input" value={String(Math.max(min + 1, Math.min(10, parseInt(sc.max || '', 10) || 5)))}
                onChange={(e) => patch({ max: e.target.value })}>
                {Array.from({ length: 10 - min }, (_, i) => min + 1 + i).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          {panelInput('Word at the low end', sc.minLabel, (v) => patch({ minLabel: v }), { placeholder: 'e.g. Not at all likely' })}
          {panelInput('Word at the high end', sc.maxLabel, (v) => patch({ maxLabel: v }), { placeholder: 'e.g. Extremely likely' })}
          <div className="fb-hint" style={{ marginBottom: 8 }}>
            A 0–10 scale is an NPS: the Summary tab works out the score from the answers.
          </div>
        </React.Fragment>
      );
    })());
    if (f.type === 'matrix') typePieces.push((() => {
      const mx = f.matrix || emptyMatrix();
      const patch = (p: Partial<typeof mx>, opts?: CommitOptions) => setField(f.key, { matrix: { ...mx, ...p } }, opts);
      const list = (which: 'rows' | 'columns', title: string, noun: string) => (
        <>
          <div className="fb-overline" style={{ margin: '14px 0 8px' }}>{title}</div>
          {mx[which].map((o, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 26px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input className="fb-input mono" style={{ padding: '7px 9px' }} value={o.value} placeholder="value"
                aria-label={`${noun} ${i + 1} value`}
                onChange={(e) => patch({ [which]: mx[which].map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) }, { coalesce: `mx:${f.key}:${which}:${i}:value` })} />
              <input className="fb-input" style={{ padding: '7px 9px', fontSize: 12.5 }} value={o.text} placeholder="label"
                aria-label={`${noun} ${i + 1} label`}
                onChange={(e) => patch({ [which]: mx[which].map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }, { coalesce: `mx:${f.key}:${which}:${i}:text` })} />
              <button className="fc-x" style={{ height: 26 }} aria-label={`Remove ${noun.toLowerCase()} ${i + 1}`}
                onClick={(e) => { e.stopPropagation(); patch({ [which]: mx[which].filter((_, j) => j !== i) }); }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <button className="fc-act" onClick={() => {
              const n = mx[which].length + 1;
              const stem = which === 'rows' ? 'row' : 'col';
              patch({ [which]: [...mx[which], { value: `${stem}${n}`, text: `${noun} ${n}` }] });
            }}>+ Add {noun.toLowerCase()}</button>
          </div>
        </>
      );
      return (
        <React.Fragment key="matrix">
          {list('rows', 'Rows — the questions down the side', 'Row')}
          {list('columns', 'Columns — the answers across the top', 'Column')}
          <div className="fb-field" style={{ marginTop: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
              <input type="checkbox" checked={!!mx.multi} onChange={(e) => patch({ multi: e.target.checked })} />
              Allow more than one answer per row
            </label>
            <div className="fb-hint">
              Each row becomes its own column in the submissions table and exports. When required, every row needs an answer.
            </div>
          </div>
        </React.Fragment>
      );
    })());
    if (f.type === 'signature') typePieces.push(
      <React.Fragment key="signature">
        <div className="fb-field">
          <label>How they can sign</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
            <input
              type="checkbox" checked={f.signature?.draw !== false}
              onChange={(e) => setField(f.key, { signature: { ...(f.signature || emptySignature()), draw: e.target.checked } })}
            /> Draw — finger, mouse or stylus
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, color: 'var(--ink-800)', marginBottom: 5 }}>
            <input
              type="checkbox" checked={!!f.signature?.type}
              onChange={(e) => setField(f.key, { signature: { ...(f.signature || emptySignature()), type: e.target.checked } })}
            /> Type a name — rendered in a script face
          </label>
          <div className="fb-hint">
            Both produce the same image, so the signature looks the same wherever it is
            stamped. Whichever way they sign, the form records when, from what address, and
            the name behind a typed one. Untick both and it falls back to drawing.
          </div>
        </div>
        <div className="fb-field">
          <label>Agreement text (optional)</label>
          <textarea
            className="fb-input" rows={2} value={f.signature?.consentText || ''}
            placeholder="By signing below you agree to the terms above."
            onChange={(e) => setField(f.key, { signature: { ...(f.signature || emptySignature()), consentText: e.target.value } })}
          />
          <div className="fb-hint">
            Shown above the pad, and recorded WITH the signature exactly as it read on the
            day — so a later edit to this wording cannot change what somebody agreed to.
          </div>
        </div>
        <div className="fb-field">
          <label>Terms they agree to (optional)</label>
          <textarea
            className="fb-input" rows={8} value={f.signature?.terms || ''}
            placeholder="By signing, the customer agrees to purchase the items listed above under the terms at ..."
            onChange={(e) => setField(f.key, { signature: { ...(f.signature || emptySignature()), terms: e.target.value } })}
          />
          <div className="fb-hint">
            The contract language this signature agrees to. Shown above the pad, and recorded WITH the signature exactly as it read for that person, answers filled in, so a later edit here cannot change what anyone already agreed to. Tokens like <code>{'{{companyName}}'}</code> are
            filled from the answers. The <Link to="../document">Document</Link> tab can print it beside
            the signature.
          </div>
        </div>
        <div className="fb-field">
          <label>Pad height (px)</label>
          <input
            className="fb-input" type="number" min={80} max={400} placeholder="160"
            value={f.signature?.height || ''}
            onChange={(e) => setField(f.key, { signature: { ...(f.signature || emptySignature()), height: e.target.value } })}
          />
        </div>
      </React.Fragment>
    );
    if (f.type === 'address') typePieces.push(
      <React.Fragment key="address">
        <div className="fb-field">
          <label>Address type</label>
          <select className="fb-input" value={f.addressKind || ''} onChange={(e) => setField(f.key, { addressKind: e.target.value as BField['addressKind'] })}>
            <option value="">Standard address</option>
            <option value="shipping">Shipping address — adds Company/Name, Attention, Phone</option>
          </select>
          <div className="fb-hint">
            A shipping address collects who the delivery is for: Company / Name (required when the
            field is), plus optional Attention and Phone Number lines.
          </div>
        </div>
        <div className="fb-field">
          <label>Default country</label>
          <select className="fb-input" value={f.addressDefaultCountry || ''} onChange={(e) => setField(f.key, { addressDefaultCountry: e.target.value })}>
            <option value="">None — the respondent picks one</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="fb-hint">
            Pre-selects the country on the form. The sub-fields and their names follow the selected
            country automatically — ZIP Code vs Postcode, State vs Province vs County, and so on.
          </div>
        </div>
      </React.Fragment>
    );
    if (f.type === 'payment') typePieces.push(<React.Fragment key="payment">{renderPaymentBlock(f)}</React.Fragment>);
    const typeSettings = typePieces.length ? <>{typePieces}</> : null;

    // ---- 4. Visibility and rules ----
    const visibility = (
      <>
        <div className="fb-overline" style={{ margin: '0 0 8px' }}>Who sees it</div>
        {VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${f.vis === v ? 'on' : ''}`}
            onClick={() => setField(f.key, { vis: v as BField['vis'] })}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          To reveal or hide this field based on another answer, add a show / hide rule on the <Link to={`${base}/logic`}>Logic</Link> page.
        </div>
        {f.type !== 'payment' && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Locked?</div>
            {LOCK_OPTS.map(([v, title, desc]) => (
              <div key={v} className={`fb-radio ${f.lock === v ? 'on' : ''}`}
                onClick={() => setField(f.key, { lock: v as BField['lock'] })}>
                <b>{title}</b><small>{desc}</small>
              </div>
            ))}
            {f.lock === 'locked' && !takesDefault(f.type) && (
              <div className="fb-hint" style={{ marginTop: 6 }}>
                This type has no starting answer to show, so locked means it stays empty unless a
                prefill or a calculation on the <Link to={`${base}/logic`}>Logic</Link> page fills it.
              </div>
            )}
            {f.lock === 'locked' && takesDefault(f.type) && !String(f.defaultValue ?? '') && (
              <div className="fb-hint" style={{ marginTop: 6 }}>
                Nothing starts in it, so respondents will see an empty box they cannot type in. Give it
                a starting answer below, or fill it from the <Link to={`${base}/logic`}>Logic</Link> page.
              </div>
            )}
          </>
        )}
        {frules.length > 0 && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Rules touching this field</div>
            {frules.map((r) => {
              const chip = ruleTriggerChip(r);
              const on = hlRule === r.id;
              return (
                <div key={r.id} className={`fb-panel-rule ${on ? 'on' : ''}`} onClick={(e) => { e.stopPropagation(); setHlRule(on ? null : r.id); }}>
                  <span className="fb-kind-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-700)', lineHeight: 1.45, marginTop: 3 }}>{ruleText(r, lbl, fields, pageLbl)}</span>
                  <Link to={`${base}/logic?rule=${encodeURIComponent(r.id)}`} onClick={(e) => e.stopPropagation()}
                    style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 600, marginTop: 4 }}>Open this rule →</Link>
                </div>
              );
            })}
            <div style={{ color: 'var(--ink-500)', fontSize: 11, margin: '2px 0 6px' }}>Click a rule to highlight every field it involves.</div>
          </>
        )}
        <div style={{ marginTop: frules.length ? 4 : 14 }}>
          <Link to={`${base}/logic?subject=${encodeURIComponent(f.key)}`} className="fs-btn"
            style={{ display: 'inline-block', fontSize: 12, textDecoration: 'none' }}>
            + Add a rule in Logic →
          </Link>
          <div style={{ color: 'var(--ink-500)', fontSize: 11, marginTop: 4 }}>
            Opens the Logic page with a new rule already watching this field.
          </div>
        </div>
      </>
    );

    // ---- 5, 6, 7 ----
    const defaults = renderDefaultBlock(f);
    const validation = renderValidationBlock(f);
    const uploads = f.type === 'file' ? (
      <>
        <UploadLimitsFields
          variant="fb" idPrefix={`fb-up-${f.key}`}
          value={f.upload || emptyUpload()}
          onChange={(upload) => setField(f.key, { upload })}
          inherited={resolveUploadLimits(undefined, form.settings?.uploads)}
          inheritLabel="Same as the form's setting"
        />
        <div className="fb-hint" style={{ marginBottom: 12 }}>
          Blank means the form-wide limits from Settings apply. Set one here to tighten it for this field only.
        </div>
      </>
    ) : null;

    // ---- 8. Repeats inside, and what was wired up ----
    const repeats = (
      <>
        <div className="fb-field">
          <label>Repeats inside</label>
          <select className="fb-input" value={f.scope} onChange={(e) => setField(f.key, { scope: e.target.value })}>
            <option value="order">Nothing — asked once per submission</option>
            {widgetScopes.map((w) => <option key={w.scope} value={w.scope}>Every {w.itemLabel} ({w.scope})</option>)}
          </select>
          <div className="fb-hint">Or drag the field into a widget on the canvas.</div>
        </div>
        {facts.length > 0 && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Wired up for you</div>
            {facts.map((fa, i) => <div key={i} className="fb-fact">{fa}</div>)}
          </>
        )}
      </>
    );

    const del = (
      <div className="fb-del-row">
        <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
          {confirm ? 'Confirm — removes it from the form' : 'Delete field'}
        </button>
        <div style={{ color: 'var(--ink-500)', fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>
          Existing submissions keep the answers they already captured.
        </div>
      </div>
    );

    // A phone gets the M5 sheet's two tabs, with the same pieces dealt between
    // them: Question is what the field IS, Rules is what the form does with it.
    if (phone) {
      return panelTab === 'question'
        ? <>{basics}{options && <><div className="fb-overline" style={{ margin: '14px 0 8px' }}>{optionsTitle}</div>{options}</>}{typeSettings}{defaults}{uploads}</>
        : <>{validation}{repeats}{visibility}{del}</>;
    }

    const groups: { id: string; title: string; body: React.ReactNode }[] = [
      { id: 'basics', title: 'Basics', body: basics },
      ...(options ? [{ id: 'options', title: optionsTitle, body: options }] : []),
      ...(typeSettings ? [{ id: 'type', title: 'Type settings', body: typeSettings }] : []),
      { id: 'visibility', title: 'Visibility and rules', body: visibility },
      ...(defaults ? [{ id: 'default', title: 'Default value', body: defaults }] : []),
      ...(validation ? [{ id: 'validation', title: 'Validation', body: validation }] : []),
      ...(uploads ? [{ id: 'uploads', title: 'Upload limits', body: uploads }] : []),
      { id: 'repeats', title: 'Repeats inside a widget', body: repeats },
    ];
    return (
      <>
        {groups.map((g) => (
          <details key={g.id} className="fb-group" open={groupOpen(g.id)}
            onToggle={(e) => toggleGroup(g.id, (e.currentTarget as HTMLDetailsElement).open)}>
            <summary className="fb-group-head">{g.title}</summary>
            <div className="fb-group-body">{g.body}</div>
          </details>
        ))}
        {del}
      </>
    );
  }

  /** Preset backgrounds offered for sections and cards alike. */
  const BGS: { c: string; name: string }[] = [
    { c: 'transparent', name: 'None' },
    { c: '#ffffff', name: 'White' },
    { c: '#f7fafc', name: 'Grey' },
    { c: '#ebf4ff', name: 'Blue tint' },
    { c: '#e6fffa', name: 'Teal tint' },
    { c: '#fffaf0', name: 'Warm tint' },
    { c: '#faf5ff', name: 'Purple tint' },
    { c: '#fff5f5', name: 'Rose tint' },
  ];

  /**
   * The styling controls shared by sections, cards and widget containers:
   * preset swatches, a free colour picker, an optional second stop for a
   * gradient, radius, width and min height.
   *
   * The stock tints are joined by the ACCOUNT'S OWN COLOURS when it has a
   * palette. A section's background is not an inherited key — the brand has
   * nothing to say about one, and a section that sets none stays transparent —
   * but the colours a company is allowed to be are exactly as relevant here as
   * they are for the form's accent, and a builder who had to fetch a hex out
   * of Settings to tint one card would rightly wonder what the palette was
   * for. Offered, never imposed: nothing about an existing form changes, and
   * an account with no palette sees the row it has always seen.
   */
  function renderStyleEditor(style: BStyle, patch: (p: Partial<BStyle>) => void, opts: { width?: boolean } = {}) {
    // Through `backgroundOf`, so the swatch under the controls is the value a
    // respondent's box is painted with and not a second spelling of it.
    const preview = backgroundOf(toStyleBody(style))
      || (style.bg && style.bg !== 'transparent' ? style.bg
        : 'repeating-linear-gradient(45deg, #fff, #fff 5px, #edf2f7 5px, #edf2f7 10px)');
    const swatch = (c: string, name: string, active: boolean, onPick: () => void) => (
      <span key={c + name} title={name} onClick={(e) => { e.stopPropagation(); onPick(); }}
        style={{
          width: 26, height: 26, borderRadius: 6, cursor: 'pointer', display: 'inline-block',
          border: active ? '2px solid #667eea' : '2px solid #e2e8f0',
          background: c === 'transparent' ? 'repeating-linear-gradient(45deg, #fff, #fff 4px, #edf2f7 4px, #edf2f7 8px)' : c,
        }} />
    );

    /**
     * The account's named colours as a second row — for the background, and
     * for a gradient's second stop, because "fade Graphite into Marine" is the
     * gradient a branded form actually wants.
     *
     * Renders nothing without a palette, which is what keeps every existing
     * form's panel exactly as it was.
     */
    const brandRow = (pick: (hex: string) => void, isActive: (hex: string) => boolean) => {
      const palette = brand?.palette || [];
      if (!palette.length) return null;
      return (
        <div className="fb-brandrow">
          <span className="fb-brandrow-label">Your brand</span>
          {palette.map((sw) => swatch(
            sw.hex,
            sw.group ? `${sw.name} — ${sw.group}` : sw.name,
            isActive(sw.hex),
            () => pick(sw.hex)
          ))}
        </div>
      );
    };
    return (
      <>
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Background</label>
        <div style={{ display: 'flex', gap: 7, marginBottom: 8, flexWrap: 'wrap' }}>
          {BGS.map((b) => swatch(b.c, b.name, style.bg === b.c, () => patch({ bg: b.c, ...(b.c === 'transparent' ? { bg2: '' } : {}) })))}
          <label title="Pick any colour" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <input type="color" value={style.bg && style.bg !== 'transparent' ? style.bg : '#ffffff'}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => patch({ bg: e.target.value })}
              style={{ width: 26, height: 26, padding: 0, border: '2px solid #e2e8f0', borderRadius: 6, background: 'none', cursor: 'pointer' }} />
          </label>
        </div>
        {brandRow(
          (hex) => patch({ bg: hex }),
          (hex) => (style.bg || '').toLowerCase() === hex.toLowerCase()
        )}
        {style.bg && style.bg !== 'transparent' && (
          <>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#4a5568', marginBottom: 6 }}>
              <input type="checkbox" checked={!!style.bg2}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => patch({ bg2: e.target.checked ? (style.bg2 || '#ffffff') : '' })} />
              Fade into a second colour (gradient)
            </label>
            {style.bg2 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                <input type="color" value={style.bg2}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => patch({ bg2: e.target.value })}
                  style={{ width: 26, height: 26, padding: 0, border: '2px solid #e2e8f0', borderRadius: 6, background: 'none', cursor: 'pointer' }} />
                {GRADIENT_DIRS.map((g) => (
                  <button key={g} className={`fb-seg ${(style.gradient || 'vertical') === g ? 'on' : ''}`}
                    onClick={(e) => { e.stopPropagation(); patch({ gradient: g }); }}>
                    {GRADIENT_LABEL[g]}
                  </button>
                ))}
              </div>
            )}
            {!!style.bg2 && brandRow(
              (hex) => patch({ bg2: hex }),
              (hex) => (style.bg2 || '').toLowerCase() === hex.toLowerCase()
            )}
            <div style={{ height: 34, borderRadius: style.radius || 4, border: '1px solid #e2e8f0', background: preview, marginBottom: 12 }} />
          </>
        )}
        <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Corner radius</label>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {[0, 8, 12, 16].map((r) => (
            <button key={r} className={`fb-seg ${(style.radius || 0) === r ? 'on' : ''}`}
              onClick={(e) => { e.stopPropagation(); patch({ radius: r }); }}>{r}</button>
          ))}
        </div>
        {opts.width !== false && (
          <>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Width</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              {(['100%', '75%', '50%'] as const).map((w) => (
                <button key={w} className={`fb-seg ${style.width === w ? 'on' : ''}`}
                  onClick={(e) => { e.stopPropagation(); patch({ width: w }); }}>{w === '100%' ? 'Auto' : w}</button>
              ))}
            </div>
          </>
        )}
        <div className="fb-field">
          <label>Min height <small style={{ color: '#a0aec0', fontWeight: 400 }}>(px, blank = auto)</small></label>
          <input className="fb-input" value={style.height || ''} placeholder="auto"
            onChange={(e) => patch({ height: e.target.value.replace(/[^0-9]/g, '') })} />
        </div>
      </>
    );
  }

  function renderSectionPanel(s: BSection) {
    const nFields = fields.filter((f) => f.section === s.id && !isStructural(f)).length;
    const facts: string[] = [];
    if (s.type !== 'fields') {
      const w = widgetFor(s);
      const n = w?.tree ? nodesOf(w.tree).length : 0;
      facts.push(w
        ? `Holds ${n} repeatable container${n === 1 ? '' : 's'} — click one on the canvas to set what it repeats and how often.`
        : 'Points at a widget that no longer exists.');
    }
    return (
      <>
        {panelInput('Section title', s.title, (v) => setSection(s.id, { title: v }))}
        <div className="fb-field">
          <label htmlFor={`fb-secdesc-${s.id}`}>Description</label>
          <textarea id={`fb-secdesc-${s.id}`} className="fb-input" rows={3} value={s.desc || ''}
            placeholder="Shown under the title. Optional."
            onChange={(e) => setSection(s.id, { desc: e.target.value })} />
          <div className="fb-hint">Use **bold**, *italic* and a [link](https://…).</div>
        </div>
        {panelInput('Id', s.id, () => undefined, { mono: true, ro: true, hint: 'Fields target this section id.' })}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Pages</div>
        {sections[0]?.id !== s.id ? (
          <div className="fb-field">
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontWeight: 600, fontSize: 12, color: '#4a5568' }}>
              <input type="checkbox" checked={!!s.pageBreak} onChange={(e) => setSection(s.id, { pageBreak: e.target.checked || undefined })} />
              Start a new page at this section
            </label>
            <div className="fb-hint">Respondents get a progress bar and Back / Next buttons. A page whose fields are all hidden by rules is skipped automatically.</div>
          </div>
        ) : (
          <div className="fb-hint" style={{ marginBottom: 6 }}>The form's first page starts here. Mark a later section "start a new page" to split the form into steps.</div>
        )}
        {(s.pageBreak || sections[0]?.id === s.id) && (
          panelInput('Page title (shown in the progress bar)', s.pageTitle || '', (v) => setSection(s.id, { pageTitle: v || undefined }), { placeholder: 'e.g. Your details' })
        )}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Who sees it</div>
        {CONTAINER_VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${(s.hidden ? 'hidden' : 'shown') === v ? 'on' : ''}`}
            onClick={(e) => { e.stopPropagation(); setSection(s.id, { hidden: v === 'hidden' || undefined }); }}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          One <Link to={`${base}/logic`}>show rule</Link> pointed at this section is all a hidden one needs —
          it appears while that rule fires. Everything in it is waived while it is away.
        </div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Styling</div>
        {renderStyleEditor(s.style, (p) => setSecStyle(s.id, p))}
        {facts.length > 0 && (
          <>
            <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Wired up for you</div>
            {facts.map((fa, i) => <div key={i} className="fb-fact">{fa}</div>)}
          </>
        )}
        <div style={{ borderTop: '1px solid #edf2f7', marginTop: 14, paddingTop: 12 }}>
          <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
            {confirm ? `Confirm — deletes ${nFields} field${nFields === 1 ? '' : 's'} too` : 'Delete section'}
          </button>
          <div style={{ color: '#718096', fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>Its fields are removed from the catalog with it.</div>
        </div>
      </>
    );
  }

  /**
   * The second half of a gradient control: the box that turns one colour into
   * two, the well for the second, and which way the pair runs.
   *
   * The first half is always the control that was already there — the accent's
   * colour well, the page background's preset row — so this adds to each one
   * rather than replacing it, and a form that sets no second stop renders the
   * checkbox and nothing else.
   */
  function washControls(o: {
    /** '' = flat. */
    c2: string;
    dir: GradientDir;
    /** The direction an absent one means for THIS surface. */
    defaultDir: GradientDir;
    /** The colour the box puts back when it is ticked again. */
    memo: string;
    label: string;
    set: (c2: string, dir: GradientDir) => void;
  }) {
    return (
      <>
        <label className="fb-check">
          <input type="checkbox" checked={!!o.c2}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => o.set(e.target.checked ? (o.memo || '#764ba2') : '', o.dir)} />
          {o.label}
        </label>
        {!!o.c2 && (
          <div className="fb-wash-row">
            <input type="color" value={o.c2}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => o.set(e.target.value, o.dir)}
              style={{ width: 26, height: 26, padding: 0, border: '2px solid #e2e8f0', borderRadius: 6, background: 'none', cursor: 'pointer' }} />
            {GRADIENT_DIRS.map((g) => (
              <button key={g} className={`fb-seg ${o.dir === g ? 'on' : ''}`}
                onClick={(e) => { e.stopPropagation(); o.set(o.c2, g); }}>{GRADIENT_LABEL[g]}</button>
            ))}
          </div>
        )}
      </>
    );
  }

  /**
   * The page background as the controls hold it: one colour, or two and a
   * direction.
   *
   * It is stored as a CSS string — a form saved before there were pickers may
   * carry any gradient at all, and an inherited one carries the account's — so
   * it is read back through `parseGradient`, which answers null for anything
   * these controls did not write. `custom` is that null said out loud, rather
   * than the value being silently rounded to something drawable.
   */
  const pageBg = useMemo(() => {
    const raw = (shown.bg || '').trim();
    if (!raw) return { c1: '', c2: '', dir: 'diagonal' as GradientDir, custom: false };
    if (/^#[0-9a-f]{6}$/i.test(raw)) return { c1: raw.toLowerCase(), c2: '', dir: 'diagonal' as GradientDir, custom: false };
    const g = parseGradient(raw);
    if (g) return { ...g, custom: false };
    return { c1: '', c2: '', dir: 'diagonal' as GradientDir, custom: true };
  }, [shown.bg]);

  /** …and back out: one colour is flat, two are the gradient it writes. */
  const setPageBg = (c1: string, c2: string, dir: GradientDir) =>
    (c2 ? gradientCss(c1, c2, dir) : c1);

  /** Form-level presentation: header text, colors, page width. */
  function renderFormPanel() {
    const setTheme2 = (patch: Partial<FormThemeSettings>) => setTheme((t) => ({ ...t, ...patch }), typingKey('theme', patch));
    const WIDTHS: [number, string][] = [[720, 'Narrow'], [960, 'Medium'], [1200, 'Wide'], [1600, 'Full']];
    return (
      <>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          How the whole form looks to the person filling it in. Like everything else on the canvas,
          it changes for respondents only when you publish.
        </div>
        {/* Only for an account that HAS a brand — see brandGives. */}
        {brand && (
          <div className="fb-brandbar">
            <span>Using your account branding for anything this form does not set.</span>
            <Link to="/admin/settings/branding">Edit branding →</Link>
          </div>
        )}
        {panelInput('Header title', theme.headerTitle || '', (v) => setTheme2({ headerTitle: v }), { placeholder: form.name, hint: 'Blank uses the form name.' })}
        {panelInput('Description', theme.headerSubtitle || '', (v) => setTheme2({ headerSubtitle: v }), { placeholder: 'One line under the title.', hint: 'Shown under the title on the form.' })}

        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Colors</div>
        {panelLabel('Accent', 'accent')}
        {/* The account's named colours, when it has any: picking a colour the
            company HAS beats mixing one, and it is why the palette exists. A
            brand with no palette falls through to the colour well below,
            exactly as every form has always had. */}
        {!!brand?.palette?.length && (
          <div className="fb-swatches">
            {brand.palette.map((sw) => (
              <span key={sw.hex} title={sw.group ? `${sw.name} — ${sw.group}` : sw.name}
                className={`fb-swatch${(shown.accent || '').toLowerCase() === sw.hex.toLowerCase() ? ' on' : ''}`}
                onClick={(e) => { e.stopPropagation(); setTheme2({ accent: sw.hex }); }}
                style={{ background: sw.hex }} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <input type="color" value={shown.accent || '#667eea'} onChange={(e) => setTheme2({ accent: e.target.value })}
            style={{ width: 44, height: 30, padding: 2, border: '2px solid #e2e8f0', borderRadius: 6, background: '#fff' }} />
          <span style={{ color: '#718096', fontSize: 11.5 }}>
            {brand?.palette?.length ? 'Or mix one. ' : ''}Section titles, buttons, page background.
          </span>
        </div>
        {/* The accent pair. Ticking this writes the form's OWN accent as well
            as its second stop, because the two are one answer: a form cannot
            take the house colour and give it a different gradient without
            saying which colour it is gradienting. */}
        {washControls({
          c2: shown.accent2 || '', dir: shown.accentGradient || 'diagonal', defaultDir: 'diagonal',
          memo: accent2Memo,
          label: 'Fade the accent into a second colour',
          set: (c2, dir) => {
            if (c2) setAccent2Memo(c2);
            setTheme2({
              accent: shown.accent || '#667eea',
              accent2: c2 || undefined,
              accentGradient: c2 && dir !== 'diagonal' ? dir : undefined,
            });
          },
        })}
        <div className="fb-hint" style={{ marginBottom: 12 }}>
          The band behind the form and the submit button. Everything else takes the first colour.
        </div>

        {panelLabel('Page background', 'bg')}
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          {[['', 'Accent gradient'], ['#f7fafc', 'Light grey'], ['#ffffff', 'White'], ['#1a202c', 'Dark']].map(([c, name]) => (
            <span key={name} title={name} onClick={(e) => { e.stopPropagation(); setTheme2({ bg: c || undefined }); }}
              style={{
                width: 26, height: 26, borderRadius: 6, cursor: 'pointer', display: 'inline-block',
                // The ring follows what the canvas DRAWS, so an inherited
                // background is shown as the one in force rather than as none.
                border: (shown.bg || '') === c ? '2px solid #667eea' : '2px solid #e2e8f0',
                background: c || accentWash(shown),
              }} />
          ))}
          <label title="Pick any colour" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            <input type="color" value={pageBg.c1 || '#f7fafc'}
              onChange={(e) => setTheme2({ bg: setPageBg(e.target.value, pageBg.c2, pageBg.dir) })}
              style={{ width: 26, height: 26, padding: 0, border: '2px solid #e2e8f0', borderRadius: 6, background: 'none', cursor: 'pointer' }} />
          </label>
        </div>
        {/* The "glow behind the card" a light page already hints at with the
            card's shadow — now something a form can actually set. */}
        {washControls({
          c2: pageBg.c2, dir: pageBg.dir, defaultDir: 'diagonal', memo: bg2Memo,
          label: 'Fade into a second colour',
          set: (c2, dir) => {
            if (c2) setBg2Memo(c2);
            setTheme2({ bg: setPageBg(pageBg.c1 || '#f7fafc', c2, dir) });
          },
        })}
        <div className="fb-hint" style={{ marginBottom: 10 }}>
          The area around the form card.
          {pageBg.custom && ' This background is a custom CSS value — touching a colour above replaces it.'}
        </div>

        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Form width</div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
          {WIDTHS.map(([px, name]) => (
            <button key={px} className={`fb-seg ${(theme.maxWidth || 1600) === px ? 'on' : ''}`}
              onClick={(e) => { e.stopPropagation(); setTheme2({ maxWidth: px }); }}>{name}</button>
          ))}
        </div>
        <div className="fb-field">
          <label>Exact width <small style={{ color: '#a0aec0', fontWeight: 400 }}>(px)</small></label>
          <input className="fb-input" value={String(theme.maxWidth || '')} placeholder="1600"
            onChange={(e) => setTheme2({ maxWidth: Number(e.target.value.replace(/[^0-9]/g, '')) || undefined })} />
        </div>

        {renderBrandingPanel(setTheme2)}

        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Submit button</div>
        {panelInput('Button label', submitLabel, setSubmitLabel, { placeholder: 'Submit', hint: 'Blank reads “Submit”. Shown at the foot of the canvas.' })}
      </>
    );
  }

  /**
   * The plan-gated half of appearance: presets, logo, fonts, dark mode. On a
   * plan without customBranding the controls render disabled with an upsell
   * hint — and the server pins the branded fields regardless, so this is
   * presentation, not enforcement.
   */
  function renderBrandingPanel(setTheme2: (patch: Partial<FormThemeSettings>) => void) {
    const off = !features.customBranding;
    const dim = off ? { opacity: 0.5, pointerEvents: 'none' as const } : {};
    // Both of these change the row on the SERVER — the bytes and the ledger
    // move with them — so they answer with the whole row and the panel takes
    // it as given, rather than patching a copy that a failed upload would
    // leave lying about a file that is not there.
    const logos = themeLogos(theme);
    const free = LOGO_ALIGNS.filter((a) => !logos.some((l) => l.align === a));
    /** The row as the server just told it, with the single logo it replaced dropped. */
    const takeRow = (next: FormLogo[]) =>
      setTheme((t) => { const { logo: _gone, ...rest } = t; return { ...rest, logos: next }; });
    async function pickLogo(e: React.ChangeEvent<HTMLInputElement>) {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      try {
        const r = await uploadFormLogo(form.id, file);
        takeRow(r.logos);
        setError('');
        refreshForm().catch(() => undefined);
      } catch (err: any) { setError(err.message || 'Logo upload failed.'); }
    }
    async function removeLogo(url: string) {
      try {
        const r = await deleteFormLogo(form.id, url);
        takeRow(r.logos);
        refreshForm().catch(() => undefined);
      } catch (err: any) { setError(err.message || 'Logo removal failed.'); }
    }
    return (
      <>
        <div className="fb-overline" style={{ margin: '18px 0 8px' }}>
          Branding
          {off && <span className="lim-counter" style={{ marginLeft: 6 }}>paid plans</span>}
        </div>
        {off && (
          <div className="fb-hint" style={{ marginBottom: 10 }}>
            Logo, fonts, presets and dark mode need a plan with custom branding — accent and backgrounds above stay yours on every plan.
          </div>
        )}
        <div style={dim}>
          <label style={{ display: 'block', fontWeight: 600, fontSize: 12, color: '#4a5568', marginBottom: 5 }}>Theme presets</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {THEME_PRESETS.map((p) => (
              <span key={p.id} title={p.name}
                onClick={(e) => {
                  e.stopPropagation();
                  setTheme2({ accent: p.accent, bg: p.bg, mode: p.mode, font: p.font, presetId: p.id });
                }}
                style={{
                  width: 34, height: 26, borderRadius: 6, cursor: 'pointer', display: 'inline-block',
                  border: theme.presetId === p.id ? '2px solid #667eea' : '2px solid #e2e8f0',
                  background: p.bg,
                }} />
            ))}
          </div>

          {panelLabel(logos.length > 1 ? 'Logos' : 'Logo', 'logos')}
          {/* The logo row is the one control a form replaces WHOLE rather than
              key by key: the brand's row and the form's are alternatives, so
              an inheriting form shows the house row it is actually drawing,
              and uploading one here replaces all of it for this form. */}
          {brandGives('logos') && !formAnswers('logos') && (
            <>
              <div className="fb-brandlogos">
                {(brand!.logos || []).map((l) => (
                  <img key={l.url} src={l.url} alt="" title={`${l.align} — from the account branding`} />
                ))}
              </div>
              <div className="fb-hint" style={{ marginBottom: 8 }}>
                From your account branding. Uploading one here replaces the row on this form only.
                <button type="button" className="fb-linkbtn"
                  onClick={(e) => { e.stopPropagation(); setTheme((t) => { const n = { ...t }; delete n.logo; return { ...n, logos: [] }; }); }}>
                  Show no logo on this form
                </button>
              </div>
            </>
          )}
          {brandGives('logos') && formAnswers('logos') && !logos.length && (
            <div className="fb-hint" style={{ marginBottom: 8 }}>
              This form shows no logo, and does not take the account&rsquo;s.
            </div>
          )}
          {logos.map((l) => (
            <div key={l.url} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, marginBottom: 8 }}>
              <img src={l.url} alt="Logo" style={{ maxHeight: 48, maxWidth: '100%', display: 'block', marginBottom: 6 }} />
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                {LOGO_ALIGNS.map((a) => {
                  // Two logos cannot share a position — one would print on top
                  // of the other — so a taken one is shown taken rather than
                  // accepted and then quietly undone by the save.
                  const taken = logos.some((o) => o.url !== l.url && o.align === a);
                  return (
                    <button key={a} className={`fb-seg ${l.align === a ? 'on' : ''}`} disabled={taken}
                      title={taken ? `Another logo is already on the ${a}. Move that one first.` : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTheme2({ logos: logos.map((o) => (o.url === l.url ? { ...o, align: a } : o)) });
                      }}>{a}</button>
                  );
                })}
                <button className="fb-danger" onClick={(e) => { e.stopPropagation(); void removeLogo(l.url); }}>Remove</button>
              </div>
            </div>
          ))}
          {free.length > 0 && (
            <label className="fb-seg" style={{ display: 'inline-block', cursor: 'pointer', marginBottom: logos.length ? 4 : 12 }}>
              {logos.length ? 'Add another logo…' : 'Upload logo…'}
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" style={{ display: 'none' }} onChange={(e) => void pickLogo(e)} />
            </label>
          )}
          {logos.length > 0 && (
            <div className="fb-hint" style={{ marginBottom: 12 }}>
              {free.length
                ? 'Up to three, one to a side — so a left and a right logo share the top row.'
                : 'All three positions are taken. Remove one to add another.'}
            </div>
          )}

          {panelLabel('Fonts', brandGives('font.heading') ? 'font.heading' : 'font.body')}
          <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
            {/* The selects show the face the canvas DRAWS — an inherited
                heading reads as that heading, not as "System default", which
                is the whole difference between a panel that describes the
                form and one that describes only its overrides. Changing one
                writes this form's own answer; Revert on the mark takes it
                back. */}
            <select className="fb-input" value={shown.font?.heading || ''}
              onChange={(e) => setTheme2({ font: { ...theme.font, heading: e.target.value || undefined } })}>
              {BRAND_FONTS.map((f) => <option key={f.family} value={f.family}>{f.family ? `Headings: ${f.label}` : 'Headings: System default'}</option>)}
            </select>
            <select className="fb-input" value={shown.font?.body || ''}
              onChange={(e) => setTheme2({ font: { ...theme.font, body: e.target.value || undefined } })}>
              {BRAND_FONTS.map((f) => <option key={f.family} value={f.family}>{f.family ? `Body: ${f.label}` : 'Body: System default'}</option>)}
            </select>
          </div>

          {panelLabel('Color mode', 'mode')}
          <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            {([['light', 'Light'], ['dark', 'Dark'], ['auto', 'Match visitor']] as const).map(([v, name]) => (
              // Which one reads as ON follows the RESOLVED mode, so a form
              // inheriting the house dark mode does not show Light.
              <button key={v} className={`fb-seg ${(shown.mode || 'light') === v ? 'on' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Light has always been stored as NO key, because light is
                  // what a themeless form is. That stays true — except when
                  // the brand says dark, where storing nothing would leave
                  // the form dark and the click would do nothing at all. Then
                  // light is a real answer and is written as one.
                  setTheme2({ mode: v === 'light' && !brandGives('mode') ? undefined : v });
                }}>{name}</button>
            ))}
          </div>

          {/* ---- adoption (B5): the two ways a look moves ---- */}
          <div className="fb-adopt">
            {ownBranded.length > 0 && (
              <button type="button" className="fb-seg" disabled={adopting}
                onClick={(e) => { e.stopPropagation(); setPromoting(true); }}>
                Use this form&rsquo;s branding for the whole account…
              </button>
            )}
            {brand && ownBranded.length > 0 && (
              <button type="button" className="fb-seg" disabled={adopting}
                onClick={(e) => { e.stopPropagation(); setResetting(true); }}>
                Reset to account branding…
              </button>
            )}
            {ownBranded.length === 0 && brand && (
              <div className="fb-hint">
                This form takes everything from your account branding.
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  /**
   * A container's conditional counts: how many of its instances meet a
   * condition, published beside the automatic count. The condition rows are
   * the Logic page's own (RunWhenEditor), over the questions an instance can
   * see — its own, and those of every level above it.
   */
  function renderConditionalCounts(node: WidgetNode, parent: WidgetNode | null) {
    const scope = node.scope;
    const list = node.counts || [];
    const setCounts = (counts: WidgetCount[]) => setWNode(scope, { counts: counts.length ? counts : undefined });
    const patchCount = (i: number, x: Partial<WidgetCount>) => setCounts(list.map((c, j) => (j === i ? { ...c, ...x } : c)));
    const above = new Set<string>(['order', scope]);
    for (let s = scope; ;) {
      const up = allNodes(widgets).find((n) => (n.children || []).some((c) => c.scope === s));
      if (!up) break;
      above.add(up.scope);
      s = up.scope;
    }
    const readable = pickerDefs.filter((d) => above.has(d.scope));
    const many = `${node.itemLabel.trim().toLowerCase() || 'item'}s`;
    return (
      <>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Conditional counts</div>
        <div className="fb-hint" style={{ marginBottom: 8 }}>
          Count only the {many} that meet a condition. Each one is sent with every submission
          {parent ? `, per ${parent.itemLabel.trim().toLowerCase() || 'parent'} and for the whole form,` : ''} beside
          the count above, so a Monday.com column, an email or a webhook can map it. Rules on the Logic page cannot read one.
        </div>
        {list.map((c, i) => {
          const id = `${scope}:${i}`;
          const typed = condKeyDraft?.of === id ? condKeyDraft.text : c.key;
          const problem = typed === c.key ? null : conditionalCountKeyProblem(typed, scope, i, widgets, fields);
          return (
            <div key={i} className="fb-condcount">
              {panelInput('Count name', c.label, (v) => patchCount(i, { label: v }), {
                placeholder: `e.g. ${node.itemLabel.trim() || 'Item'}s to order`,
                commit: () => {
                  const key = conditionalCountKeyFor(c, c.label, widgets, fields);
                  if (key !== c.key) patchCount(i, { key });
                },
              })}
              {panelInput('Key', typed, (v) => setCondKeyDraft({ of: id, text: v }), {
                mono: true,
                commit: () => {
                  const next = (condKeyDraft?.of === id ? condKeyDraft.text : '').trim();
                  if (next && next !== c.key && !conditionalCountKeyProblem(next, scope, i, widgets, fields)) {
                    patchCount(i, { key: next });
                  }
                  setCondKeyDraft(null);
                },
                problem,
                hint: 'What an integration maps. Changing it later unmaps it there.',
              })}
              <RunWhenEditor
                value={c.when.length ? { match: c.match, when: c.when } : null}
                fields={readable} sections={pickerSections}
                label={`Count the ${many} where…`}
                emptyHint="No condition yet, so this counts nothing. Add one."
                onChange={(g) => patchCount(i, { match: g?.match === 'any' ? 'any' : 'all', when: g?.when || [] })}
              />
              <button type="button" className="fb-linkbtn" style={{ marginTop: 6, fontSize: 'var(--text-caption)' }}
                onClick={(e) => { e.stopPropagation(); setCondKeyDraft(null); setCounts(list.filter((_, j) => j !== i)); }}>
                Remove this count
              </button>
            </div>
          );
        })}
        <button type="button" className="fb-seg" style={{ marginBottom: 12 }}
          onClick={(e) => { e.stopPropagation(); setCounts([...list, newConditionalCount(widgets, fields)]); }}>
          + Add a conditional count
        </button>
      </>
    );
  }

  /**
   * A repeatable container's settings — what one instance is called, what the
   * add button says, how many are allowed, and how the cards lay out. These
   * used to live on a Widget Builder tab; they belong beside the thing they
   * describe, like every other element's settings.
   */
  function renderWidgetNodePanel(secId: string, scope: string) {
    const found = findNode(widgets, scope);
    if (!found) return null;
    const { widget, node } = found;
    const isRoot = widget.tree.scope === scope;
    const parent = isRoot ? null : allNodes(widgets).find((n) => (n.children || []).some((c) => c.scope === scope)) || null;
    const own = fields.filter((f) => f.scope === scope && !isStructural(f));
    const kids = nodesOf(node).length - 1;
    const patch = (x: Partial<WidgetNode>) => setWNode(scope, x);
    // The style editor works in BStyle (every key present); a node stores the
    // trimmed SectionStyle, so it is widened on the way in and trimmed again
    // on the way out — exactly the round trip a section and a card make.
    const nodeStyle: BStyle = { ...defaultStyle(), ...(node.style || {}) };
    const num = (v: string) => Number(v.replace(/[^0-9]/g, '')) || 0;
    const perRow = Math.max(1, Math.min(4, node.perRow || 1));
    const rows = node.max ? Math.ceil(node.max / perRow) : 0;

    return (
      <>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
          {isRoot
            ? 'A repeatable container: the person filling in the form clicks its add button and a fresh copy of everything inside appears.'
            : `Repeats inside every ${parent?.itemLabel || 'parent'} — adding one ${parent?.itemLabel.toLowerCase() || 'parent'} brings its own copies along.`}
        </div>
        {panelInput('One of these is called', node.itemLabel, (v) => patch({ itemLabel: v }), { hint: `Shown as "${node.itemLabel} 1", "${node.itemLabel} 2"…` })}
        {panelInput('Add button', node.addLabel, (v) => patch({ addLabel: v }))}
        {(() => {
          // Typed here, landed on commit — the same deal the field Key gets.
          // A count key shares the submission's namespace with every answer,
          // so an empty or already-taken one is left in the box to be fixed
          // rather than silently overwriting something on the way out.
          const typed = countDraft?.of === node.scope ? countDraft.text : node.countKey;
          const problem = typed === node.countKey ? null : countKeyProblem(typed, node.scope, widgets, fields);
          return panelInput('Count key', typed, (v) => setCountDraft({ of: node.scope, text: v }), {
            mono: true,
            commit: () => {
              const next = (countDraft?.of === node.scope ? countDraft.text : '').trim();
              if (next && next !== node.countKey && !countKeyProblem(next, node.scope, widgets, fields)) {
                patch({ countKey: next });
              }
              setCountDraft(null);
            },
            problem,
            hint: 'How many there are, for calculations on the Logic page.',
          });
        })()}
        {renderConditionalCounts(node, parent)}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {panelInput('Fewest', String(node.min ?? 1), (v) => patch({ min: num(v) }))}
          {panelInput('Most', String(node.max ?? 0), (v) => patch({ max: num(v) }), { hint: '0 = no limit' })}
        </div>
        <div className="fb-field">
          <label>Title each one from</label>
          <FieldPicker
            className="fb-input" value={node.summaryField || ''} placeholder={`Just "${node.itemLabel} 1"`}
            groups={builderPicker((d) => d.scope === scope, scope)}
            onChange={(summaryField) => patch({ summaryField })}
          />
        </div>
        <div className="fb-field">
          <label>{node.itemLabel}s per row</label>
          <select className="fb-input" value={String(perRow)} onChange={(e) => patch({ perRow: Number(e.target.value) })}>
            <option value="1">1 — full width (stacked)</option>
            <option value="2">2 side by side</option>
            <option value="3">3 side by side</option>
            <option value="4">4 side by side</option>
          </select>
          <div className="fb-hint">
            Cards wrap onto a new row when the row is full{rows ? ` (most ${node.max}, so ${rows} row${rows === 1 ? '' : 's'})` : ''}. Narrow screens always stack.
          </div>
        </div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Who sees it</div>
        {CONTAINER_VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${(node.hidden ? 'hidden' : 'shown') === v ? 'on' : ''}`}
            onClick={(e) => { e.stopPropagation(); patch({ hidden: v === 'hidden' || undefined }); }}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          A hidden container takes its instances and its add button with it, and waits for a{' '}
          <Link to={`${base}/logic`}>show rule</Link> naming it.{isRoot ? ' ' : ' Nested, it is answered per ' + (parent?.itemLabel.toLowerCase() || 'parent') + ' — one can ask for it while the next does not.'}
        </div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Styling</div>
        <div className="fb-hint" style={{ marginBottom: 8 }}>
          Paints every {node.itemLabel.toLowerCase()} card, however many the person adds.
          Width comes from "{node.itemLabel}s per row" above.
        </div>
        {renderStyleEditor(nodeStyle, (p) => patch({ style: toStyleBody({ ...nodeStyle, ...p }) }), { width: false })}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Wired up for you</div>
        <div className="fb-fact">{own.length ? `${own.length} field${own.length === 1 ? '' : 's'} repeat here` : 'No fields in it yet — drag some onto it'}</div>
        {kids > 0 && <div className="fb-fact">{kids} container{kids === 1 ? '' : 's'} stacked inside it</div>}
        <div className="fb-fact">Calculations can count them as <b>{node.countKey}</b></div>
        <div className="fb-hint" style={{ marginTop: 8 }}>
          Drag its <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>⠿</span> handle onto another widget to stack it inside,
          {isRoot ? ' or drop a Widget onto this one to put a container in it.' : ' or out onto the page to give it a section of its own.'}
          {' '}Aim near a stacked widget's <b>top or bottom edge</b> to land before or after it instead of inside it.
        </div>
        <div style={{ borderTop: '1px solid #edf2f7', marginTop: 14, paddingTop: 12 }}>
          <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
            {confirm
              ? `Confirm — deletes ${own.length} field${own.length === 1 ? '' : 's'}${kids ? ` and ${kids} stacked container${kids === 1 ? '' : 's'}` : ''}${isRoot ? ' and its section' : ''}`
              : 'Delete widget'}
          </button>
          <div style={{ color: '#718096', fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>
            Existing submissions keep the answers they already captured.
          </div>
        </div>
      </>
    );
  }

  function renderCardPanel(secId: string, cardId: string) {
    const s = sections.find((x) => x.id === secId);
    const c = s?.cards.find((x) => x.id === cardId);
    if (!c) return null;
    // A card inside a widget repeats with it, and its show/hide rules are
    // answered per instance — worth saying, because it changes what a rule on
    // this card does.
    const holder = c.scope && c.scope !== 'order' ? findNode(widgets, c.scope)?.node : undefined;
    const inCard = markerOf(fields, secId, cardId)?.group || '';
    const around = inCard ? s?.cards.find((x) => x.id === inCard) : undefined;
    const room = MAX_CARD_DEPTH - (cardDepth(fields, secId, cardId) + 1);
    const inside = fields.filter((f) => f.section === secId && f.group === cardId);
    const nested = inside.filter((f) => f.cardId).length;
    return (
      <>
        {holder && (
          <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            Inside the <b>{holder.itemLabel}</b> widget — one of these appears in every {holder.itemLabel.toLowerCase()},
            and a rule that hides it hides it on that {holder.itemLabel.toLowerCase()} alone.
          </div>
        )}
        {around && (
          <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 12 }}>
            Grouped inside <b>{around.title || '(untitled card)'}</b> — hiding that card hides this one with it.
          </div>
        )}
        {panelInput('Card title', c.title, (v) => setCard(secId, cardId, { title: v }), { placeholder: '(untitled card)', hint: 'Leave blank for an unlabeled group.' })}
        {panelInput('Id', c.id, () => undefined, { mono: true, ro: true, hint: 'Fields target this as their group.' })}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Who sees it</div>
        {CONTAINER_VIS_OPTS.map(([v, title, desc]) => (
          <div key={v} className={`fb-radio ${(c.hidden ? 'hidden' : 'shown') === v ? 'on' : ''}`}
            onClick={(e) => { e.stopPropagation(); setCard(secId, cardId, { hidden: v === 'hidden' || undefined }); }}>
            <b>{title}</b><small>{desc}</small>
          </div>
        ))}
        <div className="fb-hint" style={{ marginTop: 6 }}>
          A hidden card waits for one <Link to={`${base}/logic`}>show rule</Link> naming it, and takes the
          questions grouped in it{holder ? ` — per ${holder.itemLabel.toLowerCase()}, like every rule on it` : ''}.
        </div>
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Styling</div>
        {renderStyleEditor(c.style, (p) => setCardStyle(secId, cardId, p), { width: false })}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Wired up for you</div>
        <div className="fb-fact">
          {inside.length - nested
            ? `${inside.length - nested} field${inside.length - nested === 1 ? '' : 's'} grouped in it`
            : 'No fields in it yet — drag some onto it'}
        </div>
        {nested > 0 && <div className="fb-fact">{nested} card{nested === 1 ? '' : 's'} nested inside it</div>}
        <div className="fb-hint" style={{ marginTop: 8 }}>
          {room > 0
            ? <>Drop another <b>Card</b> onto this one to group a few of its questions together — {room} more level{room === 1 ? '' : 's'} to go.</>
            : <>This is as deep as cards go, so it takes fields but no further cards.</>}
          {' '}Drag its <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>⠿</span> handle to move it anywhere on the form.
        </div>
        <div style={{ borderTop: '1px solid #edf2f7', marginTop: 14, paddingTop: 12 }}>
          <button className={`fb-danger ${confirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); doDelete(); }}>
            {confirm ? 'Confirm — everything in it stays on the form' : 'Delete card'}
          </button>
          <div style={{ color: '#718096', fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>
            Deleting a card unwraps it: its fields{nested ? ' and the cards nested in it' : ''} move up into{' '}
            {around ? `"${around.title || 'the card around it'}"` : holder ? `the ${holder.itemLabel.toLowerCase()}'s own body` : 'the section body'}.
          </div>
        </div>
      </>
    );
  }

  function renderTestPanel() {
    if (!testEv) return null;
    // A rule's dot goes green when its condition holds in ANY context (the
    // order scope or any widget instance).
    const holds = (r: FormRule): boolean => {
      if (ruleFires(r, testEv.order.values)) return true;
      for (const ctx of testEv.byInstance.values()) if (ruleFires(r, ctx.values)) return true;
      return false;
    };
    /** What a rule's `calc` actions worked out, for the "= 3" line. */
    const calcResults = (r: FormRule): string[] => (r.actions || [])
      .filter((a) => a.type === 'calc' && a.target)
      .map((a) => `${lbl((a as { target: string }).target)} = ${String(testEv.calcs[(a as { target: string }).target] ?? '')}`);
    return (
      <>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
          This is the REAL form — the same renderer respondents get. Fill it in, add widget instances, and watch the rules fire. A green dot means the condition currently holds somewhere on the form.
        </div>
        <div style={{ color: '#718096', fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
          Nothing here is saved. To save a <b>test response</b> — kept under Responses › Test, outside your quota and delivered nowhere — use <b>Fill it in as a respondent ↗</b>{publicUrl ? '' : ' once the form is published'}.
        </div>
        {/* On a phone this sheet is the whole of Test mode (the toolbar's ⋯ sits
            under it), so the live test is offered here as well. */}
        {phone && (
          <button
            type="button" className="fs-btn neutral sm fb-test-live-panel" style={{ marginBottom: 12 }}
            disabled={!publicUrl || testLinkBusy}
            title={publicUrl ? undefined : 'Publish the form first — the live test fills in the form respondents get.'}
            onClick={() => { void openLiveTest(); }}
          >
            Fill it in as a respondent ↗
          </button>
        )}
        {rules.map((r) => {
          const chip = ruleTriggerChip(r);
          const active = holds(r);
          return (
            <div key={r.id} className="fb-test-row">
              <span className="fb-test-dot" style={{ background: active ? '#48bb78' : '#cbd5e0' }} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="fb-kind-chip" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: '#4a5568', lineHeight: 1.45, marginTop: 3 }}>{ruleText(r, lbl, fields, pageLbl)}</span>
                {calcResults(r).map((line) => (
                  <span key={line} style={{ display: 'block', fontSize: 11, color: '#553c9a', fontWeight: 700, marginTop: 2 }}>{line}</span>
                ))}
              </span>
            </div>
          );
        })}
        <Link to={`${base}/logic`} style={{ display: 'inline-block', marginTop: 10, fontSize: 12, fontWeight: 600 }}>Edit rules on the Logic page →</Link>
      </>
    );
  }

  const panelTitle = testMode ? 'Test mode — rules live'
    : sel?.t === 'field' ? (() => {
      const f = fields.find((x) => x.key === sel.key);
      if (f?.type === 'divider') return 'Horizontal line';
      if (f?.type === 'heading') return 'Heading';
      if (f?.type === 'paragraph') return 'Paragraph';
      if (f?.type === 'image') return 'Image';
      const l = f?.label || '';
      return `Field — ${l.length > 24 ? `${l.slice(0, 24)}…` : l}`;
    })()
    : sel?.t === 'sec' ? `Section — ${sections.find((s) => s.id === sel.id)?.title || ''}`
    : sel?.t === 'card' ? `Card — ${sections.find((s) => s.id === sel.sec)?.cards.find((c) => c.id === sel.id)?.title || '(untitled)'}`
    : sel?.t === 'wnode' ? `Widget — ${findNode(widgets, sel.scope)?.node.itemLabel || ''}`
    : sel?.t === 'form' ? 'Form appearance'
    : 'Form Builder';

  const selField = sel?.t === 'field' ? fields.find((f) => f.key === sel.key && !isStructural(f)) : undefined;
  const selSection = sel?.t === 'sec' ? sections.find((s) => s.id === sel.id) : undefined;

  const deselect = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !testMode) {
      setSel(null); setMulti([]); setConfirm(false); setBarConfirm(false); setHlRule(null); setKeyDraft(null);
    }
  };

  // An add control at its plan ceiling goes grey and inert — still visible,
  // so the person can see where the ceiling is.
  const fieldsBlocked = limitHit?.limit === 'fieldsPerForm';

  /** One sidebar tile: dragged onto the page, or clicked to add after the selection. */
  const pal = (e: PaletteEntry, inert = false) => (
    <div
      key={e.label}
      className={`fb-pal-item${inert ? ' lim-pal-inert' : ''}`}
      draggable={!testMode && !inert}
      aria-disabled={inert || undefined}
      title={inert ? undefined : `${e.desc} — drag it onto the page, or click to add it after the selection`}
      tabIndex={inert || testMode ? -1 : 0}
      onDragStart={inert ? undefined : startDrag(e.payload)}
      onDragEnd={endDrag}
      onClick={() => { if (!inert && !testMode) addFromPalette(e.payload); }}
      onKeyDown={(ev) => { if ((ev.key === 'Enter' || ev.key === ' ') && !inert && !testMode) { ev.preventDefault(); addFromPalette(e.payload); } }}
    >
      <span className="fb-pal-glyph">{e.glyph}</span>
      <span className="fb-pal-label">{e.label}</span>
    </div>
  );

  /** The type chip at the head of the Field tab: the tile the field came from. */
  const typeChip = (f: BField) => {
    const e = paletteEntryFor(f.type, f.scale);
    return <span className="fb-type-chip"><span className="fb-pal-glyph">{e?.glyph || '·'}</span>{e?.label || f.type}</span>;
  };

  /** The Field tab's head: the type chip, and Delete (the same two-step confirm the panel always had). */
  const fieldHead = (f: BField) => (
    <div className="fb-insp-type">
      {typeChip(f)}
      <span style={{ flex: 1 }} />
      {!phone && (PRESENTATION.has(f.type) || f.type === 'divider') && (
        <span className="fb-hint" style={{ margin: 0 }}>Delete is at the foot of the panel.</span>
      )}
    </div>
  );

  /** The Page tab (R4): the page's title and sections, or the selected section / card / widget's own settings. */
  function renderPageTab() {
    if (selSection) return <>{<h2 className="fb-insp-title">{panelTitle}</h2>}{renderSectionPanel(selSection)}</>;
    if (sel?.t === 'card') return <><h2 className="fb-insp-title">{panelTitle}</h2>{renderCardPanel(sel.sec, sel.id)}</>;
    if (sel?.t === 'wnode') return <><h2 className="fb-insp-title">{panelTitle}</h2>{renderWidgetNodePanel(sel.sec, sel.scope)}</>;
    if (!curPage) {
      return (
        <div className="fb-empty-panel">
          <div className="fb-empty-title">No pages yet</div>
          <div>A form starts with a section. Add one and it is page 1.</div>
          <button type="button" className="fs-btn primary sm" style={{ marginTop: 10 }} onClick={() => insertSection(0, false)}>+ Add a section</button>
        </div>
      );
    }
    const secs = sections.slice(curPage.start, curPage.end);
    const nFields = fields.filter((f) => !isStructural(f) && secs.some((s) => s.id === f.section)).length;
    return (
      <>
        <h2 className="fb-insp-title">Page {curPage.n} of {pages.length}</h2>
        {panelInput('Page title', curPage.title, (v) => setSection(curPage.id, { pageTitle: v || undefined }), {
          placeholder: secs[0]?.title || 'e.g. Your details',
          hint: pages.length > 1 ? 'Shown in the progress bar and on the page tabs.' : 'Shown in the progress bar once the form has more than one page.',
        })}
        <div className="fb-overline" style={{ margin: '14px 0 8px' }}>Sections on this page</div>
        <div className="fb-page-secs">
          {secs.map((s, i) => (
            <div key={s.id} className="fb-page-sec">
              <button type="button" className="fb-page-sec-name" onClick={() => select({ t: 'sec', id: s.id }, { scroll: true })}>
                <span className="fb-ol-glyph" aria-hidden="true">{widgetSecIds.has(s.id) ? '⟳' : '▭'}</span>
                {s.title || s.id}
                {s.hidden && <span className="fb-sec-chip" style={{ marginLeft: 6, background: 'var(--neutral-bg)', color: 'var(--neutral-fg)' }}>hidden</span>}
              </button>
              {rowMenu(`pagesec:${s.id}`, (
                <>
                  <button onClick={() => select({ t: 'sec', id: s.id }, { scroll: true })}>Settings…</button>
                  <button disabled={!!s.widgetId} onClick={() => dupSection(s.id)}>Duplicate section</button>
                  <button disabled={i === 0 && curPage.start === 0 ? !canMoveSection(s.id, 'up') : !canMoveSection(s.id, 'up')} onClick={() => moveSectionBy(s.id, 'up')}>Move up</button>
                  <button disabled={!canMoveSection(s.id, 'down')} onClick={() => moveSectionBy(s.id, 'down')}>Move down</button>
                  <div className="rule" />
                  <button className="danger" onClick={() => deleteFromMenu({ t: 'sec', id: s.id }, `the section ${s.title || ''}`.trim())}>Delete section</button>
                </>
              ), true)}
            </div>
          ))}
        </div>
        <button type="button" className="fs-btn sm" style={{ marginTop: 6 }} onClick={() => insertSection(curPage.end, false)}>+ Section</button>
        <div className="fb-hint" style={{ marginTop: 6 }}>
          A section groups fields under a title. Drag its ⠿ grip on the page to reorder sections, or drop a Page break tile between two to split them.
        </div>
        <div className="fb-overline" style={{ margin: '18px 0 8px' }}>This page</div>
        <div className="fb-fact">{plural(secs.length, 'section')} · {plural(nFields, 'field')}</div>
        {curPage.n > 1 && (
          <div className="fb-hint" style={{ margin: '4px 0 8px' }}>
            It starts a new page at “{secs[0]?.title || secs[0]?.id}”. Untick “Start a new page” on that section to join it to the page before.
          </div>
        )}
        <div className="fb-del-row">
          <button className={`fb-danger ${pageDelConfirm ? 'confirm' : ''}`} onClick={(e) => { e.stopPropagation(); deletePage(); }}>
            {pageDelConfirm ? `Confirm — deletes ${plural(secs.length, 'section')} and ${plural(nFields, 'field')}` : 'Delete this page'}
          </button>
          <div style={{ color: 'var(--ink-500)', fontSize: 11.5, marginTop: 5, lineHeight: 1.45 }}>
            Takes every section on it. Undo (Ctrl+Z) puts it back.
          </div>
        </div>
      </>
    );
  }

  /** The Form tab (R4): name and description, then all of Form appearance. */
  function renderFormTab() {
    return (
      <>
        <h2 className="fb-insp-title">Form appearance</h2>
        {panelInput('Name', nameDraft ?? form.name, (v) => setNameDraft(v), {
          commit: () => { void commitName(); },
          hint: 'The form’s name everywhere in Form-Magic. Saved as you leave the box.',
        })}
        {renderFormPanel()}
      </>
    );
  }

  /** The Field tab (R4): the selected field, or the invitation to pick one. */
  function renderFieldTab() {
    if (selField) {
      return (
        <>
          <h2 className="fb-insp-title">{panelTitle}</h2>
          {fieldHead(selField)}
          {renderFieldPanel(selField)}
        </>
      );
    }
    return (
      <div className="fb-empty-panel">
        <div className="fb-empty-title">Nothing selected</div>
        <div style={{ marginBottom: 10 }}>Click a field on the page to edit it here. Shift-click to select several.</div>
        <button type="button" className="fs-btn primary sm" onClick={() => openPicker(null)}>+ Add field</button>
        <ul style={{ margin: '14px 0 0', paddingLeft: 18 }}>
          <li style={{ marginBottom: 6 }}>Drag tiles from the sidebar onto the page, or press <span className="fb-kbd">/</span>.</li>
          <li style={{ marginBottom: 6 }}>Drag a field onto another field's <b>left or right edge</b> to share a row, or its <b>top or bottom edge</b> to land there.</li>
          <li style={{ marginBottom: 6 }}>Drop a <b>Card</b> anywhere a field can go — including onto another card, to group part of it.</li>
          <li style={{ marginBottom: 6 }}>Drag the <span style={{ fontFamily: 'var(--font-mono)' }}>⠿</span> handle to reorder sections, or to move a widget into another one.</li>
          <li style={{ marginBottom: 6 }}>Fields with a <span className="fb-rule-badge" style={{ marginLeft: 0 }}>⚡</span> badge are touched by logic rules.</li>
          <li>Use <b>Test</b> to fill in the form and watch the rules fire.</li>
        </ul>
      </div>
    );
  }

  /** What the inspector shows on a phone: the selected thing, as the M5 sheet always did. */
  function renderPhoneBody() {
    if (testMode) return renderTestPanel();
    if (selField) return <>{fieldHead(selField)}{renderFieldPanel(selField)}</>;
    if (selSection) return renderSectionPanel(selSection);
    if (sel?.t === 'card') return renderCardPanel(sel.sec, sel.id);
    if (sel?.t === 'wnode') return renderWidgetNodePanel(sel.sec, sel.scope);
    if (sel?.t === 'form') return renderFormTab();
    return renderFieldTab();
  }

  if (!draft) return <div className="fb-page"><div style={{ padding: 24, color: 'var(--ink-500)' }}>Loading the builder…</div></div>;

  const pageTabs = pages.map((p, i) => ({
    ix: i, n: p.n,
    label: p.title || sections[p.start]?.title || `Page ${p.n}`,
  }));
  const lastSec = curPage ? sections[curPage.end - 1] : undefined;
  const pasteCount = CLIP?.fields.length || 0;
  const showInspector = (!mid || !!sel || testMode) && !(mid && !narrow && inspHidden);
  void clipStamp;

  return (
    <div className="fb-page" ref={pageRef}>
      {/* ---- the canvas toolbar (R4): page tabs · selection bar · paste · widths · Test · undo/redo ---- */}
      <div className="fb-toolbar">
        <h1 className="fb-vh">Form Builder</h1>
        {!testMode && (
          <div className="fb-pagetabs" role="tablist" aria-label="Pages">
            {pageTabs.map((p) => (
              <button
                key={p.ix} type="button" role="tab" aria-selected={p.ix === pageIx}
                className={p.ix === pageIx ? 'on' : ''}
                onClick={() => setPageIx(p.ix)}
                // Dragging something over a tab brings that page up, so a field
                // can be carried from one page to another.
                onDragEnter={() => { if (drag.current) setPageIx(p.ix); }}
                onDragOver={(e) => { if (drag.current) { e.preventDefault(); if (p.ix !== pageIx) setPageIx(p.ix); } }}
              >
                {p.n} · {p.label}
              </button>
            ))}
            <button type="button" className="fb-pagetab-add" aria-label="Add a page" title="Add a page" onClick={addPage}>+</button>
          </div>
        )}
        {!testMode && selKeys.length > 0 && (
          <div className="fb-selbar" role="toolbar" aria-label="Selection">
            <span className="fb-selbar-n">{plural(selKeys.length, 'field')} selected</span>
            <button type="button" onClick={copySel}>Copy <kbd>⌘C</kbd></button>
            <button type="button" onClick={duplicateSel}>Duplicate</button>
            <button type="button" className="danger" onClick={deleteSel}>
              {barConfirm ? `Confirm — delete ${selKeys.length}` : 'Delete'}
            </button>
            {selKeys.length > 1 && (
              <button type="button" className="x" aria-label="Clear the selection" title="Clear the selection (Esc)" onClick={clearMulti}>✕</button>
            )}
          </div>
        )}
        {!testMode && pasteCount > 0 && (
          <button type="button" className="fs-btn primary sm fb-paste-btn" onClick={pasteClip} title="Paste after the selected field (Ctrl+V)">
            Paste {plural(pasteCount, 'field')} <kbd>⌘V</kbd>
          </button>
        )}
        {!testMode && coarse && (
          <button type="button" className={`fs-btn sm fb-select-toggle${selectMode ? ' on' : ''}`} aria-pressed={selectMode}
            onClick={() => { setSelectMode((v) => !v); if (selectMode) clearMulti(); }}>
            {selectMode ? 'Done selecting' : 'Select'}
          </button>
        )}
        {error && <span className="fb-toolbar-err" title={error}>{error}</span>}
        <div className="fb-sr-only" aria-live="polite" role="status">{announce}</div>
        <span style={{ flex: 1 }} />
        <label className="fb-lens-label">Preview as</label>
        <select className="fb-lens fs-input" value={lensId} onChange={(e) => setLensId(e.target.value)} aria-label="Preview as">
          {lenses.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {testMode && (
          <div className="fs-seg fb-device-seg" role="group" aria-label="Preview width">
            {DEVICES.map((d) => (
              <button
                key={d.id} type="button" aria-pressed={device === d.id}
                className={device === d.id ? 'on' : ''} title={`${d.label} · ${d.width}px`}
                onClick={() => setDevice(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        {testMode && (
          <button
            type="button" className="fs-btn neutral sm fb-test-live" disabled={!publicUrl || testLinkBusy}
            title={publicUrl
              ? 'Opens the live form in a new tab. What you submit there is saved as a test: outside your quota, delivered nowhere, under Responses › Test.'
              : 'Publish the form first — the live test fills in the form respondents get.'}
            onClick={() => { void openLiveTest(); }}
          >
            Fill it in as a respondent ↗
          </button>
        )}
        <button className={`fb-test-btn ${testMode ? 'on' : ''}`} onClick={toggleTest}>{testMode ? '✕ Exit test mode' : 'Test mode'}</button>
        <span className="fb-history">
          <button className="fs-btn neutral sm" disabled={testMode || !history.canUndo} title="Undo (Ctrl+Z)" onClick={history.undo}>↶ Undo</button>
          <button className="fs-btn neutral sm" disabled={testMode || !history.canRedo} title="Redo (Ctrl+Shift+Z or Ctrl+Y)" onClick={history.redo}>↷ Redo</button>
        </span>
        {mid && !narrow && (
          <button type="button" className={`fs-btn neutral sm fb-insp-toggle${showInspector ? ' on' : ''}`} aria-pressed={showInspector}
            title="Show or hide the inspector" onClick={() => setInspHidden((v) => !v)}>Inspector</button>
        )}
        {/* Under 900 the label and the select are hidden by CSS and live here
            instead, beside the device widths Test mode offers (§4E E1). */}
        {narrow && (
          <span className="fb-toolbar-more">
            <RowMenu open={menu === 'toolbar'} onToggle={(o) => setMenu(o ? 'toolbar' : null)} label="Builder options">
              {testMode && !phone && DEVICES.map((d) => (
                <button key={d.id} onClick={() => setDevice(d.id)}>
                  {device === d.id ? '✓ ' : ''}{d.label} · {d.width}px
                </button>
              ))}
              {testMode && !phone && <div className="rule" />}
              {testMode && (
                <button disabled={!publicUrl || testLinkBusy} onClick={() => { void openLiveTest(); }}>
                  Fill it in as a respondent ↗
                </button>
              )}
              {testMode && <div className="rule" />}
              {lenses.map((p) => (
                <button key={p.id} onClick={() => setLensId(p.id)}>
                  {lensId === p.id ? '✓ ' : ''}Preview as {p.name}
                </button>
              ))}
            </RowMenu>
          </span>
        )}
      </div>
      <div className="fb-main">
        {/* ---- the sidebar (R4): Fields (the tiles) or the Outline ---- */}
        <div className={`fb-palette ${testMode ? 'testing' : ''}`}>
          <div className="fs-seg fb-side-seg" role="tablist" aria-label="Sidebar">
            <button type="button" role="tab" aria-selected={sideTab === 'fields'} className={sideTab === 'fields' ? 'on' : ''} onClick={() => setSideTab('fields')}>Fields</button>
            <button type="button" role="tab" aria-selected={sideTab === 'outline'} className={sideTab === 'outline' ? 'on' : ''} onClick={() => setSideTab('outline')}>Outline</button>
          </div>
          {sideTab === 'fields' ? (
            <>
              <input
                className="fs-input fb-pal-filter" placeholder="Search fields" aria-label="Search fields"
                value={palSearch} onChange={(e) => setPalSearch(e.target.value)}
              />
              <div className="fb-pal-help">
                Drag onto the page. Drop on the left or right edge of a field to put them side by side.
                Or click to add after the selected field.
              </div>
              {limitHit && (
                <div style={{ margin: '10px 0 12px' }}>
                  <PlanLimitBlock body={limitHit} />
                </div>
              )}
              {PALETTE_GROUPS.map((g) => {
                const items = searchPalette(palSearch, PALETTE.filter((e) => e.group === g));
                if (!items.length) return null;
                return (
                  <React.Fragment key={g}>
                    <div className="fb-overline">
                      {g}
                      {g === 'Layout' && fieldsBlocked && limitHit && (
                        <>{' '}<span className="lim-counter">{limitHit.used} / {limitHit.allowed} element{limitHit.allowed === 1 ? '' : 's'}</span></>
                      )}
                    </div>
                    <div className="fb-pal-grid">
                      {items.map((e) => pal(e, !!e.gated && fieldsBlocked))}
                    </div>
                  </React.Fragment>
                );
              })}
              {palSearch.trim() && !searchPalette(palSearch).length && (
                <div className="fb-pal-none">Nothing called “{palSearch}”.</div>
              )}
            </>
          ) : (
            <BuilderOutline
              sections={sections} fields={fields} widgets={widgets} rules={rules}
              // The stored document names the LIVE keys; the Outline shows the
              // draft's, so a placement follows the renames recorded here (R5).
              placements={templatesOf(workingDocumentOf(form))
                .flatMap((t) => (t.placements as { fieldKey: string; page: number }[] | undefined) || [])
                .map((p) => ({ ...p, fieldKey: draftKeyOf(p.fieldKey, renames) }))}
              sel={sel}
              onSelect={(s) => select(s, { scroll: true })}
              onAddField={() => openPicker(null)}
              onAddPage={addPage}
            />
          )}
        </div>
        <div
          className="fb-canvas"
          onClick={deselect}
          // While editing the paper sits on the app's canvas (R4). Test mode
          // paints the respondent's page around it: the same two stops
          // `.of-page` draws (form.css), accent2 falling back to the accent.
          style={testMode ? { background: shown.bg || accentWash(shown) } : undefined}
        >
          <div
            className={`fb-doc${shown.mode === 'dark' ? ' of-dark' : shown.mode === 'auto' ? ' of-auto' : ''}${testMode && device !== 'desktop' ? ` fb-device fb-device-${device}` : ''}`}
            onClick={deselect}
            data-device={testMode ? device : undefined}
            style={{
              // Test mode's device width wins over the theme's own (P6); the
              // frame's border is inside it, so the paper measures the device.
              maxWidth: testMode && device !== 'desktop' ? DEVICE_WIDTH[device] : shown.maxWidth || 900,
              ['--of-accent' as string]: shown.accent || '#667eea',
              ['--of-accent2' as string]: shown.accent2 || shown.accent || '#764ba2',
              // The whole band, so a direction reaches the submit pill the
              // same way it reaches a respondent's (`--of-accent-wash`).
              ['--of-accent-wash' as string]: accentWash(shown),
              ...(fontStack(shown.font?.body) ? { ['--of-font-body' as string]: fontStack(shown.font?.body) } : {}),
              ...(fontStack(shown.font?.heading || shown.font?.body)
                ? { ['--of-font-heading' as string]: fontStack(shown.font?.heading || shown.font?.body) }
                : {}),
            }}
          >
            <div
              onClick={testMode ? undefined : (e) => { e.stopPropagation(); select({ t: 'form' }); }}
              title={testMode ? undefined : 'Click to edit the form title, colors and width'}
              style={{
                cursor: testMode ? 'default' : 'pointer', borderRadius: 6, padding: 4, margin: '-4px -4px 0',
                boxShadow: sel?.t === 'form' ? SEL_RING : 'none',
                background: sel?.t === 'form' ? 'var(--accent-wash)' : 'transparent',
              }}
            >
              {/* The logo row the respondent gets, in the same markup and the
                  same grid the public form uses (`.of-logo`, form.css) — so a
                  house logo the form inherits is on the canvas and in test
                  mode, not only in the panel. `shown`, not `theme`: an
                  inheriting form draws the account's row. */}
              {canvasLogos.length > 0 && (
                <div className={`of-logo${canvasLogos.length > 1 ? ' of-logo-multi' : ''}`}>
                  {canvasLogos.map((l) => (
                    <div key={l.url} className={`of-logo-${l.align}`}
                      style={{ ['--of-logo-h' as string]: `${Math.min(400, l.height || 60)}px` }}>
                      <img src={l.url} alt="" />
                    </div>
                  ))}
                </div>
              )}
              <h1 style={{ margin: 0 }}>{shown.headerTitle || form.name}</h1>
              {shown.headerSubtitle
                ? <p className="fb-doc-sub" style={{ marginBottom: 0 }}>{shown.headerSubtitle}</p>
                : !testMode && <p className="fb-doc-sub" style={{ marginBottom: 0, fontStyle: 'italic', color: 'var(--of-ink-soft, #a0aec0)' }}>Click here to set a description, colors and width</p>}
            </div>
            <div style={{ height: 26 }} />
            {testMode && testState ? (
              <>
                <PagerProgress pager={testPager} />
                <ErrorSummaryTop pager={testPager} />
                <FormRenderer
                  def={testDef}
                  state={testState}
                  onState={(update) => setTestState((s) => (s ? update(s) : s))}
                  cfg={lensCfg}
                  formName={form.name}
                  idPrefix="fbtest"
                  sections={testPager.paged ? testPager.sections : undefined}
                  errors={testPager.errors}
                  onClearError={testPager.clearError}
                  onFieldError={testPager.setError}
                  uploads={form.settings?.uploads}
                  requiredStyle={form.settings?.requiredStyle}
                />
                <PagerControls pager={testPager} />
              </>
            ) : (
              <>
                {/* The page card's head (R4): "PAGE n OF m" and the page's
                    title, typed in place. The title is the first section's
                    `pageTitle` — what the progress bar shows respondents. */}
                {curPage && (
                  <div className="fb-pagehead" onClick={(e) => e.stopPropagation()}>
                    <div className="fb-pagehead-n">Page {curPage.n} of {pages.length}</div>
                    <input
                      className="fb-pagetitle" value={curPage.title} aria-label="Page title"
                      placeholder="Page title (optional)"
                      onChange={(e) => setSection(curPage.id, { pageTitle: e.target.value || undefined })}
                    />
                  </div>
                )}
                {renderZone(curPage ? curPage.start : 0)}
                {curPage && sections.slice(curPage.start, curPage.end).map((sec) => renderSection(sec, sections.indexOf(sec)))}
                {!sections.length && (
                  <div className="fb-empty-drop" style={{ padding: 24 }}>
                    Drop a <b>Section</b> here to start the form, or{' '}
                    <button type="button" className="fb-linkbtn" onClick={(e) => { e.stopPropagation(); insertSection(0, false); }}>add one</button>.
                  </div>
                )}
                {sections.length > 0 && (
                  <button
                    type="button" className="fb-add-dashed"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPicker(lastSec ? { cont: sectionBody(lastSec), afterKey: null, label: `the section ${lastSec.title || lastSec.id}` } : null);
                    }}
                  >
                    + Add a field <span className="fb-kbd">/</span>
                  </button>
                )}
              </>
            )}
            {(testMode ? (!testPager.paged || testPager.atLast) : (!curPage || pageIx === pages.length - 1)) && (
              // The foot of the form is form-level too — its label, its colour
              // and its gradient all live on the appearance panel — so it
              // opens the same panel the header does rather than being the one
              // thing on the canvas a click does nothing to.
              <div
                className="fb-submit"
                onClick={testMode ? undefined : (e) => { e.stopPropagation(); select({ t: 'form' }); }}
                title={testMode ? undefined : 'Click to edit the button label, colors and width'}
                style={{
                  cursor: testMode ? 'default' : 'pointer',
                  boxShadow: sel?.t === 'form' ? SEL_RING : undefined,
                }}
              >{submitLabel || 'Submit'}</div>
            )}
          </div>
          {/* What a respondent sees under the card, drawn where they see it.
              Not a link here: clicking it mid-edit should not take the owner
              off to a marketing site. The switch lives on Settings. */}
          {showsBadge(form.settings.hideAdvertisement, features.hideAdvertisement) && (
            <FormMagicBadge preview />
          )}
        </div>
        {/* ---- the inspector (R4): Field · Page · Form on a desktop; under 1100
            an overlay while something is selected (the toolbar's Inspector
            button puts it away between 900 and 1100); a full-screen sheet in
            two tabs under 600, as M5 built it. ---- */}
        {showInspector && (
        <div className="fb-panel">
          <div className="fb-panel-head">
            {phone ? (
              <>
                <span className="title">{panelTitle}</span>
                <button className="fb-panel-done" onClick={() => { if (testMode) toggleTest(); else { setSel(null); setMulti([]); setConfirm(false); setHlRule(null); setKeyDraft(null); } }}>
                  Done
                </button>
              </>
            ) : testMode ? (
              <span className="title">{panelTitle}</span>
            ) : (
              <div className="fb-insp-tabs" role="tablist" aria-label="Inspector">
                {(['field', 'page', 'form'] as const).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={inspTab === t} className={inspTab === t ? 'on' : ''}
                    onClick={() => setInspTab(t)}>
                    {t === 'field' ? 'Field' : t === 'page' ? 'Page' : 'Form'}
                  </button>
                ))}
                {mid && (
                  <button type="button" className="fs-btn icon fb-insp-close" aria-label="Hide the inspector" title="Hide the inspector"
                    onClick={() => { if (narrow) { setSel(null); setMulti([]); } else setInspHidden(true); }}>✕</button>
                )}
              </div>
            )}
          </div>
          {phone && !testMode && selField && !PRESENTATION.has(selField.type) && selField.type !== 'divider' && (
            <div className="fb-panel-tabs" role="tablist">
              <button role="tab" aria-selected={panelTab === 'question'} className={panelTab === 'question' ? 'on' : ''}
                onClick={() => setPanelTab('question')}>Question</button>
              <button role="tab" aria-selected={panelTab === 'rules'} className={panelTab === 'rules' ? 'on' : ''}
                onClick={() => setPanelTab('rules')}>Rules</button>
            </div>
          )}
          <div className="fb-panel-body">
            {phone ? renderPhoneBody()
              : testMode ? renderTestPanel()
                : inspTab === 'field' ? renderFieldTab()
                  : inspTab === 'page' ? renderPageTab()
                    : renderFormTab()}
          </div>
        </div>
        )}
      </div>
      {/* ---- E1: the "+" under 900. It opens the same picker "/" opens. ---- */}
      {narrow && !testMode && (
        <button
          className="fb-add-fab" title="Add a field, card or section"
          data-drawer={!phone && (!!sel || testMode) ? 'open' : 'shut'}
          aria-label="Add a field, card or section"
          disabled={pickerOpen}
          onClick={() => openPicker(null)}
        >+</button>
      )}
      {pickerOpen && (
        <AddFieldPicker
          where={pickerWhere()}
          limitHit={limitHit} fieldsBlocked={fieldsBlocked}
          onPick={(e) => addFromPalette(e.payload)}
          onClose={() => { setPickerOpen(false); setPickerAt(null); }}
        />
      )}
      {/* "Put beside…" (R4): join a row without dragging. */}
      {besideFor && (
        <Modal title="Put beside…" narrow onClose={() => setBesideFor(null)}>
          <div className="fb-pal-sheet">
            <div className="fb-pal-where">
              Pick the field <b>{fields.find((f) => f.key === besideFor)?.label || besideFor}</b> should share a row with.
              A row holds up to four.
            </div>
            {besideCandidates(besideFor).map((x) => (
              <button key={x.key} type="button" className="fb-pal-item" onClick={() => putBeside(besideFor, x.key)}>
                <span className="fb-pal-glyph">{paletteEntryFor(x.type, x.scale)?.glyph || '·'}</span>
                <span className="fb-pal-label">{x.label || x.key}</span>
              </button>
            ))}
            {!besideCandidates(besideFor).length && <div className="fb-pal-none">Nothing in this container has room beside it.</div>}
          </div>
        </Modal>
      )}
      {/* "Move to…": every container a field could be moved into, listed. */}
      {moveTo && (
        <Modal title="Move to…" narrow onClose={() => setMoveTo(null)}>
          <div className="fb-pal-sheet">
            {moveTargets().map((t) => (
              <button
                key={`${t.cont.sec}:${t.cont.scope}:${t.cont.grp}`}
                type="button" className="fb-pal-item"
                onClick={() => moveFieldTo(moveTo, t.cont)}
              >
                <span className="fb-pal-label">{t.label}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* ---- adoption (B5) ---- */}
      {promoting && (
        <Modal
          narrow
          title="Use this form&rsquo;s branding for the account?"
          onClose={() => setPromoting(false)}
          footer={
            <>
              <button className="fs-btn primary" type="button" disabled={adopting} onClick={() => void doPromote()}>
                {adopting ? 'Applying…' : 'Make it the account branding'}
              </button>
              <span style={{ flex: 1 }} />
              <button className="fs-btn neutral" type="button" disabled={adopting}
                onClick={() => setPromoting(false)}>Cancel</button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            This form&rsquo;s {ownBranded.map(brandKeyLabel).join(', ')} {ownBranded.length === 1 ? 'becomes' : 'become'} your
            account&rsquo;s house style. Every other form that does not set its own
            {ownBranded.length === 1 ? ' one' : ' ones'} will start using {ownBranded.length === 1 ? 'it' : 'them'} —
            there is no publish step for branding.
          </p>
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14, fontSize: 13, lineHeight: 1.5 }}>
            <input type="checkbox" checked={clearOnPromote} onChange={(e) => setClearOnPromote(e.target.checked)}
              style={{ marginTop: 3 }} />
            <span>
              Let this form inherit it too.
              <span style={{ color: 'var(--ink-500)', display: 'block' }}>
                Leave this on unless you want the form to keep its own copy — a copy will not follow the
                brand when you change it.
              </span>
            </span>
          </label>
        </Modal>
      )}

      {resetting && (
        <Modal
          narrow
          title="Reset to account branding?"
          onClose={() => setResetting(false)}
          footer={
            <>
              <button className="fs-btn primary" type="button" onClick={() => { doReset(); }}>
                Reset this form
              </button>
              <span style={{ flex: 1 }} />
              <button className="fs-btn neutral" type="button" onClick={() => setResetting(false)}>Cancel</button>
            </>
          }
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            This form stops setting its own {ownBranded.map(brandKeyLabel).join(', ')} and uses your account
            branding instead. Its header text and width are its own and stay.
          </p>
          <p style={{ margin: '10px 0 0', lineHeight: 1.6, color: 'var(--ink-500)' }}>
            It is a change on the canvas like any other — respondents see it when you publish.
          </p>
        </Modal>
      )}
    </div>
  );
}
